import { useState, useEffect, useRef, useMemo, lazy, Suspense } from 'react';
import { faltasDoPedido } from '../DetalhesCartao/PedidoGix';
import { createPortal } from 'react-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import type { Etapa, Card, Flow, Departamento, Colaborador, ConfiguracaoHierarquiaEtapa, ConfiguracaoCardConectado } from '../../types';
import { PlusIcon, Cog6ToothIcon, ChevronLeftIcon, ViewColumnsIcon, ListBulletIcon, ArrowPathIcon, ChartBarIcon, DocumentTextIcon, GlobeAltIcon, RectangleGroupIcon, HomeModernIcon, XMarkIcon, CalendarDaysIcon, CheckBadgeIcon, BanknotesIcon, ClipboardDocumentListIcon, PresentationChartBarIcon } from '@heroicons/react/24/outline';
import { apiUrl } from '../../utils/apiBase';
import { useSharedPoll } from '../../hooks/useSharedPoll';
import ConfiguracaoEtapa from '../ConfiguracaoEtapa';
import ConfiguracaoHierarquia from '../ConfiguracaoHierarquia';
import NovoCartao from '../NovoCartao';
import DetalhesCartao from '../DetalhesCartao';
import ColaboradorPopover from '../Organograma/ColaboradorPopover';
import CardPai from '../CardPai';
import ModalAdicionarFilho from '../ModalAdicionarFilho';
import ModalCriarCardConectado from '../CardsConectados/ModalCriarCardConectado';
import ModalConfigPermissoes from './ModalConfigPermissoes';
import ModalSolicitarReenvio, { reprovacaoSoPorMotivoInterno } from './ModalSolicitarReenvio';
import type { SolicitacaoReenvioPayload } from './ModalSolicitarReenvio';
import Documentos from '../Documentos';
import Portais from '../Portais';
import { ehAdminMaster } from '../../utils/adminMaster';
import { textoParcelas } from '../CampoParcelas';
import { textoItens } from '../CampoItens';
// A Vila é experimental e pesada (canvas hoje, 3D depois). Carregando sob demanda,
// o pedaço dela só é baixado por quem realmente abre a aba.
const VilaFluxo = lazy(() => import('./VilaFluxo'));
const AgendaFluxo = lazy(() => import('./AgendaFluxo'));
import { fluxoTemAgenda } from './AgendaFluxo';
const QualidadeFluxo = lazy(() => import('./QualidadeFluxo'));
import { fluxoTemQualidade } from './QualidadeFluxo';
const VerbasFluxo = lazy(() => import('./VerbasFluxo'));
import { fluxoTemVerbas } from './VerbasFluxo';
const AtasFluxo = lazy(() => import('./AtasFluxo'));
import { fluxoTemAtas } from './AtasFluxo';
const ChamadosTIFluxo = lazy(() => import('./ChamadosTIFluxo'));
import { fluxoTemChamadosTI } from './ChamadosTIFluxo';
import { obterCardsRaiz, popularCardsFilhos, podeVirarCardPai, transformarEmCardPai, podeVirarCardFilho, adicionarCardFilho, removerCardFilho, podeDestravarCardPai, destravarCardPai } from '../../utils/cardHierarchyHelpers';
import { notifyCardCreated, notifyCardMoved, notifyCardUpdated, notifyCardDeleted } from '../../utils/chatNotifications';
import { useSocket } from '../../contexts/SocketContext';
import { automationEngine } from '../../utils/automationEngine';
import { generateShortCode, generateShortCodeLegacy, isValidShortCode } from '../../utils/shortCodeUtils';
import { calcularHorasSLA, entradaNaEtapa, nivelEstouroSLA, NIVEL_ESCALONAMENTO_SLA, formatarHorasSLA } from '../../utils/slaUtils';
import { calcularPrazoProcesso, formatarDuracao } from '../../utils/prazoProcesso';
import AIAssistant from '../AIAssistant';
import { EtiquetaBadges, GerenciarEtiquetasModal, corTextoParaFundo } from '../Etiquetas';


interface KanbanBoardProps {
  flow: Flow;
  departamentos: Departamento[];
  colaboradores: Colaborador[];
  onUpdateFlow: (flow: Flow) => Promise<Flow>;
  onBack?: () => void;
  onNavigateToFlow?: (flow: Flow, cardIdToOpen?: string) => void;
  // Sinalizações para UI do app (ex.: recolher menu ao abrir config de etapa)
  onOpenEtapaConfig?: () => void;
  onCloseEtapaConfig?: () => void;
  userPermissions?: {
    canEdit: boolean;
    canAdmin: boolean;
    currentUser?: any;
    configurarEtapas?: any; // granular section-level permission from permissoes_flow
  };
  checkCardEditPermission?: (etapa: any, card?: any) => boolean;
  checkEtapaEditPermission?: () => boolean;
  automacoes?: any[]; // Lista de automações
  cardToOpen?: string | null;
  onCardOpened?: () => void;
  onUpdateCache?: (cards: Card[]) => void; // Atualiza cache IndexedDB com cards atuais
}

// Função para obter iniciais do nome
const getInitials = (name: string): string => {
  if (!name) return '??';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Cores para os avatares
const avatarColors = [
  'bg-blue-500',
  'bg-purple-500',
  'bg-pink-500',
  'bg-green-500',
  'bg-yellow-500',
  'bg-red-500',
  'bg-indigo-500',
  'bg-teal-500',
  'bg-cyan-500',
  'bg-orange-500',
  'bg-lime-500',
  'bg-amber-500',
];

const getAvatarColor = (index: number): string => {
  return avatarColors[index % avatarColors.length];
};

// Função para gerar cor baseada no ID (hash simples)
const getAvatarColorFromId = (id: string): string => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

const KanbanBoard = ({ 
  flow, 
  departamentos,
  colaboradores,
  onUpdateFlow, 
  onBack,
  onNavigateToFlow,
  onOpenEtapaConfig,
  onCloseEtapaConfig,
  userPermissions,
  checkCardEditPermission,
  checkEtapaEditPermission,
  automacoes = [],
  cardToOpen,
  onCardOpened,
  onUpdateCache
}: KanbanBoardProps) => {
  const [etapas, setEtapas] = useState<Etapa[]>([]);
  const [todasEtapas, setTodasEtapas] = useState<Etapa[]>([]); // Todas as etapas sem filtro de permissões
  const [cards, setCards] = useState<Card[]>([]);
  const [configuring, setConfiguring] = useState<Etapa | null>(null);
  const [configuringHierarquia, setConfiguringHierarquia] = useState<Etapa | null>(null);
  const [novoCartao, setNovoCartao] = useState<Etapa | null>(null);
  const [cardSelecionado, setCardSelecionado] = useState<Card | null>(null);
  const [adicionandoFilhoAoPai, setAdicionandoFilhoAoPai] = useState<Card | null>(null); // Card pai que vai receber filho
  const [colaboradorSelecionado, setColaboradorSelecionado] = useState<{ 
    colaborador: Colaborador, 
    position: { x: number, y: number } 
  } | null>(null);
  const [configurandoOrdenacao, setConfigurandoOrdenacao] = useState<Etapa | null>(null); // Estado para modal de ordenação
  // 03/11/2026 André: filtrar os cards da etapa por período (de/até), pela data em que o card
  // entrou na etapa (sem histórico, a data de criação). Fica só no navegador de quem filtrou.
  const chaveFiltroData = `escalone_filtro_data_etapa:${flow?.id || ''}`;
  const [filtroDataEtapa, setFiltroDataEtapa] = useState<Record<string, { de?: string; ate?: string }>>(() => {
    try { return JSON.parse(localStorage.getItem(`escalone_filtro_data_etapa:${flow?.id || ''}`) || '{}') || {}; } catch { return {}; }
  });
  const salvarFiltroData = (etapaId: string, valor: { de?: string; ate?: string } | null) => {
    setFiltroDataEtapa(prev => {
      const novo = { ...prev };
      if (!valor || (!valor.de && !valor.ate)) delete novo[String(etapaId)]; else novo[String(etapaId)] = valor;
      try { localStorage.setItem(chaveFiltroData, JSON.stringify(novo)); } catch { /* sem localStorage */ }
      return novo;
    });
  };
  const aplicarFiltroData = (lista: Card[], etapaId: string): Card[] => {
    const f = filtroDataEtapa[String(etapaId)];
    if (!f || (!f.de && !f.ate)) return lista;
    const ini = f.de ? new Date(`${f.de}T00:00:00`).getTime() : -Infinity;
    const fim = f.ate ? new Date(`${f.ate}T23:59:59`).getTime() : Infinity;
    return lista.filter(card => {
      const ent = entradaNaEtapa(card.etapaHistory as any, String(etapaId));
      let bruto: any = ent || (card as any).createdAt || (card as any).dataCriacao;
      if (typeof bruto === 'string' && /T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(bruto)) bruto += 'Z';
      const t = new Date(bruto).getTime();
      return !isNaN(t) && t >= ini && t <= fim;
    });
  };
  const [cardMovendo, setCardMovendo] = useState<string | null>(null); // ID do card sendo movido (loading)
  const [salvandoGlobal, setSalvandoGlobal] = useState<string | null>(null); // Mensagem de loading global (bloqueia tela)
  const [cardConectadoModal, setCardConectadoModal] = useState<Card | null>(null); // Card para criar card conectado
  const [cardsConectadosConfigs, setCardsConectadosConfigs] = useState<ConfiguracaoCardConectado[]>([]); // Configs de cards conectados para este fluxo
  const etapasComCardConectado = new Set(cardsConectadosConfigs.filter(c => c.ativo).map(c => String(c.etapaOrigemId)));

  // 📎 Movimentação segurada esperando o modal "qual anexo reenviar".
  // O card só cai na etapa de reprovação depois que a equipe diz o que devolver.
  const [reenvioPendente, setReenvioPendente] = useState<{
    card: Card;
    draggableId: string;
    sourceId: string;
    destinationId: string;
    destIndexRelativo: number;
    // Veio de uma automação (campo preenchido no modal), não do arraste: a
    // regra de permiteMoverPara/permissão não se aplica, a automação já decidiu.
    viaAutomacao?: boolean;
  } | null>(null);

  // 🔔 Estado para toast de notificação
  const [toastInfo, setToastInfo] = useState<{
    visible: boolean;
    titulo: string;
    mensagem: string;
    camposLimpos: { nomeCampo: string; valorAnterior: string }[];
  } | null>(null);

  // 💬 Mensagens não lidas do portal por card + toast de nova mensagem
  const [unreadByCard, setUnreadByCard] = useState<Record<string, number>>({});
  // 💬 Cards que possuem chat com o solicitante (qualquer mensagem) -> total de mensagens por card
  const [chatByCard, setChatByCard] = useState<Record<string, number>>({});
  // 🔎 Protocolo do portal por card -> permite pesquisar o card pelo código que o solicitante recebeu
  const [protocoloByCard, setProtocoloByCard] = useState<Record<string, string>>({});
  // 🧾 GIX: status de aprovação de OC (movimento 96) por card -> 'aprovada' | 'elegivel'
  const [gixOcByCard, setGixOcByCard] = useState<Record<string, 'aprovada' | 'elegivel'>>({});
  const [portalToast, setPortalToast] = useState<{ nome: string; protocolo?: string } | null>(null);
  const prevPortalTotalRef = useRef<number | null>(null);
  const prevPortalPorCardRef = useRef<Record<string, number>>({});

  // Polling das mensagens não lidas do portal (badges no card + toast global)
  // Compartilhado entre as abas: quem busca é a aba dona, as outras recebem o
  // mesmo resultado pelo BroadcastChannel (ver utils/sharedPolling).
  useSharedPoll(
    flow?.id ? `portal-nao-lidas:${flow.id}` : null,
    apiUrl(`/portal-submissoes/nao-lidas?flowId=${flow?.id}`),
    15000,
    (data: any) => {
      if (!data?.success) return;

      const mapa: Record<string, number> = {};
      for (const item of (data.itens || [])) {
        if (item.cardCriadoId) mapa[String(item.cardCriadoId)] = item.mensagensNaoLidas || 0;
      }
      setUnreadByCard(mapa);

      // Toast quando o total aumenta entre dois ciclos (mensagem nova chegou).
      // Fica do lado de cá de propósito: cada aba mostra o próprio toast, igual antes.
      const total = data.total || 0;
      const anterior = prevPortalTotalRef.current;
      if (anterior !== null && total > anterior) {
        // A solicitação que ganhou mensagem neste ciclo — não a primeira da lista com
        // não lidas, que podia ser outra, antiga, e sem nome.
        const itens = data.itens || [];
        const prevPorCard = prevPortalPorCardRef.current;
        const nova = itens.find((i: any) => (i.mensagensNaoLidas || 0) > (prevPorCard[String(i.cardCriadoId)] || 0))
          || itens.find((i: any) => (i.mensagensNaoLidas || 0) > 0);
        // Sem nomeExterno (o portal não achou campo "nome"): o título do card, que
        // nesses fluxos já é o nome de quem pediu.
        const cardDaMensagem = nova?.cardCriadoId
          ? cards.find(c => String(c.id) === String(nova.cardCriadoId))
          : undefined;
        const tituloCard = cardDaMensagem?.titulo && !cardDaMensagem.titulo.startsWith('Submissão Portal')
          ? cardDaMensagem.titulo
          : '';
        setPortalToast({
          nome: nova?.nomeExterno || tituloCard || 'Solicitante',
          protocolo: nova?.protocolo
        });
        setTimeout(() => setPortalToast(null), 8000);
      }
      prevPortalTotalRef.current = total;
      prevPortalPorCardRef.current = mapa;
    }
  );

  // 💬 Carrega os cards que possuem chat com o solicitante (qualquer mensagem)
  // -> usado para o selo de conversa no minicard e para o filtro "Com chat"
  useSharedPoll(
    flow?.id ? `portal-com-chat:${flow.id}` : null,
    apiUrl(`/portal-submissoes/com-chat?flowId=${flow?.id}`),
    30000,
    (data: any) => {
      if (!data?.success) return;
      const mapa: Record<string, number> = {};
      for (const item of (data.itens || [])) {
        if (item.cardCriadoId) mapa[String(item.cardCriadoId)] = item.totalMensagens || 0;
      }
      setChatByCard(mapa);
    }
  );

  // 🔎 Carrega o protocolo do portal de cada card do fluxo
  // -> quem atende cola o protocolo que o solicitante recebeu e a busca acha o card
  useSharedPoll(
    flow?.id ? `portal-protocolos:${flow.id}` : null,
    apiUrl(`/portal-submissoes/protocolos?flowId=${flow?.id}`),
    60000,
    (data: any) => {
      if (!data?.success) return;
      const mapa: Record<string, string> = {};
      for (const item of (data.itens || [])) {
        if (item.cardCriadoId && item.protocolo) mapa[String(item.cardCriadoId)] = String(item.protocolo);
      }
      setProtocoloByCard(mapa);
    }
  );

  // ⏳ Relógio do SLA do minicard: sem ele o "faltam Xh" só mudava quando algo
  // mais re-renderizava o quadro. Só liga se alguma etapa tiver prazo.
  const [agoraSla, setAgoraSla] = useState(() => new Date());
  const temEtapaComPrazo = (flow?.etapas || []).some((e: any) => e.tempoMaximoHoras);
  useEffect(() => {
    if (!temEtapaComPrazo) return;
    const interval = setInterval(() => setAgoraSla(new Date()), 60000);
    return () => clearInterval(interval);
  }, [temEtapaComPrazo]);

  // 🧾 GIX (somente ambiente local): varre as etapas com aprovação automática de OC
  // por movimento 96 e popula o badge no minicard. A varredura no backend já aprova
  // (ou simula, em dry-run) e carimba o card; aqui só lemos o resultado p/ exibir.
  useEffect(() => {
    const isLocal = typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    if (!isLocal || !flow?.id) return;
    // Só roda se alguma etapa tiver a auto-aprovação ligada
    const temAutoAprovar = (flow.etapas || []).some((e: any) => e.gixAutoAprovarMov96);
    if (!temAutoAprovar) return;

    let ativo = true;
    const varrer = async () => {
      try {
        const res = await fetch(apiUrl('/gix/aprovacao-oc/varrer'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ flowId: flow.id }),
        });
        const data = await res.json();
        if (!ativo || !data.success) return;
        const mapa: Record<string, 'aprovada' | 'elegivel'> = {};
        for (const item of (data.itens || [])) {
          if (item.cardId && item.badgeStatus) mapa[String(item.cardId)] = item.badgeStatus;
        }
        setGixOcByCard(mapa);
      } catch (err) {
        console.error('Erro ao varrer aprovação de OC (GIX):', err);
      }
    };
    varrer();
    const interval = setInterval(varrer, 60000);
    return () => { ativo = false; clearInterval(interval); };
  }, [flow?.id, flow.etapas]);

  // 🧾 Lançamento automático de NF no GIX (etapas com gixLancarNfMov96).
  // Diferente da aprovação de OC, que varre sozinha a cada 60s: aqui é botão.
  // Lançar nota é escrita em ERP — quem dispara tem que ser uma pessoa, e o
  // resultado card a card precisa ficar na tela para alguém conferir.
  const [varrendoNf, setVarrendoNf] = useState(false);
  // Qual card está sendo lançado sozinho — para o spinner ficar no botão daquele
  // card, e não na etapa inteira.
  const [varrendoNfCardId, setVarrendoNfCardId] = useState<string | null>(null);
  const [resultadoNf, setResultadoNf] = useState<any | null>(null);

  // Etapas onde cabe o botão "lançar só este card": as que têm a chave ligada e,
  // junto, as etapas de recusa que elas apontam. A fila de "Notas não lançadas" é
  // justamente onde alguém quer resolver um card isolado — e o backend já varre
  // as duas, então o botão funciona nas duas.
  const etapasComLancamentoNf = useMemo(() => {
    const ids = new Set<string>();
    for (const e of (flow?.etapas || []) as any[]) {
      if (!e?.gixLancarNfMov96) continue;
      ids.add(String(e.id));
      if (e.gixLancarNfEtapaFalha) ids.add(String(e.gixLancarNfEtapaFalha));
    }
    return ids;
  }, [flow?.etapas]);

  const varrerLancamentoNf = async (cardId?: string) => {
    if (varrendoNf || varrendoNfCardId || !flow?.id) return;
    if (cardId) setVarrendoNfCardId(String(cardId));
    else setVarrendoNf(true);
    try {
      const res = await fetch(apiUrl('/gix/lancamento-nf/varrer'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flowId: flow.id, ...(cardId ? { cardId: String(cardId) } : {}) }),
      });
      const data = await res.json();
      setResultadoNf(data);
      // Os cards que se moverem chegam pelo socket 'card_moved' que o board já escuta.
    } catch (err) {
      setResultadoNf({ success: false, error: String(err) });
    } finally {
      setVarrendoNf(false);
      setVarrendoNfCardId(null);
    }
  };

  // Estado para controlar o modo de visualização
  type ViewMode = 'kanban' | 'lista' | 'fluxo' | 'relatorios' | 'hierarquia' | 'documentos' | 'portais' | 'vila' | 'agenda' | 'qualidade' | 'verbas' | 'atas' | 'indicadoresTI';

  // 🏰 Vila é experimental e ainda não tem acabamento: fica fora de produção.
  // import.meta.env.DEV é true só no servidor de desenvolvimento (npm run dev),
  // então o bundle publicado na VPS nunca mostra a aba.
  const podeVerVila =
    (import.meta as any).env?.DEV === true &&
    ehAdminMaster(userPermissions?.currentUser?.email);
  // 📋 Fluxo de atas abre direto no Modo Reunião (aba "Atas")
  const [viewMode, setViewMode] = useState<ViewMode>(() => (fluxoTemAtas(flow) ? 'atas' : 'kanban'));
  const atasCardSalvo = (salvo: Card) => {
    const c = { ...salvo, id: String(salvo.id), etapaId: String(salvo.etapaId) } as Card;
    setCards(prev => {
      const novo = prev.some(x => x.id === c.id) ? prev.map(x => (x.id === c.id ? c : x)) : [...prev, c];
      queueMicrotask(() => onUpdateCache?.(novo));
      return novo;
    });
  };
  
  // Estado para etapas expandidas na visualização Lista
  const [etapasExpandidas, setEtapasExpandidas] = useState<Set<string>>(new Set());

  // 🎯 Estado para filtro por responsável (colaboradorId por etapaId)
  const [filtroResponsavel, setFiltroResponsavel] = useState<Map<string, string>>(new Map());
  
  // Estado para mostrar cards da etapa no Fluxo
  const [etapaFluxoSelecionada, setEtapaFluxoSelecionada] = useState<Etapa | null>(null);
  
  // Estados para zoom e navegação do Fluxo BPMN
  const [fluxoZoom, setFluxoZoom] = useState(1);
  const [fluxoPan, setFluxoPan] = useState({ x: 0, y: 0 });
  const [fluxoIsDragging, setFluxoIsDragging] = useState(false);
  const [fluxoDragStart, setFluxoDragStart] = useState({ x: 0, y: 0 });
  
  // Estado para modal de configuração de permissões
  const [mostrarConfigPermissoes, setMostrarConfigPermissoes] = useState(false);
  const [permissoesGranulares, setPermissoesGranulares] = useState<any[]>([]);
  const [fluxoIsPanMode, setFluxoIsPanMode] = useState(false);
  const [etapaDestacada, setEtapaDestacada] = useState<string | null>(null);
  const [modoFiltroConexao, setModoFiltroConexao] = useState<'todos' | 'origem' | 'destino'>('todos');
  const [conexaoSelecionada, setConexaoSelecionada] = useState<{origem: string; destino: string} | null>(null);
  const fluxoContainerRef = useRef<HTMLDivElement>(null);
  
  // Estado para o construtor de relatórios
  const [dimensoesSelecionadas, setDimensoesSelecionadas] = useState<string[]>([]);
  const [medidasSelecionadas, setMedidasSelecionadas] = useState<string[]>([]);
  const [mostrarCamposRelatorio, setMostrarCamposRelatorio] = useState(true);
  const [nomeRelatorioSalvar, setNomeRelatorioSalvar] = useState('');
  const [relatoriosSalvos, setRelatoriosSalvos] = useState<Array<{
    _id?: string;
    id: string;
    nome: string;
    dimensoes: string[];
    medidas: string[];
    dataCriacao: string;
    empresaId: string;
    usuarioId: string;
    flowId: string;
    filtroGix?: string;
  }>>([]);
  // 🤖 Filtro do relatório por situação do lançamento de NF. A tabela dinâmica
  // agrupa tudo; sem filtro não dá para pedir "só as que o robô lançou".
  const [filtroGix, setFiltroGix] = useState<'todos' | 'lancadas' | 'ja_constava' | 'nao_lancadas'>('todos');
  const [mostrarModalSalvar, setMostrarModalSalvar] = useState(false);
  const [mostrarRelatoriosSalvos, setMostrarRelatoriosSalvos] = useState(false);
  const [carregandoRelatorios, setCarregandoRelatorios] = useState(false);
  const [salvandoRelatorio, setSalvandoRelatorio] = useState(false);
  
  // Estado para pesquisa de cards
  const [termoPesquisa, setTermoPesquisa] = useState('');
  // 🧬 Filtro por tipo de card: 'todos' | 'pais' | 'conectados' | 'filhos' | 'normais'
  const [filtroTipoCard, setFiltroTipoCard] = useState<'todos' | 'pais' | 'conectados' | 'filhos' | 'normais' | 'comChat'>('todos');
  const [mostrarMenuFiltroTipo, setMostrarMenuFiltroTipo] = useState(false);

  // 🏷️ Etiquetas: filtro por etiqueta (IDs selecionados), menu e modal de gerenciamento
  const [filtroEtiquetas, setFiltroEtiquetas] = useState<string[]>([]);
  const [mostrarMenuEtiquetas, setMostrarMenuEtiquetas] = useState(false);
  const [mostrarGerenciarEtiquetas, setMostrarGerenciarEtiquetas] = useState(false);
  const etiquetasFlow = flow.etiquetas || [];

  // 🧮 Contagem do cabeçalho da etapa, igual na Lista e no Kanban.
  //
  // O Kanban mostra só cards RAIZ: card filho aparece dentro do card pai, e o
  // pai pode estar em OUTRA etapa. Isso é o funcionamento normal (no fluxo de
  // compras há etapa com 32 raiz e 31 filhos). A Lista listava tudo, então a
  // mesma etapa aparecia com números diferentes nas duas telas.
  //
  // Aqui o número principal é o de raiz — o que o Kanban desenha — e os filhos
  // são declarados à parte, dizendo ONDE eles estão, em vez de sumirem sem
  // explicação. Mostrar os filhos soltos na coluna deles despejaria dezenas de
  // cards antigos no quadro: eles já estão representados pelo card pai.
  const rotuloContagemEtapa = (cardsDaEtapa: Card[], filtrados?: Card[]) => {
    const raiz = cardsDaEtapa.filter(c => !c.cardPaiId).length;
    const filhos = cardsDaEtapa.length - raiz;
    const sufixo = filhos > 0 ? ` · ${filhos} em cards pai` : '';
    if (filtrados && filtrados.length !== cardsDaEtapa.length) {
      const raizFiltrados = filtrados.filter(c => !c.cardPaiId).length;
      return `${raizFiltrados}/${raiz} cartões${sufixo}`;
    }
    return `${raiz} ${raiz === 1 ? 'cartão' : 'cartões'}${sufixo}`;
  };

  // Helper: resolve o ID do criador do card considerando variações de chaves do backend
  const resolverCriadorId = (card: any): string | undefined => {
    const candidatos = [
      card?.criadoPor,
      card?.createdBy,
      card?.criadoPorId,
      card?.creatorId,
      card?.createdById
    ].filter(Boolean);
    if (candidatos.length > 0) return String(candidatos[0]);
    return undefined;
  };

  // Helper: verifica se o criadorId bate com a condição (considerando userId ou colaboradorId)
  // Busca bidirecional: tanto pelo quando→colaborador quanto pelo criadorId→colaborador
  const matchCriadorCondicao = (criadorId: string, condicaoQuando: string): boolean => {
    // Match direto de IDs
    if (String(condicaoQuando) === String(criadorId)) return true;
    
    // 1️⃣ Forward: quando é um colaboradorId → verificar se o userId desse colaborador bate com criadorId
    const colaboradorPeloId = colaboradores.find(c => c.id === condicaoQuando);
    if (colaboradorPeloId) {
      if (colaboradorPeloId.userId && String(colaboradorPeloId.userId) === String(criadorId)) {
        return true;
      }
    }
    
    // 2️⃣ Reverse: criadorId é um userId → encontrar o colaborador do criador e comparar com quando
    const colaboradorDoCriador = colaboradores.find(c => 
      String(c.userId) === String(criadorId) || String(c.id) === String(criadorId)
    );
    if (colaboradorDoCriador) {
      // Comparar o colaborador do criador com o quando
      if (String(colaboradorDoCriador.id) === String(condicaoQuando)) {
        console.log('🔗 Match reverso (criador→colaborador→quando):', colaboradorDoCriador.nome);
        return true;
      }
      // Comparar emails como último recurso
      if (colaboradorPeloId && colaboradorDoCriador.email && colaboradorPeloId.email &&
          colaboradorDoCriador.email.toLowerCase() === colaboradorPeloId.email.toLowerCase()) {
        console.log('🔗 Match por email do criador:', colaboradorDoCriador.email);
        return true;
      }
    }
    
    // 3️⃣ Tentar match reverso (o quando pode ser userId)
    const colaboradorPeloUserId = colaboradores.find(c => c.userId === condicaoQuando);
    if (colaboradorPeloUserId && String(colaboradorPeloUserId.userId) === String(criadorId)) {
      return true;
    }
    
    // 4️⃣ FIX 26/02/2026: Match por email - quando o colaborador não tem userId preenchido
    // O criadorId pode ser o _id do user (collection users), e o colaborador pode ter
    // o mesmo email mas sem userId. Buscar pelo email do user logado.
    if (colaboradorPeloId && !colaboradorDoCriador) {
      // Não encontramos o colaborador do criador por userId/id
      // Tentar match por email: buscar o user logado e comparar email com o colaborador do "quando"
      const emailUsuario = userPermissions?.currentUser?.email;
      if (emailUsuario && colaboradorPeloId.email && 
          emailUsuario.toLowerCase() === colaboradorPeloId.email.toLowerCase()) {
        console.log('🔗 Match por email do user logado:', emailUsuario);
        return true;
      }
    }
    
    // 5️⃣ FIX 26/02/2026: Match por email reverso - encontrar colaborador pelo email do card
    if (!colaboradorDoCriador) {
      // Verificar se algum colaborador com mesmo email do criador bate com o "quando"
      const emailUsuario = userPermissions?.currentUser?.email;
      if (emailUsuario) {
        const colabPorEmail = colaboradores.find(c => 
          c.email && c.email.toLowerCase() === emailUsuario.toLowerCase()
        );
        if (colabPorEmail && String(colabPorEmail.id) === String(condicaoQuando)) {
          console.log('🔗 Match por email reverso:', colabPorEmail.nome, colabPorEmail.email);
          return true;
        }
      }
    }
    
    return false;
  };

  // 🔥 Socket.IO para sincronização em tempo real
  const { socket, isConnected } = useSocket();

  // 🔗 Carregar configurações de cards conectados para este fluxo
  useEffect(() => {
    const carregarConfigsConectados = async () => {
      try {
        const email = userPermissions?.currentUser?.email;
        const empresaId = flow.empresaId;
        if (!email || !empresaId) return;
        const params = new URLSearchParams({ userEmail: email, empresaId });
        const res = await fetch(apiUrl(`/cards-conectados/configuracoes/fluxo/${flow.id}`) + '?' + params.toString());
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            setCardsConectadosConfigs(data.configuracoes || []);
          }
        }
      } catch (err) {
        console.warn('Erro ao carregar configs de cards conectados:', err);
      }
    };
    carregarConfigsConectados();
  }, [flow.id, flow.empresaId, userPermissions?.currentUser?.email]);

  // 🔐 Carregar permissões granulares do fluxo
  useEffect(() => {
    const carregarPermissoes = async () => {
      try {
        const res = await fetch(apiUrl(`/flows/${flow.id}/permissoes`));
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.permissoes) {
            console.log('🔐 Permissões granulares carregadas:', data.permissoes);
            setPermissoesGranulares(data.permissoes);
          }
        }
      } catch (err) {
        console.warn('Erro ao carregar permissões granulares:', err);
      }
    };
    carregarPermissoes();
  }, [flow.id]);

  // Garantir consistência dos IDs ao carregar dados
  useEffect(() => {
    // Guardar TODAS as etapas sem filtro (para ConfiguracaoEtapa)
    const allEtapas = flow.etapas?.map(etapa => ({
      ...etapa,
      id: String(etapa.id)
    })) || [];
    setTodasEtapas(allEtapas);
    
    let newEtapas = [...allEtapas];
    
    // Filtrar etapas apenas para viewers - editores e admins veem tudo
    if (userPermissions?.currentUser) {
      const userRole = userPermissions.currentUser.role;
      const userId = userPermissions.currentUser.id;
      
      // Apenas VIEWERS têm visualização restrita às etapas onde participam
      if (userRole === 'viewer') {
        const isFlowCreator = flow.createdBy === userId || flow.responsavelId === userId;
        
        // Se não for criador do fluxo, filtrar apenas etapas onde participa
        if (!isFlowCreator) {
          newEtapas = newEtapas.filter(etapa => {
            const isStageParticipant = etapa.participantes?.includes(userId);
            const isSameDepartment = etapa.departamentoId === userPermissions.currentUser.departamentoId;
            return isStageParticipant || isSameDepartment;
          });
        }
      }

      // 🔐 Filtro granular por etapa: permissoesPorEtapa[etapaId].acessar === false oculta a etapa.
      // Admins e criadores do fluxo continuam vendo tudo (failsafe).
      const isFlowCreator = flow.createdBy === userId || flow.responsavelId === userId;
      const isAdmin = userPermissions.canAdmin === true || userRole === 'admin';
      if (!isAdmin && !isFlowCreator && permissoesGranulares.length > 0) {
        const emailUsuario = userPermissions.currentUser.email;
        const colaborador = emailUsuario
          ? colaboradores?.find(c => c.email === emailUsuario)
          : null;
        if (colaborador) {
          const colabId = String(colaborador.id || (colaborador as any)._id);
          const colabIdAlt = String((colaborador as any)._id || colaborador.id);
          const permUser = permissoesGranulares.find(p => {
            const pid = String(p.colaboradorId);
            return pid === colabId || pid === colabIdAlt;
          });
          if (permUser?.permissoesPorEtapa) {
            newEtapas = newEtapas.filter(etapa => {
              const pe = permUser.permissoesPorEtapa[String(etapa.id)];
              return pe ? pe.acessar !== false : true;
            });
          }
        }
      }
    }
    
    const newCards = flow.cards?.map(card => ({
      ...card,
      id: String(card.id),
      etapaId: String(card.etapaId)
    })) || [];
    
    // 🔍 DETECTAR CARDS DUPLICADOS
    const cardIds = newCards.map(c => c.id);
    const uniqueCardIds = new Set(cardIds);
    let cardsFinais = newCards;
    
    if (cardIds.length !== uniqueCardIds.size) {
      console.error('❌ CARDS DUPLICADOS DETECTADOS!');
      const duplicados = cardIds.filter((id, index) => cardIds.indexOf(id) !== index);
      console.error('IDs duplicados:', duplicados);
      console.error('Total: ' + cardIds.length + ' cards, ' + uniqueCardIds.size + ' únicos');
      
      // Remover duplicatas mantendo apenas a primeira ocorrência
      cardsFinais = newCards.filter((card, index) => 
        cardIds.indexOf(card.id) === index
      );
    }
    
    // Popular hierarquia de cards (pais e filhos)
    const cardsComHierarquia = cardsFinais.map(card => {
      if (card.ehCardPai) {
        return popularCardsFilhos(card, cardsFinais);
      }
      return card;
    });

    // 🔐 Ocultar cards que estão em etapas sem permissão de visualizar
    const etapasVisiveisIds = new Set(newEtapas.map(e => String(e.id)));
    let cardsVisiveis = cardsComHierarquia.filter(c => etapasVisiveisIds.has(String(c.etapaId)));

    // 🔐 Filtro por escopo de cards por etapa (todos / próprios / responsável)
    if (userPermissions?.currentUser) {
      const userRole = userPermissions.currentUser.role;
      const userId = userPermissions.currentUser.id;
      const isFlowCreator = flow.createdBy === userId || flow.responsavelId === userId;
      const isAdmin = userPermissions.canAdmin === true || userRole === 'admin';
      if (!isAdmin && !isFlowCreator && permissoesGranulares.length > 0) {
        const emailUsuario = userPermissions.currentUser.email;
        const colaborador = emailUsuario
          ? colaboradores?.find(c => c.email === emailUsuario)
          : null;
        if (colaborador) {
          const colabId = String(colaborador.id || (colaborador as any)._id);
          const colabIdAlt = String((colaborador as any)._id || colaborador.id);
          const userIdStr = userId ? String(userId) : '';
          const permUser = permissoesGranulares.find(p => {
            const pid = String(p.colaboradorId);
            return pid === colabId || pid === colabIdAlt;
          });
          if (permUser?.permissoesPorEtapa) {
            const idsValidos = new Set([colabId, colabIdAlt, userIdStr].filter(Boolean));
            const nomeColab = (colaborador.nome || '').trim().toLowerCase();
            const emailColab = (colaborador.email || '').trim().toLowerCase();
            const tokensMencao = [
              ...Array.from(idsValidos).map(s => String(s).toLowerCase()),
              nomeColab,
              emailColab,
            ].filter(Boolean);
            const containsMention = (val: any): boolean => {
              if (val == null) return false;
              if (Array.isArray(val)) return val.some(containsMention);
              if (typeof val === 'object') return Object.values(val).some(containsMention);
              const s = String(val).toLowerCase();
              return tokensMencao.some(tok => s.includes(tok));
            };
            cardsVisiveis = cardsVisiveis.filter(card => {
              const pe = permUser.permissoesPorEtapa[String(card.etapaId)];
              const escopo = pe?.escopoCards || 'todos';
              if (escopo === 'todos') return true;
              if (escopo === 'proprios') {
                const criador = resolverCriadorId(card);
                return criador ? idsValidos.has(String(criador)) : false;
              }
              if (escopo === 'responsavel') {
                const responsaveis: string[] = [
                  (card as any).responsavelId,
                  (card as any).responsavelIndividualId,
                  (card as any).responsavel,
                ].filter(Boolean).map(String);
                return responsaveis.some(r => idsValidos.has(r));
              }
              if (escopo === 'mencionado') {
                if (containsMention((card as any).campos)) return true;
                if (containsMention((card as any).camposValores)) return true;
                if (containsMention((card as any).titulo)) return true;
                return false;
              }
              return true;
            });
          }
        }
      }
    }

    setEtapas(newEtapas);
    setCards(cardsVisiveis);
    
    // NÃO inicializar etapas expandidas - deixar todas fechadas por padrão
    // (removido código que expandia todas automaticamente)
  }, [flow.id, flow.etapas?.length, flow.cards?.length, JSON.stringify(flow.cards), JSON.stringify(flow.etapas), permissoesGranulares, colaboradores, userPermissions?.currentUser?.email]); // ⚠️ FIX 26/02/2026: Observar TODAS as mudanças em etapas (regrasResponsavel, responsavelPadraoId, etc)

  // Carregar relatórios salvos quando entrar no modo relatórios
  useEffect(() => {
    const carregarRelatorios = async () => {
      if (viewMode !== 'relatorios') return;
      if (!flow.empresaId || !userPermissions?.currentUser?.id) {
        console.log('⚠️ Não é possível carregar relatórios: empresaId ou usuarioId ausentes');
        return;
      }
      
      try {
        setCarregandoRelatorios(true);
        console.log('📥 Carregando relatórios salvos...', { 
          empresaId: flow.empresaId, 
          usuarioId: userPermissions.currentUser.id,
          flowId: flow.id 
        });
        
        const response = await fetch(
          `/api/relatorios?empresaId=${flow.empresaId}&usuarioId=${userPermissions.currentUser.id}&flowId=${flow.id}`
        );
        
        if (!response.ok) {
          console.warn('⚠️ Erro ao carregar relatórios:', response.status);
          return;
        }
        
        const relatorios = await response.json();
        console.log('✅ Relatórios carregados:', relatorios.length);
        setRelatoriosSalvos(relatorios);
      } catch (error) {
        console.error('❌ Erro ao carregar relatórios:', error);
      } finally {
        setCarregandoRelatorios(false);
      }
    };

    carregarRelatorios();
  }, [viewMode, flow.empresaId, flow.id, userPermissions?.currentUser?.id]);

  // 🔥 Listeners Socket.IO para sincronização em tempo real GRANULAR
  useEffect(() => {
    if (!socket) {
      console.log('⚠️ Socket não disponível');
      return;
    }

    console.log('🔌 Registrando listeners Socket.IO granulares');
    console.log('📍 FlowId:', flow.id);
    console.log('👤 Usuário:', userPermissions?.currentUser?.id);
    console.log('🔗 Socket conectado:', socket.connected);
    console.log('🆔 Socket ID:', socket.id);

    // 📝 Card criado por outro usuário/aba
    const handleCardCreated = (data: any) => {
      console.log('🔔 [SOCKET] card_created recebido:', data);
      
      if (data.flowId !== flow.id) {
        console.log('⏭️ Ignorando - flowId diferente:', data.flowId, '!==', flow.id);
        return;
      }
      
      // ⚡ Adicionar card ao estado local (verificar duplicatas)
      const newCard = {
        ...data.card,
        id: String(data.card.id),
        etapaId: String(data.card.etapaId)
      };
      
      setCards(prevCards => {
        // Verificar se card já existe (evitar duplicatas)
        if (prevCards.find(c => c.id === newCard.id)) {
          console.log('⏭️ Card já existe no estado, ignorando duplicata');
          return prevCards;
        }
        console.log('✅ Card adicionado ao estado local via Socket.IO');
        return [...prevCards, newCard];
      });
    };

    // 🔄 Card atualizado ou movido por outro usuário/aba
    const handleCardUpdated = (data: any) => {
      console.log('🔔 [SOCKET] card_updated:', data);
      
      if (data.flowId !== flow.id) return;
      
      // ⚡ Atualizar card no estado local (previne duplicação se mudou de etapa)
      const updatedCard = {
        ...data.card,
        id: String(data.card.id),
        etapaId: String(data.card.etapaId)
      };
      
      setCards(prevCards => {
        // 🔧 FIX: Verificar se a atualização é mais recente
        const cardExistente = prevCards.find(c => c.id === updatedCard.id);
        if (cardExistente) {
          const dataExistente = new Date(cardExistente.updatedAt || 0).getTime();
          const dataRecebida = new Date(updatedCard.updatedAt || 0).getTime();
          
          if (dataRecebida < dataExistente) {
            console.log('⏭️ [SOCKET] Ignorando atualização ANTIGA:', {
              cardId: updatedCard.id,
              etapaRecebida: updatedCard.etapaId,
              etapaAtual: cardExistente.etapaId,
              dataRecebida: new Date(dataRecebida).toLocaleString(),
              dataAtual: new Date(dataExistente).toLocaleString()
            });
            return prevCards; // Ignorar dados antigos
          }
        }
        
        // Se o card mudou de etapa, remover da etapa antiga
        const cardAntigo = prevCards.find(c => c.id === updatedCard.id);
        if (cardAntigo && cardAntigo.etapaId !== updatedCard.etapaId) {
          // Card mudou de etapa - remover e readicionar
          const semCardAntigo = prevCards.filter(c => c.id !== updatedCard.id);
          console.log(`✅ Card atualizado e movido: ${cardAntigo.etapaId} → ${updatedCard.etapaId}`);
          return [...semCardAntigo, updatedCard];
        } else {
          // Card não mudou de etapa - apenas atualizar
          const updated = prevCards.map(c => 
            c.id === updatedCard.id ? updatedCard : c
          );
          console.log('✅ Card atualizado no estado local via Socket.IO');
          return updated;
        }
      });
      
      // Atualizar modal se estiver aberto
      setCardSelecionado(prev => 
        prev?.id === updatedCard.id ? updatedCard : prev
      );
    };

    // 🚚 Card movido por outro usuário/aba
    const handleCardMoved = (data: any) => {
      console.log('🔔 [SOCKET] card_moved:', data);
      
      if (data.flowId !== flow.id) return;
      
      // ⚡ Atualizar etapa do card (remover duplicatas)
      const movedCard = {
        ...data.card,
        id: String(data.card.id),
        etapaId: String(data.card.etapaId)
      };
      
      setCards(prevCards => {
        // 🔧 FIX: Verificar se a movimentação é mais recente
        const cardExistente = prevCards.find(c => c.id === movedCard.id);
        if (cardExistente) {
          const dataExistente = new Date(cardExistente.updatedAt || 0).getTime();
          const dataRecebida = new Date(movedCard.updatedAt || 0).getTime();
          
          if (dataRecebida < dataExistente) {
            console.log('⏭️ [SOCKET] Ignorando movimentação ANTIGA:', {
              cardId: movedCard.id,
              etapaRecebida: movedCard.etapaId,
              etapaAtual: cardExistente.etapaId,
              dataRecebida: new Date(dataRecebida).toLocaleString(),
              dataAtual: new Date(dataExistente).toLocaleString()
            });
            return prevCards; // Ignorar dados antigos
          }
        }
        
        // Remover todas as instâncias do card (previne duplicação)
        const semCardAntigo = prevCards.filter(c => c.id !== movedCard.id);
        // Adicionar card atualizado
        console.log(`✅ Card movido via Socket: ${data.etapaAnterior} → ${data.etapaNova}`);
        return [...semCardAntigo, movedCard];
      });
      
      // Atualizar modal se estiver aberto
      setCardSelecionado(prev => 
        prev?.id === movedCard.id ? movedCard : prev
      );
    };

    // 🗑️ Card deletado por outro usuário/aba
    const handleCardDeleted = (data: any) => {
      console.log('🔔 [SOCKET] card_deleted:', data);
      
      if (data.flowId !== flow.id) return;
      
      // ⚡ Remover card do estado local
      setCards(prevCards => {
        const filtered = prevCards.filter(c => c.id !== data.cardId);
        console.log('✅ Card removido do estado local via Socket.IO');
        return filtered;
      });
      
      // Fechar modal se estiver aberto
      setCardSelecionado(prev => 
        prev?.id === data.cardId ? null : prev
      );
    };

    // 📡 Registrar listeners
    // 🔊 DEBUG: Listener para qualquer evento (remover em produção)
    const handleAnyEvent = (event: string, ...args: any[]) => {
      if (event.includes('card') || event.includes('flow')) {
        console.log(`🔊 [SOCKET DEBUG] Evento: ${event}`, args);
      }
    };
    socket.onAny(handleAnyEvent);
    
    socket.on('card_created', handleCardCreated);
    socket.on('card_updated', handleCardUpdated);
    socket.on('card_moved', handleCardMoved);
    socket.on('card_deleted', handleCardDeleted);
    
    console.log('👂 Listeners granulares registrados');

    return () => {
      socket.offAny(handleAnyEvent);
      socket.off('card_created', handleCardCreated);
      socket.off('card_updated', handleCardUpdated);
      socket.off('card_moved', handleCardMoved);
      socket.off('card_deleted', handleCardDeleted);
      console.log('🔇 Listeners granulares removidos');
    };
  }, [socket, flow.id, userPermissions?.currentUser?.id]);

  // 🔗 Gerenciar URL quando card é aberto/fechado (usando shortCode)
  useEffect(() => {
    if (cardSelecionado) {
      // Usar shortCode na URL para links mais limpos
      const shortCode = generateShortCode(cardSelecionado.id);
      const newUrl = `/flows/${flow.id}/card/${shortCode}`;
      window.history.pushState({ cardId: cardSelecionado.id, shortCode }, '', newUrl);
      console.log('🔗 URL atualizada (shortCode):', newUrl);
    } else {
      const newUrl = `/flows/${flow.id}`;
      window.history.pushState({}, '', newUrl);
      console.log('🔗 URL limpa');
    }
  }, [cardSelecionado, flow.id]);

  // 🔙 Gerenciar navegação do navegador (voltar/avançar)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      console.log('🔙 Navegação detectada:', event.state);
      
      if (event.state?.cardId) {
        const card = cards.find(c => c.id === event.state.cardId);
        if (card) {
          setCardSelecionado(card);
          console.log('✅ Card reaberto:', card.titulo);
        }
      } else {
        setCardSelecionado(null);
        console.log('❌ Card fechado');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [cards]);

  // 🚀 Abrir card automaticamente se ID/shortCode estiver na URL
  useEffect(() => {
    const urlPath = window.location.pathname;
    const cardIdMatch = urlPath.match(/\/card\/([^/]+)$/);
    
    if (cardIdMatch && cardIdMatch[1] && cards.length > 0) {
      const urlCardId = cardIdMatch[1];
      
      // Tentar encontrar o card - pode ser ID completo ou shortCode
      let card = cards.find(c => c.id === urlCardId);
      
      // Se não encontrou, pode ser um shortCode - buscar pelo shortCode gerado de cada card
      if (!card && isValidShortCode(urlCardId)) {
        const alvo = urlCardId.toUpperCase();
        // Tenta o algoritmo novo e, como fallback, o antigo (links já compartilhados)
        card = cards.find(c => generateShortCode(c.id) === alvo)
            || cards.find(c => generateShortCodeLegacy(c.id) === alvo);
        if (card) {
          console.log('🔗 Card encontrado pelo shortCode:', urlCardId, '→', card.titulo);
        }
      }
      
      if (card) {
        console.log('✅ Abrindo card da URL:', card.titulo);
        setCardSelecionado(card);
      } else {
        console.warn('⚠️ Card não encontrado:', urlCardId);
        window.history.replaceState({}, '', `/flows/${flow.id}`);
      }
    }
  }, [cards, flow.id]);

  // UseEffect para abrir card automaticamente quando vindo da Home
  useEffect(() => {
    if (cardToOpen && cards.length > 0) {
      const card = cards.find(c => String(c.id) === String(cardToOpen));
      if (card) {
        console.log('🎯 Abrindo card automaticamente:', card.titulo);
        setCardSelecionado(card);
        if (onCardOpened) {
          onCardOpened();
        }
      }
    }
  }, [cardToOpen, cards, onCardOpened]);

  // Handler para detectar início do drag
  const handleDragStart = (start: any) => {
    console.log('🎬 Drag iniciado:', {
      draggableId: start.draggableId,
      type: start.type,
      source: start.source
    });
  };

  const handleDragEnd = async (result: DropResult) => {
    console.log('🔄 handleDragEnd iniciado:', result);
    console.log('📊 Detalhes do drag:', {
      type: result.type,
      draggableId: result.draggableId,
      source: result.source,
      destination: result.destination,
      reason: result.reason,
      viewMode: viewMode // Log do modo atual
    });
    
    if (!result.destination) {
      console.log('❌ Sem destino definido - drag cancelado ou fora da área');
      return;
    }

    // ⚠️ PROTEÇÃO: Só processar drags se estivermos em kanban ou lista
    if (viewMode !== 'kanban' && viewMode !== 'lista') {
      console.warn('⚠️ Drag ignorado - viewMode não suporta drag:', viewMode);
      return;
    }

    // ⚠️ VALIDAÇÃO: Verificar se os droppables são válidos
    const validDroppableIds = ['etapas-list', ...etapas.map(e => String(e.id))];
    
    if (!validDroppableIds.includes(result.source.droppableId)) {
      console.warn('⚠️ Source droppableId inválido:', result.source.droppableId, 'válidos:', validDroppableIds);
      return;
    }
    
    if (!validDroppableIds.includes(result.destination.droppableId)) {
      console.warn('⚠️ Destination droppableId inválido:', result.destination.droppableId, 'válidos:', validDroppableIds);
      return;
    }

    // 🔀 DRAG DE ETAPAS: Detectar se é um drag de etapa (não de card)
    if (result.type === 'ETAPA') {
      const sourceIndex = result.source.index;
      const destinationIndex = result.destination.index;
      
      console.log('🔄 Drag de ETAPA detectado:', {
        sourceIndex,
        destinationIndex,
        etapaId: result.draggableId,
        sourceDroppableId: result.source.droppableId,
        destDroppableId: result.destination.droppableId
      });
      
      if (sourceIndex === destinationIndex) {
        console.log('⏭️ Mesma posição, ignorando');
        return; // Não mudou de posição
      }

      console.log('✅ Reordenando etapa:', { sourceIndex, destinationIndex });
      
      const etapaId = result.draggableId;
      handleReordenarEtapas(etapaId, destinationIndex);
      return;
    }
    
    // 🃏 DRAG DE CARDS: delega para a rotina compartilhada de movimentação —
    // exatamente a mesma usada pelo "Mover para" dentro do modal do card.
    await executarMovimentacaoCard(
      String(result.draggableId),
      String(result.source.droppableId),
      String(result.destination.droppableId),
      result.destination.index
    );
  };

  // 🔐 REGRA ÚNICA DE MOVIMENTAÇÃO ENTRE ETAPAS
  // Fonte de verdade compartilhada pelo drag-and-drop e pelo botão "Mover para"
  // do modal do card. Assim os dois caminhos nunca divergem.
  // - motivo preenchido  → bloqueio que deve ser avisado ao usuário (alert)
  // - motivo ausente     → bloqueio silencioso (falta de permissão)
  const verificarMovimentacaoPermitida = (
    sourceId: string,
    destinationId: string,
    opts: { log?: boolean; card?: Card } = {}
  ): { permitido: boolean; motivo?: string } => {
    const log = (...args: any[]) => { if (opts.log) console.log(...args); };

    const sourceEtapa = etapas.find(e => String(e.id) === String(sourceId));
    const destinationEtapa = etapas.find(e => String(e.id) === String(destinationId));

    if (!sourceEtapa || !destinationEtapa) {
      log('❌ Etapa não encontrada');
      return { permitido: false };
    }

    // Verificar se usuário pode editar AMBAS as etapas
    const canEditSource = checkCardEditPermission?.(sourceEtapa, opts.card);
    const canEditDestination = checkCardEditPermission?.(destinationEtapa, opts.card);
    // card de outro comprador em etapa soDonoEdita: não move, nem pelas permissões por etapa
    if (opts.card && (sourceEtapa as any).soDonoEdita && !canEditSource) {
      return { permitido: false, motivo: 'Este card é de outro comprador. Só o responsável ou o gestor dele pode mexer.' };
    }
    
    // 🔎 Permissão granular do usuário logado neste fluxo (se existir registro salvo)
    const emailUsuario = userPermissions?.currentUser?.email;
    const colaboradorLogado = emailUsuario ? colaboradores?.find(c => c.email === emailUsuario) : null;
    const permUser = (() => {
      if (!colaboradorLogado) return undefined;
      const colabId = String(colaboradorLogado.id || (colaboradorLogado as any)._id);
      const colabIdAlt = String((colaboradorLogado as any)._id || colaboradorLogado.id);
      // Busca por id OU _id (caso o modal tenha salvo num formato e o runtime use outro)
      return permissoesGranulares.find(p => {
        const pid = String(p.colaboradorId);
        return pid === colabId || pid === colabIdAlt;
      });
    })();

    const moverCardsCfg = permUser?.permissoesGerais?.moverCards;

    // 🚫 Toggle "Mover cards entre etapas" desligado explicitamente no modal de
    // permissões = bloqueio duro, mesmo que o usuário possa EDITAR as duas etapas.
    // (Admin já passou direto lá em cima pelo checkCardEditPermission? Não —
    //  admin nunca tem registro granular aplicado aqui, então segue livre.)
    if (permUser && moverCardsCfg && moverCardsCfg.permitido === false && !userPermissions?.canAdmin) {
      log('⛔ [moverCards] Bloqueado: toggle "Mover cards entre etapas" desligado para este colaborador');
      return { permitido: false };
    }

    // Se não pode editar, verificar permissão granular moverCards
    let canMoveByPermissao = false;
    if (!canEditSource || !canEditDestination) {
      log('🔍 [moverCards] Lookup colaborador:', {
        emailUsuario,
        colaboradorEncontrado: !!colaboradorLogado,
        colaboradorIdRuntime: colaboradorLogado?.id,
        colaborador_idRuntime: (colaboradorLogado as any)?._id,
        totalColaboradores: colaboradores?.length || 0,
        totalPermissoes: permissoesGranulares.length,
        idsSalvosNasPermissoes: permissoesGranulares.map(p => p.colaboradorId),
        permUserEncontrado: !!permUser,
        moverCardsPermitido: moverCardsCfg?.permitido,
        permissoesPorEtapaSource: permUser?.permissoesPorEtapa?.[sourceId],
        permissoesPorEtapaDest: permUser?.permissoesPorEtapa?.[destinationId],
      });
      if (moverCardsCfg?.permitido) {
        // 1) Modelo NOVO da UI: permissoesPorEtapa[etapaId] = { acessar, moverPara, moverDe }
        //    O modal de "Configuração de Permissões" grava aqui (checkboxes Ver / → Para / ← De).
        //    Default = true quando ausente, conforme UI ({ acessar: true, moverPara: true, moverDe: true }).
        const peSource = permUser?.permissoesPorEtapa?.[sourceId];
        const peDest = permUser?.permissoesPorEtapa?.[destinationId];
        const podeSairOrigem = peSource ? peSource.moverDe !== false : true;
        const podeEntrarDestino = peDest ? peDest.moverPara !== false : true;

        // 2) Modelo LEGADO: permissoesGerais.moverCards.regras = [{ deEtapaId, paraEtapasIds }]
        const regras = moverCardsCfg.regras;
        if (regras && regras.length > 0) {
          const regraSource = regras.find((r: any) => String(r.deEtapaId) === sourceId);
          const passouRegrasLegado = regraSource
            ? regraSource.paraEtapasIds.includes(destinationId)
            : false;
          canMoveByPermissao = passouRegrasLegado && podeSairOrigem && podeEntrarDestino;
        } else {
          // Sem regras legadas: usa permissoesPorEtapa
          canMoveByPermissao = podeSairOrigem && podeEntrarDestino;
        }
        if (canMoveByPermissao) {
          log('✅ Permissão de mover cards concedida via toggle (permissoesGranulares)', {
            podeSairOrigem,
            podeEntrarDestino,
          });
        } else {
          log('⛔ [moverCards] Bloqueado por permissoesPorEtapa', {
            podeSairOrigem,
            podeEntrarDestino,
          });
        }
      }
    }

    if ((!canEditSource || !canEditDestination) && !canMoveByPermissao) {
      log('❌ Sem permissão para mover card entre estas etapas', {
        source: sourceEtapa.nome,
        canEditSource,
        destination: destinationEtapa.nome,
        canEditDestination,
        canMoveByPermissao
      });
      return { permitido: false };
    }

    // 🎯 Verificar se a etapa de origem permite movimentação para a etapa de destino
    if (sourceEtapa.permiteMoverPara !== undefined) {
      // Se permiteMoverPara foi configurado (array existe, mesmo que vazio)
      if (sourceEtapa.permiteMoverPara.length === 0) {
        // Array vazio = nenhuma movimentação permitida
        log('❌ Nenhuma movimentação permitida - array vazio:', {
          etapaOrigem: sourceEtapa.nome,
          etapaDestino: destinationEtapa.nome,
          permiteMoverPara: sourceEtapa.permiteMoverPara
        });
        return {
          permitido: false,
          motivo: `⚠️ A etapa "${sourceEtapa.nome}" não permite mover cards para nenhuma outra etapa. Configure as movimentações permitidas.`
        };
      } else if (!sourceEtapa.permiteMoverPara.map(String).includes(String(destinationId))) {
        // Array com itens, mas destino não está na lista
        log('❌ Movimentação não permitida pela configuração da etapa:', {
          etapaOrigem: sourceEtapa.nome,
          etapaDestino: destinationEtapa.nome,
          permiteMoverPara: sourceEtapa.permiteMoverPara
        });
        return {
          permitido: false,
          motivo: `⚠️ A etapa "${sourceEtapa.nome}" não permite mover cards para "${destinationEtapa.nome}". Verifique as configurações do fluxo.`
        };
      }
    }
    // Se permiteMoverPara é undefined, permite todas as movimentações (comportamento padrão)

    return { permitido: true };
  };

  // 📋 Etapas para as quais este card pode ser movido AGORA (mesmas regras do drag-and-drop).
  // Alimenta o seletor "Mover para" do modal de detalhes do card.
  const getEtapasDisponiveisParaMover = (card: Card): Etapa[] => {
    const sourceId = String(card.etapaId);
    return etapas.filter(e =>
      String(e.id) !== sourceId &&
      verificarMovimentacaoPermitida(sourceId, String(e.id), { card }).permitido
    );
  };

  // 🚚 Movimentação de card entre etapas (drag-and-drop e "Mover para" do modal).
  // destIndexRelativo = posição dentro da coluna de destino (0 = topo).
  const executarMovimentacaoCard = async (
    draggableId: string,
    sourceId: string,
    destinationId: string,
    destIndexRelativo: number,
    solicitacaoReenvio?: SolicitacaoReenvioPayload,
    opcoes: { viaAutomacao?: boolean } = {}
  ) => {
    const sourceEtapa = etapas.find(e => String(e.id) === String(sourceId));
    const destinationEtapa = etapas.find(e => String(e.id) === String(destinationId));

    if (!sourceEtapa || !destinationEtapa) {
      console.error('❌ Etapas não encontradas', { sourceId, destinationId });
      return;
    }

    const verificacao = opcoes.viaAutomacao
      ? { permitido: true as const, motivo: undefined }
      : verificarMovimentacaoPermitida(sourceId, destinationId, { log: true, card: cards.find(c => String(c.id) === String(draggableId)) });
    if (!verificacao.permitido) {
      if (verificacao.motivo) alert(verificacao.motivo);
      return;
    }

    // 🔗 Etapa que exige card conectado antes de o card sair dela (pedido do André,
    // 08/10/2026: Chamados de TI "Em compras" → Fluxo de compras e pagamentos).
    // Sem a conexão feita, o chamado seguia sem compra aberta e ninguém comprava.
    if (!opcoes.viaAutomacao && (sourceEtapa as any).exigirCardConectado) {
      const cardAlvo = cards.find(c => String(c.id) === String(draggableId));
      if (cardAlvo && !(cardAlvo.cardsConectadosIds && cardAlvo.cardsConectadosIds.length > 0)) {
        alert((sourceEtapa as any).exigirCardConectadoMensagem ||
          `A etapa "${sourceEtapa.nome}" exige um card conectado antes de seguir. ` +
          'Abra o card e crie a conexão em "Cards conectados".');
        return;
      }
    }

    // 🔒 Trava de saída (pedido do Evandro, 14/10/2026, Compras Revenda): a etapa com
    // exigirObrigatoriosParaSair só deixa o card AVANÇAR com os campos obrigatórios dela
    // preenchidos (ex.: o documento da análise, mesmo sem necessidade de compra).
    // Voltar para uma etapa anterior continua livre. Antes, o obrigatório só era cobrado
    // ao salvar o card, e arrastar ou "Mover para" passava sem nada.
    if (!opcoes.viaAutomacao && (sourceEtapa as any).exigirObrigatoriosParaSair
        && Number((destinationEtapa as any).ordemFluxo ?? 0) > Number((sourceEtapa as any).ordemFluxo ?? 0)) {
      const cardAlvo = cards.find(c => String(c.id) === String(draggableId));
      const vazio = (v: any) => v == null || (Array.isArray(v) ? v.length === 0 : String(v).trim() === '');
      const LF = String.fromCharCode(10);
      const faltando = (sourceEtapa.campos || []).filter(c => c.obrigatorio && c.tipo !== 'texto_informativo'
        && vazio((cardAlvo?.campos as any)?.[c.id]));
      if (cardAlvo && faltando.length) {
        alert(`A etapa "${sourceEtapa.nome}" só avança com os campos obrigatórios preenchidos e salvos:` + LF + LF + '• ' +
          faltando.map(c => c.nome).join(LF + '• '));
        return;
      }
      // 🧾 Pedido no GIX (Negociação do Compras Revenda): sem ele completo a OC não sai sozinha
      const faltasPedido = (sourceEtapa as any).pedidoGix && !/reprov|cancel/i.test(destinationEtapa.nome || '')
        ? faltasDoPedido((cardAlvo?.campos as any)?.__pedidoGix) : [];
      if (cardAlvo && faltasPedido.length) {
        alert(`Complete e salve o "Pedido no GIX" antes de avançar. Falta: ${faltasPedido.join(', ')}.`);
        return;
      }
    }

    // 📎 Etapa de reprovação configurada para pedir reenvio de anexo:
    // segura a movimentação até a equipe dizer QUAIS campos estão errados.
    // Sem isso o card cairia em Reprovados sem o solicitante saber o que fazer.
    const destinationEtapaConfig = flow.etapas?.find(e => String(e.id) === String(destinationId)) || destinationEtapa;
    if (destinationEtapaConfig.solicitarReenvioAnexos && !solicitacaoReenvio) {
      const cardAlvo = cards.find(c => String(c.id) === String(draggableId));
      // Reprovado só por motivo interno: não há documento para pedir, entra direto.
      if (cardAlvo && !reprovacaoSoPorMotivoInterno(cardAlvo, flow, destinationEtapaConfig)) {
        setReenvioPendente({
          card: cardAlvo,
          draggableId,
          sourceId: String(sourceId),
          destinationId: String(destinationId),
          destIndexRelativo,
        });
        return;
      }
    }

    try {
      console.log('📍 IDs:', { sourceId, destinationId });

      console.log('📋 Etapas encontradas:', {
        source: sourceEtapa.nome,
        destination: destinationEtapa.nome,
        sourcePermiteMoverPara: sourceEtapa.permiteMoverPara
      });

      console.log('🃏 Cards antes da movimentação:', cards.length);

      // Corrigir seleção do card: o índice de origem é relativo à coluna,
      // então usamos o draggableId (id do card) para localizar o índice no array global
      const newCards = Array.from(cards);
      const sourceGlobalIndex = newCards.findIndex(c => String(c.id) === String(draggableId));
      if (sourceGlobalIndex === -1) {
        console.error('❌ Card não encontrado pelo draggableId', { draggableId });
        return;
      }
      const [movedCard] = newCards.splice(sourceGlobalIndex, 1);
      
      console.log('📤 Card sendo movido:', {
        titulo: movedCard.titulo,
        etapaOrigem: movedCard.etapaId,
        etapaDestino: destinationEtapa.id
      });
      
      // ⏳ Ativar loading no card IMEDIATAMENTE
      setCardMovendo(String(movedCard.id));
      
      // 🎯 Forçar re-render para mostrar loading antes de processar
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // 🎯 APLICAR REGRAS DE RESPONSÁVEL AUTOMÁTICO
      let novoResponsavelId: string | undefined = undefined;
      
      // ⚠️ FIX 26/02/2026: Usar flow.etapas (prop fresca) para buscar regrasResponsavel,
      // porque o state local 'etapas' pode estar desatualizado (useEffect só roda em re-render)
      const destinationEtapaFresh = flow.etapas?.find(e => String(e.id) === destinationId);
      const regrasSource = destinationEtapaFresh || destinationEtapa;
      
      console.log('🔎 DEBUG regrasResponsavel:', {
        etapaDestino: destinationEtapa.nome,
        temRegrasNoState: !!destinationEtapa.regrasResponsavel,
        condicoesNoState: destinationEtapa.regrasResponsavel?.condicoes?.length || 0,
        temRegrasNoFlow: !!destinationEtapaFresh?.regrasResponsavel,
        condicoesNoFlow: destinationEtapaFresh?.regrasResponsavel?.condicoes?.length || 0,
        usandoFonte: regrasSource === destinationEtapaFresh ? 'flow.etapas (fresco)' : 'state etapas'
      });
      
      // 🙋 Etapa "quem mover para cá vira o responsável": vale para arrastar e
      // "Mover para"; viaAutomacao (automação segurada pelo modal de reenvio) não conta.
      // O backend faz a mesma conta e é quem vale; aqui é só para a tela já mostrar.
      let responsavelPorQuemMoveu = false;
      if (regrasSource.responsavelQuemMoveu && !opcoes.viaAutomacao) {
        const eu = userPermissions?.currentUser;
        const emailEu = String(eu?.email || '').toLowerCase();
        const meuColab = colaboradores?.find(c =>
          (eu?.id && (String(c.userId || '') === String(eu.id) || String(c.id) === String(eu.id))) ||
          (emailEu && String(c.email || '').toLowerCase() === emailEu)
        );
        if (meuColab) {
          novoResponsavelId = String(meuColab.id);
          responsavelPorQuemMoveu = true;
          console.log('🙋 Quem moveu vira responsável:', meuColab.nome);
        }
      }

      // Verificar se há regras condicionais configuradas na etapa de destino
      if (!novoResponsavelId && regrasSource.regrasResponsavel && regrasSource.regrasResponsavel.condicoes.length > 0) {
        const regras = regrasSource.regrasResponsavel;
        
        if (regras.tipo === 'criador') {
          // Baseado em quem criou o card
          const criadorId = String(resolverCriadorId(movedCard) || userPermissions?.currentUser?.id || '');
          const condicaoDireta = regras.condicoes.find(c => matchCriadorCondicao(criadorId, c.quando));
          const condicaoGenerica = regras.condicoes.find(c => c.quando === 'card_creator');
          const condicao = condicaoDireta || condicaoGenerica;
          if (condicao?.atribuirPara) {
            novoResponsavelId = condicao.atribuirPara;
            console.log('🎯 Regra aplicada (criador):', { criadorId, novoResponsavelId });
          }
        } else if (regras.tipo === 'responsavel_anterior') {
          // Baseado no responsável da etapa anterior
          const responsavelAnterior = String(movedCard.responsavelIndividualId || '');
          const condicao = regras.condicoes.find(c => String(c.quando) === responsavelAnterior);
          if (condicao) {
            novoResponsavelId = condicao.atribuirPara;
            console.log('🎯 Regra aplicada (responsável anterior):', { responsavelAnterior, novoResponsavelId });
          }
        }
      }
      
      // Se não há regra condicional, verificar responsável padrão da etapa
      if (!novoResponsavelId && (regrasSource.responsavelPadraoId || destinationEtapa.responsavelPadraoId)) {
        novoResponsavelId = regrasSource.responsavelPadraoId || destinationEtapa.responsavelPadraoId;
        console.log('👤 Responsável padrão aplicado:', novoResponsavelId);
      }
      
      // 🔄 FIX 26/02/2026: Se NENHUMA regra condicional nem responsável padrão foi aplicado,
      // mas a etapa tem regras condicionais configuradas (e nenhuma bateu com o criador),
      // FORÇAR limpeza do responsável anterior para não carregar para frente.
      // Sem isso, o responsável da etapa anterior "passa reto" para a nova etapa.
      if (!novoResponsavelId && regrasSource.regrasResponsavel && regrasSource.regrasResponsavel.condicoes?.length > 0) {
        // A etapa tem regras mas nenhuma bateu - NÃO manter o responsável anterior
        // O backend vai reaplicar e se nada bater, usa responsavelPadraoId ou fica vazio
        novoResponsavelId = regrasSource.responsavelPadraoId || destinationEtapa.responsavelPadraoId || 'CLEAR_RESPONSAVEL';
        console.log('⚠️ Nenhuma regra condicional bateu, usando padrão ou limpando:', novoResponsavelId);
      }
      
      // 🔄 LIMPAR CAMPOS QUE DISPARAM AUTOMAÇÕES DE SAÍDA (movimento para etapa anterior)
      // ⚠️ IMPORTANTE - Corrigido em 22/01/2026
      // Quando o usuário move um card MANUALMENTE para uma etapa ANTERIOR:
      // 1. Identificamos as automações que SAEM dessa etapa destino
      // 2. Pegamos os campos que disparam essas automações (condições)
      // 3. Limpamos esses campos para a automação NÃO executar automaticamente
      // 4. Assim o usuário pode preencher novamente e a automação funciona corretamente
      // Exemplo: Card volta de Contabilidade → Financeiro. No Financeiro, campo "aprovado" dispara
      //          automação para Contabilidade. Limpamos "aprovado" para o usuário poder aprovar novamente.
      const indexOrigem = etapas.findIndex(e => String(e.id) === sourceId);
      const indexDestino = etapas.findIndex(e => String(e.id) === destinationId);
      const isMovimentoParaTras = indexDestino < indexOrigem;
      
      // Campos a serem limpos (para não disparar automação novamente)
      let camposParaLimpar: string[] = [];
      
      // 🔍 DEBUG: Log para verificar automações disponíveis
      console.log('🔍 DEBUG - Verificando limpeza de campos:', {
        isMovimentoParaTras,
        indexOrigem,
        indexDestino,
        automacoesDisponiveis: automacoes?.length || 0,
        etapaDestinoId: destinationId,
        etapaDestinoNome: destinationEtapa.nome
      });
      
      // naoLimparGatilhosAoVoltar (Viagens, 29/10/2026): etapa cujos gatilhos são dados do pedido
      // (viajantes, cotações...) — limpar apagaria o que a pessoa já preencheu.
      if (isMovimentoParaTras && !(destinationEtapa as any).naoLimparGatilhosAoVoltar && automacoes && automacoes.length > 0) {
        console.log('🔙 Movimento para etapa ANTERIOR detectado:', {
          de: sourceEtapa.nome,
          para: destinationEtapa.nome,
          indexOrigem,
          indexDestino
        });
        
        // Buscar automações que SAEM da etapa de destino (onde o card está indo)
        const automacoesEtapaDestino = automacoes.filter(
          (a: any) => String(a.etapaOrigemId) === destinationId
        );
        
        console.log('🤖 Automações da etapa destino:', {
          quantidade: automacoesEtapaDestino.length,
          automacoes: automacoesEtapaDestino.map((a: any) => ({
            id: a.id,
            nome: a.nome,
            etapaOrigemId: a.etapaOrigemId,
            condicoes: a.condicoes,
            acoes: a.acoes?.map((ac: any) => ({
              tipo: ac.tipo,
              configuracao: ac.configuracao
            }))
          }))
        });
        
        // Para cada automação, identificar os campos que disparam movimento
        automacoesEtapaDestino.forEach((automacao: any) => {
          console.log('🔍 Verificando automação:', automacao.nome);
          
          // Verificar cada ação da automação
          automacao.acoes?.forEach((acao: any) => {
            const tipoAcao = acao.tipo;
            const ehAcaoMover = tipoAcao === 'mover_card' || tipoAcao === 'moverCard' || tipoAcao === 'moveCard';
            
            console.log('  📌 Ação:', {
              tipo: tipoAcao,
              ehAcaoMover,
              temConfiguracaoRegrasMover: !!acao.configuracao?.regrasMover,
              qtdRegrasMover: acao.configuracao?.regrasMover?.length || 0
            });
            
            if (ehAcaoMover) {
              // 1️⃣ ESTRUTURA 1: Condições em automacao.condicoes (estrutura simples)
              if (automacao.condicoes && automacao.condicoes.length > 0) {
                console.log('  ✅ Estrutura 1: condicoes no nível da automação');
                automacao.condicoes.forEach((condicao: any) => {
                  if (condicao.campoId && !camposParaLimpar.includes(condicao.campoId)) {
                    camposParaLimpar.push(condicao.campoId);
                    console.log('  🧹 Campo encontrado (estrutura 1):', condicao.campoId);
                  }
                });
              }
              
              // 2️⃣ ESTRUTURA 2: Condições em acao.configuracao.regrasMover[].condicoes[] ou .condicao
              if (acao.configuracao?.regrasMover && acao.configuracao.regrasMover.length > 0) {
                console.log('  ✅ Estrutura 2: regrasMover dentro da ação');
                acao.configuracao.regrasMover.forEach((regra: any) => {
                  const condicoes = regra.condicoes?.length > 0 ? regra.condicoes : (regra.condicao ? [regra.condicao] : []);
                  condicoes.forEach((cond: any) => {
                    const campoId = cond.campoId;
                    if (campoId && !camposParaLimpar.includes(campoId)) {
                      camposParaLimpar.push(campoId);
                      console.log('  🧹 Campo encontrado (estrutura 2):', campoId);
                    }
                  });
                });
              }
              
              // 3️⃣ ESTRUTURA 3: Condições diretamente em acao.configuracao.condicoes
              if (acao.configuracao?.condicoes && acao.configuracao.condicoes.length > 0) {
                console.log('  ✅ Estrutura 3: condicoes dentro da configuração da ação');
                acao.configuracao.condicoes.forEach((condicao: any) => {
                  if (condicao.campoId && !camposParaLimpar.includes(condicao.campoId)) {
                    camposParaLimpar.push(condicao.campoId);
                    console.log('  🧹 Campo encontrado (estrutura 3):', condicao.campoId);
                  }
                });
              }
            }
          });
        });
        
        console.log('📋 Total de campos para limpar:', camposParaLimpar.length, camposParaLimpar);
      }
      
      // 🧹 LIMPAR CAMPOS CONFIGURADOS NA ETAPA DESTINO (configuração da etapa)
      // Isso complementa a limpeza automática baseada em automações
      if (destinationEtapa.limparCamposAoRetornar?.ativo && destinationEtapa.limparCamposAoRetornar.campos.length > 0) {
        // Verificar se o card já passou por esta etapa antes (retorno)
        const jaPassouPelaEtapa = movedCard.etapaHistory?.some(
          (h: any) => String(h.etapaId) === String(destinationId)
        );
        if (jaPassouPelaEtapa) {
          console.log('🧹 [CONFIG-ETAPA] Card retornando para etapa com limparCamposAoRetornar configurado');
          destinationEtapa.limparCamposAoRetornar.campos.forEach(campoId => {
            if (!camposParaLimpar.includes(campoId)) {
              camposParaLimpar.push(campoId);
              console.log('  🧹 [CONFIG-ETAPA] Campo adicionado para limpeza:', campoId);
            }
          });
        }
      }
      
      // Preparar campos do card (incluindo limpeza se necessário)
      const camposAtualizados = { ...movedCard.campos };
      
      // 📋 Guardar informações dos campos limpos para notificação e histórico
      const camposLimposInfo: { campoId: string; nomeCampo: string; valorAnterior: any }[] = [];
      
      if (camposParaLimpar.length > 0) {
        console.log('🧹 Limpando', camposParaLimpar.length, 'campos para evitar automação automática');
        
        camposParaLimpar.forEach(campoId => {
          const valorAtual = camposAtualizados[campoId];
          
          // Buscar nome do campo para exibição
          const campoConfig = destinationEtapa.campos?.find(c => c.id === campoId) ||
                              etapas.flatMap(e => e.campos || []).find(c => c.id === campoId);
          const nomeCampo = campoConfig?.nome || campoId;
          
          // Salvar info do campo limpo (se tinha valor)
          if (valorAtual !== undefined && valorAtual !== '' && 
              !(Array.isArray(valorAtual) && valorAtual.length === 0)) {
            camposLimposInfo.push({
              campoId,
              nomeCampo,
              valorAnterior: valorAtual
            });
          }
          
          // Determinar valor vazio baseado no tipo atual do campo
          if (Array.isArray(valorAtual)) {
            camposAtualizados[campoId] = [];
          } else {
            camposAtualizados[campoId] = '';
          }
          
          console.log('  ✓ Campo', campoId, 'limpo (era:', typeof valorAtual === 'string' ? valorAtual.substring(0, 30) : valorAtual, ')');
        });
        
        // 🔔 Mostrar notificação toast com campos limpos
        if (camposLimposInfo.length > 0) {
          const camposParaToast = camposLimposInfo.map(info => ({
            nomeCampo: info.nomeCampo,
            valorAnterior: Array.isArray(info.valorAnterior) 
              ? info.valorAnterior.join(', ')
              : String(info.valorAnterior || '').substring(0, 100)
          }));
          
          // Mostrar toast bonito (não bloqueia a interface)
          setTimeout(() => {
            setToastInfo({
              visible: true,
              titulo: '⚠️ Campos Limpos Automaticamente',
              mensagem: 'Card movido para etapa anterior. Os campos abaixo foram limpos para evitar disparo automático da automação:',
              camposLimpos: camposParaToast
            });
            
            // Auto-fechar após 10 segundos
            setTimeout(() => {
              setToastInfo(null);
            }, 10000);
          }, 300);
        }
      }
      
      // Atualizar o card movido
      const updatedCard = {
        ...movedCard,
        etapaId: String(destinationEtapa.id),
        campos: camposAtualizados,
        updatedAt: new Date(),
        // ⚠️ FIX 26/02/2026: Só sobrescrever responsável se uma regra foi encontrada.
        // 🔄 FIX 26/02/2026: Quando movendo de etapa, SEMPRE enviar o novo responsável.
        // Se novoResponsavelId é 'CLEAR_RESPONSAVEL', limpar o responsável (nenhuma regra bateu).
        // Se é undefined, manter o original (o backend aplica regras e corrige).
        ...(novoResponsavelId === 'CLEAR_RESPONSAVEL' 
          ? { responsavelIndividualId: '' } 
          : novoResponsavelId ? { responsavelIndividualId: novoResponsavelId } : {}),
        isExplicitMove: true, // 🛡️ Flag para indicar que é uma movimentação intencional (drag-and-drop)
        etapaHistory: [
          ...(((movedCard as any).etapaHistory || []).map((h: any) => {
            // Se encontrar a entrada da etapa atual (origem), adicionar leftAt
            if (String(h.etapaId) === String(sourceId) && !h.leftAt) {
              return { ...h, leftAt: new Date() };
            }
            return h;
          })),
          {
            etapaId: String(destinationEtapa.id),
            movedAt: new Date(),
            movedBy: userPermissions?.currentUser?.id,
            motivo: isMovimentoParaTras ? 'dragdrop_retorno' : 'dragdrop',
            // � FIX 23/01/2026: SALVAR camposSnapshot com anexos para histórico
            camposSnapshot: { ...(movedCard.campos || {}) },
            // �📋 Salvar campos que foram limpos automaticamente (para relatórios)
            camposLimposAutomaticamente: camposLimposInfo.length > 0 ? camposLimposInfo.map(info => ({
              campoId: info.campoId,
              nomeCampo: info.nomeCampo,
              valorAnterior: info.valorAnterior,
              motivoLimpeza: 'Movimentação manual para etapa anterior - evitar disparo automático de automação'
            })) : undefined
          }
        ]
      };
      
      console.log('🔄 Card atualizado:', {
        titulo: updatedCard.titulo,
        novaEtapaId: updatedCard.etapaId,
        responsavelLimpo: true
      });
  
      // Inicializar campos da nova etapa
      destinationEtapa.campos.forEach(campo => {
        if (!(campo.id in updatedCard.campos)) {
          switch (campo.tipo) {
            case 'texto':
            case 'data':
            case 'valor':
              updatedCard.campos[campo.id] = '';
              break;
            case 'lista':
              // Lista simples (select) - string vazia por padrão
              updatedCard.campos[campo.id] = '';
              break;
            case 'checkbox':
            case 'progresso':
              // Campos multi-seleção - array vazio por padrão
              updatedCard.campos[campo.id] = [];
              break;
            case 'anexo':
              updatedCard.campos[campo.id] = [];
              break;
            default:
              updatedCard.campos[campo.id] = '';
          }
        }
      });

      // Campo tipo Responsável da etapa destino acompanha quem moveu; senão o campo
      // ficava vazio enquanto o cabeçalho já mostrava a pessoa.
      if (responsavelPorQuemMoveu && novoResponsavelId) {
        destinationEtapa.campos.forEach(campo => {
          if (campo.tipo === 'responsavel') updatedCard.campos[campo.id] = novoResponsavelId;
        });
      }

      // Inserir o card atualizado na posição correta DENTRO da etapa destino.
      // destIndexRelativo é relativo aos cards da etapa destino, então calculamos
      // o índice global correspondente para inserir no array completo.
      const indicesGlobaisDestino: number[] = [];
      newCards.forEach((c, idx) => {
        if (String(c.etapaId) === String(destinationEtapa.id)) indicesGlobaisDestino.push(idx);
      });
      // Índice global onde vamos inserir: posição 'destIndexRelativo' dentro dos índices globais
      let insertionGlobalIndex: number;
      if (destIndexRelativo <= 0) {
        // Inserir antes do primeiro da etapa destino (ou na frente se vazia)
        insertionGlobalIndex = indicesGlobaisDestino[0] ?? newCards.length;
      } else if (destIndexRelativo >= indicesGlobaisDestino.length) {
        // Inserir após o último da etapa destino
        insertionGlobalIndex = (indicesGlobaisDestino[indicesGlobaisDestino.length - 1] ?? (newCards.length - 1)) + 1;
      } else {
        insertionGlobalIndex = indicesGlobaisDestino[destIndexRelativo];
      }

      newCards.splice(insertionGlobalIndex, 0, updatedCard);
      
      console.log('💾 [FRONTEND] Card:', updatedCard.titulo);
      console.log('📧 [FRONTEND] De:', sourceEtapa.nome, '→ Para:', destinationEtapa.nome);
      
      // ⚡ Atualizar UI imediatamente (optimistic update)
      setCards(newCards);
      setCardMovendo(null);
      
      // Abrir modal do card movido
      setCardSelecionado(null);
      setTimeout(() => setCardSelecionado(updatedCard), 0);
      
      // ⚡ Salvar APENAS o card movido (operação atômica)
      console.log('🚀 Salvando card movido atomicamente...');
      
      try {
        const response = await fetch(`/api/flows/${flow.id}/cards/${updatedCard.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Data': JSON.stringify({
              id: userPermissions?.currentUser?.id || 'admin_user',
              email: userPermissions?.currentUser?.email,
              empresaId: flow.empresaId || 'empresa_principal',
              role: 'admin'
            })
          },
          // isMovimentoManual só no corpo, não no state: se ficasse no card, um
          // movimento por automação depois contaria como "quem moveu".
          body: JSON.stringify({ ...updatedCard, isMovimentoManual: !opcoes.viaAutomacao })
        });
        
        if (!response.ok) {
          // O backend devolve o texto da exceção em "error": sem ele o 500 vira
          // um "Recarregue a página" que não diz nada.
          let detalhe = '';
          try { detalhe = (await response.json())?.error || ''; } catch { /* corpo não-JSON */ }
          throw new Error(`Erro ao salvar card: ${response.status}${detalhe ? ` — ${detalhe}` : ''}`);
        }

        console.log('✅ Card movido salvo atomicamente no backend!');

        // 📎 Registrar o pedido de reenvio e avisar o solicitante pelo portal.
        // Só depois do PUT: se o card não moveu, não faz sentido cobrar arquivo.
        if (solicitacaoReenvio) {
          try {
            const respReenvio = await fetch(apiUrl(`/cards/${updatedCard.id}/solicitar-reenvio`), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-User-Data': JSON.stringify({
                  id: userPermissions?.currentUser?.id || 'admin_user',
                  empresaId: flow.empresaId || 'empresa_principal',
                  role: 'admin'
                })
              },
              body: JSON.stringify({
                campos: solicitacaoReenvio.campos,
                observacao: solicitacaoReenvio.observacao,
                etapaOrigemId: String(sourceId),
                etapaRetornoId: solicitacaoReenvio.etapaRetornoId,
                solicitadoPor: userPermissions?.currentUser?.id,
                solicitadoPorNome: userPermissions?.currentUser?.nome || 'Equipe interna',
              })
            });
            const dataReenvio = await respReenvio.json();
            if (dataReenvio.success) {
              setToastInfo({
                visible: true,
                titulo: '📎 Reenvio solicitado',
                mensagem: `O solicitante foi avisado e vai reenviar: ${solicitacaoReenvio.campos.map(c => c.nome).join(', ')}.`,
                camposLimpos: []
              });
              setTimeout(() => setToastInfo(null), 8000);
            } else {
              alert(`⚠️ O card foi movido, mas não deu para avisar o solicitante: ${dataReenvio.error}`);
            }
          } catch (errReenvio) {
            console.error('❌ Erro ao solicitar reenvio de anexo:', errReenvio);
            alert('⚠️ O card foi movido, mas não deu para avisar o solicitante. Tente pelo chat do portal.');
          }
        }
        
        // 💾 Atualizar cache IndexedDB com cards atuais
        onUpdateCache?.(newCards);
        
        // 🔗 AUTO-TRIGGER: Verificar se a etapa de destino tem configs de Cards Conectados
        // Se existir config "mover_existente", disparar automaticamente a movimentação do card conectado
        // Se existir config "criar" (padrão), abrir o modal para criação do card conectado
        console.log('🔗 [AUTO-TRIGGER DEBUG] Configs carregados:', cardsConectadosConfigs.length, 
          cardsConectadosConfigs.map(c => ({ nome: c.nome, tipo: c.tipo, ativo: c.ativo, etapaOrigemId: c.etapaOrigemId })),
          'Etapa destino:', destinationEtapa.id, destinationEtapa.nome
        );
        
        const configsMoverExistente = cardsConectadosConfigs.filter(
          c => c.ativo && 
               c.tipo === 'mover_existente' && 
               String(c.etapaOrigemId) === String(destinationEtapa.id)
        );
        
        // Também verificar configs "criar" para auto-abrir modal
        const configsCriarCard = cardsConectadosConfigs.filter(
          c => c.ativo && 
               (!c.tipo || c.tipo === 'criar') && 
               String(c.etapaOrigemId) === String(destinationEtapa.id)
        );
        
        if (configsMoverExistente.length > 0) {
          console.log(`🔗 [AUTO-TRIGGER] ${configsMoverExistente.length} config(s) "mover_existente" encontrada(s) para etapa "${destinationEtapa.nome}"`);
          
          for (const configMover of configsMoverExistente) {
            try {
              console.log(`🔗 [AUTO-TRIGGER] Executando config "${configMover.nome}" para card "${updatedCard.titulo}"...`);
              
              const email = userPermissions?.currentUser?.email || '';
              const empresaId = flow.empresaId || '';
              const params = new URLSearchParams({ userEmail: email, empresaId });
              
              const resMover = await fetch(`${apiUrl('/cards-conectados/criar')}?${params.toString()}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-User-Data': JSON.stringify({
                    id: userPermissions?.currentUser?.id || 'admin_user',
                    empresaId: empresaId,
                    role: 'admin'
                  })
                },
                body: JSON.stringify({
                  configuracaoId: configMover.id,
                  cardOrigemId: updatedCard.id,
                  flowOrigemId: flow.id
                })
              });
              
              const dataMover = await resMover.json();
              if (dataMover.success) {
                console.log(`✅ [AUTO-TRIGGER] Card conectado movido com sucesso: ${dataMover.message}`);
                // Toast de sucesso
                setToastInfo({
                  visible: true,
                  titulo: '🔗 Card Conectado Movido',
                  mensagem: dataMover.message || `Card conectado movido automaticamente pela config "${configMover.nome}"`,
                  camposLimpos: []
                });
                setTimeout(() => setToastInfo(null), 6000);
              } else if (dataMover.condicoesNaoAtendidas) {
                console.log(`⚠️ [AUTO-TRIGGER] Condições não atendidas para config "${configMover.nome}" - card não movido`);
              } else {
                console.warn(`⚠️ [AUTO-TRIGGER] Erro ao mover card conectado: ${dataMover.error}`);
              }
            } catch (errMover) {
              console.error(`❌ [AUTO-TRIGGER] Erro ao executar config "${configMover.nome}":`, errMover);
            }
          }
        } else {
          console.log(`🔗 [AUTO-TRIGGER] Nenhuma config "mover_existente" para etapa "${destinationEtapa.nome}" (id=${destinationEtapa.id}). Total configs: ${cardsConectadosConfigs.length}, configs neste flow: ${cardsConectadosConfigs.map(c => `${c.nome}[tipo=${c.tipo},etapa=${c.etapaOrigemId},ativo=${c.ativo}]`).join(', ')}`);
        }
      } catch (error) {
        console.error('❌ Erro ao salvar movimentação:', error);
        alert(`⚠️ Card movido localmente, mas erro ao salvar no servidor. Recarregue a página.\n\n${error instanceof Error ? error.message : ''}`);
      }

      // 💬 Notificar no chat
      if (flow.empresaId && flow.id && sourceEtapa && destinationEtapa) {
        notifyCardMoved(
          flow.id,
          flow.name,
          updatedCard.titulo,
          sourceEtapa.nome,
          destinationEtapa.nome,
          userPermissions?.currentUser?.nome || 'Usuário',
          flow.empresaId
        ).catch(err => console.error('Erro ao notificar movimentação de card:', err));
      }
    } catch (error) {
      console.error('❌ Erro ao mover card:', error);
      setCardMovendo(null);
      alert('❌ Erro ao mover o card. Tente novamente.');
    }
  };

  // 🔐 Verificar se usuário pode transformar em pai nesta etapa
  const podeTransformarEmPai = (etapaId: string): boolean => {
    // Admin sempre pode
    if (userPermissions?.canAdmin) return true;
    
    // Buscar email do usuário atual
    const emailUsuario = userPermissions?.currentUser?.email;
    if (!emailUsuario) return false;
    
    // Buscar colaborador pelo email
    const colaborador = colaboradores?.find(c => c.email === emailUsuario);
    if (!colaborador) return false;
    
    // Buscar permissões deste colaborador
    const permissoesUsuario = permissoesGranulares.find(p => p.colaboradorId === colaborador.id);
    if (!permissoesUsuario) return false;
    
    // Verificar permissão de transformar em pai
    const permTransformar = permissoesUsuario.permissoesGerais?.transformarEmPai;
    if (!permTransformar || !permTransformar.permitido) return false;
    
    // Verificar se a etapa específica está permitida
    const etapasPermitidas = permTransformar.etapas || [];
    return etapasPermitidas.includes(etapaId);
  };

  // Transformar card normal em card pai
  const handleTransformarEmCardPai = (card: Card, etapa: Etapa) => {
    // Validar se pode virar pai
    const validacao = podeVirarCardPai(card, etapa, flow);
    if (!validacao.permitido) {
      alert(validacao.motivo || 'Este card não pode se tornar um card pai');
      return;
    }

    // Transformar o card
    const cardTransformado = transformarEmCardPai(card);
    // Registrar evento na linha do tempo (hierarchyHistory)
    const cardAtualizado: Card = {
      ...cardTransformado,
      hierarchyHistory: [
        ...((card.hierarchyHistory || []) as any[]),
        {
          event: 'tornou_pai',
          at: new Date().toISOString(),
          by: userPermissions?.currentUser?.id || 'admin_user',
        },
      ],
    };
    
    // ⚡ Atualizar estado local (optimistic update)
    setCards(prevCards => prevCards.map(c => 
      String(c.id) === String(card.id) ? cardAtualizado : c
    ));
    
    // ⚡ Salvar APENAS o card transformado (operação atômica)
    fetch(`/api/flows/${flow.id}/cards/${card.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Data': JSON.stringify({
          id: userPermissions?.currentUser?.id || 'admin_user',
          empresaId: flow.empresaId || 'empresa_principal',
          role: 'admin'
        })
      },
      body: JSON.stringify(cardAtualizado)
    })
    .then(res => res.json())
    .then(data => console.log('✅ Resposta do servidor (transformar pai):', data))
    .catch(err => console.error('❌ Erro ao salvar card pai:', err));
    
    console.log('✅ Card transformado em Card Pai atomicamente:', cardAtualizado);
  };

  // Atualizar configuração de hierarquia de uma etapa
  const handleUpdateHierarquiaConfig = (etapaId: string, config: any) => {
    const newEtapas = etapas.map(e => 
      String(e.id) === String(etapaId) 
        ? { ...e, configuracaoHierarquia: config }
        : e
    );
    
    setEtapas(newEtapas);
    onUpdateFlow({ ...flow, etapas: newEtapas });
    
    console.log('✅ Configuração de hierarquia atualizada:', { etapaId, config });
  };

  // Handler para salvar configuração de hierarquia do modal
  const handleSaveHierarquiaConfig = (config: ConfiguracaoHierarquiaEtapa) => {
    if (configuringHierarquia) {
      handleUpdateHierarquiaConfig(configuringHierarquia.id, config);
    }
  };

  // Adicionar um card filho a um card pai
  const handleAdicionarFilho = (cardPai: Card, cardFilho: Card) => {
    console.log('🚨🚨🚨 [handleAdicionarFilho] FUNÇÃO CHAMADA!!!');
    console.log('🔍 [handleAdicionarFilho] Iniciando...');
    console.log('🔍 [handleAdicionarFilho] Card Pai:', cardPai?.titulo, 'ID:', cardPai?.id, 'etapa:', cardPai?.etapaId);
    console.log('🔍 [handleAdicionarFilho] Card Filho:', cardFilho?.titulo, 'ID:', cardFilho?.id, 'etapa:', cardFilho?.etapaId);
    console.log('🔍 [handleAdicionarFilho] Automações totais:', automacoes?.length || 0);
    
    // Encontrar etapas
    const etapaPai = etapas.find(e => String(e.id) === String(cardPai.etapaId));
    const etapaFilho = etapas.find(e => String(e.id) === String(cardFilho.etapaId));
    
    console.log('🔍 [handleAdicionarFilho] Etapa Pai encontrada:', etapaPai?.nome);
    console.log('🔍 [handleAdicionarFilho] Etapa Filho encontrada:', etapaFilho?.nome);
    console.log('🔍 [handleAdicionarFilho] Automações disponíveis:', automacoes?.length);
    console.log('🔍 [handleAdicionarFilho] Automações:', automacoes);
    
    if (!etapaPai || !etapaFilho) {
      alert('❌ Erro: etapas não encontradas');
      return;
    }

    // Validar se pode adicionar como filho
    const validacao = podeVirarCardFilho(cardFilho, cardPai, etapaFilho, etapaPai, flow, automacoes);
    console.log('🔍 [handleAdicionarFilho] Resultado validação:', validacao);
    
    if (!validacao.permitido) {
      // Se precisa configurar automações, oferecer autorização específica do card
      if (validacao.precisaConfigurar && (validacao as any).cardId) {
        const confirmar = confirm(
          `⚠️ CARD COM AUTOMAÇÕES ATIVAS\n\n${validacao.motivo}\n\n` +
          `OPÇÕES:\n` +
          `• Clique OK para AUTORIZAR e AGRUPAR este card\n` +
          `• Clique Cancelar para voltar\n\n` +
          `⚠️ Autorizar este card NÃO afetará outros cards da mesma etapa.`
        );
        
        if (!confirmar) {
          return; // Usuário cancelou
        }
        
        // Adicionar este card à lista de exceções na configuração de hierarquia
        const configAtual = etapaPai.configuracaoHierarquia?.regraAgrupamento || {};
        const cardsExcecao = (configAtual as any).cardsExcecaoAutomacao || [];
        
        // Adicionar card à lista de exceções se ainda não estiver
        if (!cardsExcecao.includes((validacao as any).cardId)) {
          cardsExcecao.push((validacao as any).cardId);
        }
        
        // Atualizar etapa com nova configuração
        const etapaAtualizada = {
          ...etapaPai,
          configuracaoHierarquia: {
            ...etapaPai.configuracaoHierarquia,
            regraAgrupamento: {
              ...configAtual,
              cardsExcecaoAutomacao: cardsExcecao
            }
          }
        };
        
        // Atualizar flow com etapa modificada
        const flowAtualizado = {
          ...flow,
          etapas: flow.etapas.map(e => 
            String(e.id) === String(etapaPai.id) ? etapaAtualizada : e
          )
        };
        
        // Salvar configuração no backend (não precisa esperar)
        onUpdateFlow(flowAtualizado as Flow);
        
        // CONTINUAR para adicionar como filho (não dar return!)
        console.log('✅ Card autorizado como exceção - prosseguindo com agrupamento');
      } else {
        alert(validacao.motivo || 'Este card não pode se tornar filho deste card pai');
        return;
      }
    }

    // Adicionar relacionamento
    const resultado = adicionarCardFilho(cardPai, cardFilho);
    
    // Atualizar cards no estado e popular filhos
    let newCards = cards.map(c => {
      if (String(c.id) === String(cardPai.id)) {
        return resultado.pai;
      }
      if (String(c.id) === String(cardFilho.id)) {
        return resultado.filho;
      }
      return c;
    });

    // Repopular filhos de todos os pais após mudança
    newCards = newCards.map(card => {
      if (card.ehCardPai) {
        return popularCardsFilhos(card, newCards);
      }
      return card;
    });

    // ⚡ Atualizar estado local
    setCards(newCards);
    
    // ⚡ Salvar APENAS os 2 cards afetados (pai e filho) atomicamente
    Promise.all([
      fetch(`/api/flows/${flow.id}/cards/${resultado.pai.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Data': JSON.stringify({
            id: userPermissions?.currentUser?.id || 'admin_user',
            empresaId: flow.empresaId || 'empresa_principal',
            role: 'admin'
          })
        },
        body: JSON.stringify(resultado.pai)
      }).then(r => r.json()).then(d => console.log('✅ Pai salvo:', d)),
      fetch(`/api/flows/${flow.id}/cards/${resultado.filho.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Data': JSON.stringify({
            id: userPermissions?.currentUser?.id || 'admin_user',
            empresaId: flow.empresaId || 'empresa_principal',
            role: 'admin'
          })
        },
        body: JSON.stringify(resultado.filho)
      }).then(r => r.json()).then(d => console.log('✅ Filho salvo:', d))
    ]).catch(err => console.error('❌ Erro ao salvar hierarquia:', err));
    
    console.log('✅ Card filho adicionado ao pai atomicamente:', { pai: cardPai.titulo, filho: cardFilho.titulo });
    setAdicionandoFilhoAoPai(null);
  };

  // Remover um card filho de um card pai (desvincular)
  const handleRemoverFilho = (cardPaiId: string, cardFilhoId: string) => {
    // Encontrar o card pai
    const cardPai = cards.find(c => String(c.id) === String(cardPaiId));
    if (!cardPai) {
      console.error('❌ Card pai não encontrado:', cardPaiId);
      return;
    }

    // Remover relacionamento
    const resultado = removerCardFilho(cardPai, cardFilhoId);
    
    // Atualizar cards no estado
    let newCards = cards.map(c => {
      if (String(c.id) === String(cardPaiId)) {
        return resultado.pai;
      }
      if (String(c.id) === String(cardFilhoId)) {
        // Remover cardPaiId do filho - IMPORTANTE: usar null (não undefined).
        // O JSON.stringify do PUT descarta chaves com valor undefined, então o
        // servidor nunca receberia o campo e não faria o $unset no Mongo, deixando
        // o filho "órfão" (some ao recarregar). Com null, o backend executa o $unset.
        const { cardPaiId: _, ...cardSemPai } = c;
        return { ...cardSemPai, cardPaiId: null as any };
      }
      return c;
    });

    // Repopular filhos
    newCards = newCards.map(card => {
      if (card.ehCardPai) {
        return popularCardsFilhos(card, newCards);
      }
      return card;
    });

    // ⚡ Atualizar estado local
    setCards(newCards);
    
    // ⚡ Salvar APENAS os 2 cards afetados atomicamente
    const cardPaiAtualizado = newCards.find(c => String(c.id) === String(cardPaiId));
    const cardFilhoAtualizado = newCards.find(c => String(c.id) === String(cardFilhoId));
    
    if (cardPaiAtualizado && cardFilhoAtualizado) {
      Promise.all([
        fetch(`/api/flows/${flow.id}/cards/${cardPaiId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Data': JSON.stringify({
              id: userPermissions?.currentUser?.id || 'admin_user',
              empresaId: flow.empresaId || 'empresa_principal',
              role: 'admin'
            })
          },
          body: JSON.stringify(cardPaiAtualizado)
        }),
        fetch(`/api/flows/${flow.id}/cards/${cardFilhoId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Data': JSON.stringify({
              id: userPermissions?.currentUser?.id || 'admin_user',
              empresaId: flow.empresaId || 'empresa_principal',
              role: 'admin'
            })
          },
          body: JSON.stringify(cardFilhoAtualizado)
        })
      ]).catch(err => console.error('❌ Erro ao salvar desvinculação:', err));
    }
    
    console.log('✅ Card filho removido do pai atomicamente');
  };

  // Destravar um card pai (transformar de volta em card normal)
  const handleDestravarCardPai = (cardId: string) => {
    const card = cards.find(c => String(c.id) === String(cardId));
    if (!card) {
      console.error('❌ Card não encontrado:', cardId);
      return;
    }

    // Verificar se pode destravar
    const validacao = podeDestravarCardPai(card);
    if (!validacao.permitido) {
      alert(validacao.motivo);
      console.warn('⚠️ Não pode destravar card pai:', validacao.motivo);
      return;
    }

    // Confirmar com o usuário
    if (!confirm(`Destravar "${card.titulo}" como card pai?\n\nO card voltará a ser um card normal.`)) {
      return;
    }

    // Destravar o card
    const cardDestravado = destravarCardPai(card);
    
    // ⚡ Atualizar estado local (optimistic update)
    setCards(prevCards => prevCards.map(c => 
      String(c.id) === String(cardId) ? cardDestravado : c
    ));
    
    // ⚡ Salvar APENAS o card destravado atomicamente
    fetch(`/api/flows/${flow.id}/cards/${cardId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Data': JSON.stringify({
          id: userPermissions?.currentUser?.id || 'admin_user',
          empresaId: flow.empresaId || 'empresa_principal',
          role: 'admin'
        })
      },
      body: JSON.stringify(cardDestravado)
    }).catch(err => console.error('❌ Erro ao salvar card destravado:', err));
    
    console.log('✅ Card pai destravado atomicamente:', card.titulo);
  };

  // Atualizar valor total de um card pai
  const handleUpdateValorTotal = (cardId: string, valorTotal: number) => {
    // Buscar card atual para registrar valor antigo no histórico
    const cardAtual = cards.find(c => String(c.id) === String(cardId));
    const valorAntigo = cardAtual?.valorTotal;

    // Registrar alteração no fieldHistory
    const fieldHistory = { ...(cardAtual?.fieldHistory || {}) };
    if (!fieldHistory['valorTotal']) {
      fieldHistory['valorTotal'] = [];
    }
    fieldHistory['valorTotal'] = [
      ...fieldHistory['valorTotal'],
      {
        value: valorTotal,
        oldValue: valorAntigo,
        editedBy: userPermissions?.currentUser?.id || 'admin_user',
        editedAt: new Date()
      }
    ];

    // Atualizar estado local (optimistic update)
    setCards(prevCards => prevCards.map(c =>
      String(c.id) === String(cardId) ? { ...c, valorTotal, fieldHistory } : c
    ));

    // Salvar no backend (mesma rota PUT usada por todo o sistema)
    fetch(`/api/flows/${flow.id}/cards/${cardId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Data': JSON.stringify({
          id: userPermissions?.currentUser?.id || 'admin_user',
          empresaId: flow.empresaId || 'empresa_principal',
          role: 'admin'
        })
      },
      body: JSON.stringify({ valorTotal, fieldHistory })
    }).catch(err => console.error('❌ Erro ao salvar valor total:', err));

    console.log('✅ Valor total atualizado:', { cardId, valorTotal, valorAntigo });
  };

  const addEtapa = () => {
    const newEtapa: Etapa = {
      id: String(Date.now()),
      nome: 'Nova Etapa',
  campos: [],
  camposVisiveisNoCard: [],
      departamentoId: '',
      participantes: [],
      empresaId: flow.empresaId || ''
    };
    const newEtapas = [...etapas, newEtapa];
    setEtapas(newEtapas);
    setConfiguring(newEtapa);
    onUpdateFlow({ ...flow, etapas: newEtapas });
  };

  // Insere uma nova etapa em uma posição específica (antes do índice informado)
  const addEtapaAt = (index: number) => {
    const newEtapa: Etapa = {
      id: String(Date.now()),
      nome: 'Nova Etapa',
      campos: [],
      camposVisiveisNoCard: [],
      departamentoId: '',
      participantes: [],
      empresaId: flow.empresaId || ''
    };
    const newEtapas = [...etapas];
    newEtapas.splice(index, 0, newEtapa);
    setEtapas(newEtapas);
    setConfiguring(newEtapa);
    onUpdateFlow({ ...flow, etapas: newEtapas });
  };

  const handleUpdateEtapa = (etapaAtualizada: Etapa) => {
    const newEtapas = etapas.map(e =>
      String(e.id) === String(etapaAtualizada.id) ? { ...etapaAtualizada, id: String(etapaAtualizada.id) } : e
    );
    setEtapas(newEtapas);
    setConfiguring(null);
    onUpdateFlow({ 
      ...flow, 
      etapas: newEtapas,
      updatedAt: new Date()
    });
  };

  const handleDeleteEtapa = async (etapaId: string) => {
    const hasCards = cards.some(c => String(c.etapaId) === String(etapaId));
    if (hasCards) {
      alert('Não é possível excluir uma etapa que contém cartões. Mova os cartões para outra etapa primeiro.');
      return;
    }

    const nomeEtapa = etapas.find(e => String(e.id) === String(etapaId))?.nome || 'esta etapa';
    if (!window.confirm(
      `Excluir a etapa "${nomeEtapa}"?\n\n`
      + 'Isso não pode ser desfeito. Automações, portais e regras que apontam para esta etapa podem parar de funcionar.'
    )) return;

    // flow.etapas, não o state: o state esconde as etapas que a pessoa não pode
    // acessar, e mandar a lista filtrada apagava essas junto.
    const newEtapas = (flow.etapas || []).filter(e => String(e.id) !== String(etapaId));
    setConfiguring(null);
    // Sem tirar da tela antes: se o servidor recusar (a etapa ainda tem cards que
    // esta pessoa não enxerga), ela continua. Salvo, o fluxo novo atualiza o quadro.
    await onUpdateFlow({
      ...flow,
      etapas: newEtapas,
      updatedAt: new Date()
    });
  };

  // Função para reordenar etapas (usado em todos os views)
  const handleReordenarEtapas = (etapaId: string, novaPosicao: number) => {
    const etapaIndex = etapas.findIndex(e => String(e.id) === String(etapaId));
    if (etapaIndex === -1) return;

    const newEtapas = [...etapas];
    const [etapaMovida] = newEtapas.splice(etapaIndex, 1);
    newEtapas.splice(novaPosicao, 0, etapaMovida);

    // Atualizar ordemFluxo de todas as etapas baseado na nova ordem
    const etapasComOrdem = newEtapas.map((e, idx) => ({
      ...e,
      ordemFluxo: idx
    }));

    setEtapas(etapasComOrdem);
    onUpdateFlow({ 
      ...flow, 
      etapas: etapasComOrdem,
      updatedAt: new Date()
    });
  };

  const handleAddCard = async (cardData: Omit<Card, 'id' | 'createdAt' | 'updatedAt'>) => {
    console.log('🃏 handleAddCard chamado com:', cardData);
    
    // ⏳ NÃO FECHAR O MODAL - vai fechar só após salvar!
    
    // Tentar chamar API Flask para criar card
    let cardIdFromApi = null;
    try {
      console.log('📤 Tentando chamar API Flask para criar card...');
      const nodeApiUrl = '/api/cards/criar'; // Usa proxy do Vite → Flask (5000)
      
      const response = await fetch(nodeApiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Data': JSON.stringify({
              id: userPermissions?.currentUser?.id || 'admin_user',
              empresaId: flow.empresaId || 'empresa_principal',
              role: 'admin'
            })
          },
          body: JSON.stringify({
            fluxoId: flow.id,
            etapaId: String(cardData.etapaId),
            titulo: cardData.titulo,
            descricao: cardData.campos?.descricao || '',
            campos: cardData.campos,
            origem: 'manual',
            criadoPor: userPermissions?.currentUser?.id, // Adicionar quem criou
          }),
        });

        if (response.ok) {
          const result = await response.json();
          cardIdFromApi = result.card.id;
          console.log('✅ Card criado pela API Node.js (emails enviados):', cardIdFromApi);
        }
    } catch (error) {
      console.warn('⚠️ API Node.js indisponível (normal em dev local):', (error as Error).message);
      // Não bloqueia - continua o fluxo normal
    }

    // Criar card com o ID da API ou gerar um novo
    const criadorId = String(resolverCriadorId(cardData) || userPermissions?.currentUser?.id || '');
    
    // 🎯 APLICAR REGRAS DE RESPONSÁVEL AUTOMÁTICO NA CRIAÇÃO
    let responsavelAutomatico: string | undefined = undefined;
    const etapaDestino = etapas.find(e => String(e.id) === String(cardData.etapaId));
    
    if (etapaDestino) {
      // Verificar se há regras condicionais configuradas
      if (etapaDestino.regrasResponsavel && etapaDestino.regrasResponsavel.condicoes.length > 0) {
        const regras = etapaDestino.regrasResponsavel;
        
        if (regras.tipo === 'criador') {
          // Baseado em quem está criando o card
          const condicaoDireta = regras.condicoes.find(c => matchCriadorCondicao(criadorId, c.quando));
          const condicaoGenerica = regras.condicoes.find(c => c.quando === 'card_creator');
          const condicao = condicaoDireta || condicaoGenerica;
          if (condicao?.atribuirPara) {
            responsavelAutomatico = condicao.atribuirPara;
            console.log('🎯 Regra aplicada na criação (criador):', { criadorId, responsavelAutomatico });
          }
        }
      }
      
      // Se não há regra condicional, verificar responsável padrão da etapa
      if (!responsavelAutomatico && etapaDestino.responsavelPadraoId) {
        responsavelAutomatico = etapaDestino.responsavelPadraoId;
        console.log('👤 Responsável padrão aplicado na criação:', responsavelAutomatico);
      }
    }
    
    const newCard: Card = {
      id: cardIdFromApi || String(Date.now()),
      ...cardData,
      etapaId: String(cardData.etapaId),
      createdAt: new Date(),
      updatedAt: new Date(),
      criadoPor: criadorId, // 👤 Salvar quem criou o card
      responsavelIndividualId: responsavelAutomatico || cardData.responsavelIndividualId, // Aplicar responsável automático
      etapaHistory: [
        {
          etapaId: String(cardData.etapaId),
          movedAt: new Date(),
          movedBy: userPermissions?.currentUser?.id,
          motivo: 'criado'
          // camposSnapshot removido - causava documentos > 16MB
        }
      ]
    };
    
    // ⚡ OTIMIZAÇÃO: Atualizar UI primeiro (otimistic update)
    const newCards = [...cards, newCard];
    setCards(newCards);
    
    console.log('🃏 Total de cards após adicionar:', newCards.length);
    
    // ⚡ Salvar APENAS o card novo (operação atômica)
    try {
      const cardToSave = {
        ...newCard,
        flowId: flow.id,
        empresaId: flow.empresaId
      };
      
      // Usar endpoint atômico: POST /api/flows/{flowId}/cards
      const response = await fetch(`/api/flows/${flow.id}/cards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Data': JSON.stringify({
            id: userPermissions?.currentUser?.id || 'admin_user',
            empresaId: flow.empresaId || 'empresa_principal',
            role: 'admin'
          })
        },
        body: JSON.stringify(cardToSave)
      });
      
      if (!response.ok) {
        throw new Error(`Erro ao salvar card: ${response.status}`);
      }
      
      const cardSalvoResponse = await response.json();
      console.log('✅ Card salvo atomicamente no backend. Resposta:', cardSalvoResponse);
      
      // 💾 Atualizar cache IndexedDB com cards atuais
      onUpdateCache?.(newCards);
      
      console.log('🤖 Executando automações após criação do card...');
      const etapaCard = etapas.find(e => e.id === newCard.etapaId);
      if (etapaCard && automacoes && automacoes.length > 0) {
        // Executar automações com os valores dos campos do card criado
        automationEngine.executeAutomations(
          automacoes,
          etapaCard,
          newCard.campos || {},
          'initial', // Usar 'initial' para avaliar todas as regras
          () => {}, // onFieldVisibilityChange - não aplicável aqui
          async (etapaDestinoId: string, meta?: { automacaoId?: string; automacaoNome?: string; campoGatilho?: string }) => {
            // Callback para mover card quando automação exigir
            console.log(`🚚 Automação solicitou mover card ${newCard.id} para etapa ${etapaDestinoId}`);
            console.log('🔧 Campo gatilho a ser limpo:', meta?.campoGatilho);
            try {
              const etapaDestino = etapas.find(e => String(e.id) === String(etapaDestinoId));
              
              // 🎯 APLICAR REGRAS DE RESPONSÁVEL AUTOMÁTICO PARA MOVIMENTAÇÃO POR AUTOMAÇÃO (criação)
              let novoResponsavelId: string | undefined = undefined;

              console.log('🔎 DEBUG etapaDestino:', etapaDestino?.nome, 'regrasResponsavel:', JSON.stringify(etapaDestino?.regrasResponsavel));

              if (etapaDestino?.regrasResponsavel && etapaDestino.regrasResponsavel.condicoes.length > 0) {
                const regras = etapaDestino.regrasResponsavel;
                console.log('📋 Regras encontradas:', regras.tipo, 'condições:', regras.condicoes.length);

                if (regras.tipo === 'criador') {
                  // O newCard ainda não tem criadoPor porque foi criado agora - usar o currentUser
                  const criadorId = String(newCard.criadoPor || userPermissions?.currentUser?.id || '');
                  console.log('🔍 Automação (criação) - criadorId:', criadorId);
                  console.log('🔍 Condições disponíveis:', regras.condicoes.map(c => `quando=${c.quando} -> atribuirPara=${c.atribuirPara}`));
                  
                  const condicaoDireta = regras.condicoes.find(c => matchCriadorCondicao(criadorId, c.quando));
                  const condicaoGenerica = regras.condicoes.find(c => c.quando === 'card_creator');
                  console.log('🔍 condicaoDireta:', condicaoDireta, 'condicaoGenerica:', condicaoGenerica);
                  
                  const condicao = condicaoDireta || condicaoGenerica;
                  if (condicao?.atribuirPara) {
                    novoResponsavelId = condicao.atribuirPara;
                    console.log('🎯 Regra aplicada (automação criação - criador):', { criadorId, novoResponsavelId });
                  } else {
                    console.log('⚠️ Nenhuma condição encontrada para criadorId:', criadorId);
                  }
                } else if (regras.tipo === 'responsavel_anterior') {
                  const responsavelAnterior = String(newCard.responsavelIndividualId || '');
                  const condicao = regras.condicoes.find(c => String(c.quando) === responsavelAnterior);
                  if (condicao?.atribuirPara) {
                    novoResponsavelId = condicao.atribuirPara;
                    console.log('🎯 Regra aplicada (automação criação - resp. anterior):', { responsavelAnterior, novoResponsavelId });
                  }
                }
              } else {
                console.log('⚠️ Nenhuma regra de responsável configurada na etapa destino');
              }

              if (!novoResponsavelId && etapaDestino?.responsavelPadraoId) {
                novoResponsavelId = etapaDestino.responsavelPadraoId;
                console.log('👤 Responsável padrão aplicado (automação criação):', novoResponsavelId);
              }

              console.log('✅ novoResponsavelId final:', novoResponsavelId);

              // 🧹 LIMPAR CAMPOS CONFIGURADOS NA ETAPA DESTINO (ao card retornar)
              let camposAtualizadosCriacao = { ...newCard.campos };
              const etapaDestinoObj = etapas.find(e => String(e.id) === String(etapaDestinoId));
              if (etapaDestinoObj?.limparCamposAoRetornar?.ativo && etapaDestinoObj.limparCamposAoRetornar.campos.length > 0) {
                // Verificar se o card já passou por esta etapa antes (retorno)
                const jaPassou = newCard.etapaHistory?.some((h: any) => String(h.etapaId) === String(etapaDestinoId));
                if (jaPassou) {
                  console.log('🧹 [CRIAÇÃO-AUTOMAÇÃO] Limpando campos configurados ao retornar para', etapaDestinoObj.nome);
                  etapaDestinoObj.limparCamposAoRetornar.campos.forEach(campoId => {
                    const val = camposAtualizadosCriacao[campoId];
                    camposAtualizadosCriacao[campoId] = Array.isArray(val) ? [] : '';
                    console.log('  ✓ Campo', campoId, 'limpo');
                  });
                }
              }

              // 🔄 Mostrar loading global
              setSalvandoGlobal(`Movendo card para "${etapaDestino?.nome}"...`);

              // Atualizar o card com a nova etapa e responsável
              const cardMovido = {
                ...newCard,
                etapaId: etapaDestinoId,
                campos: camposAtualizadosCriacao, // Campos mantidos sem limpeza
                responsavelIndividualId: novoResponsavelId, // 🎯 Aplicar responsável automático
                isExplicitMove: true, // 🛡️ Flag para indicar movimentação intencional (automação)
                updatedAt: new Date(),
                etapaHistory: [
                  ...(newCard.etapaHistory || []),
                  {
                    etapaId: etapaDestinoId,
                    movedAt: new Date(),
                    movedBy: userPermissions?.currentUser?.id,
                    motivo: meta?.automacaoNome ? `Automação: ${meta.automacaoNome}` : 'Automação',
                    // 🔧 FIX: SALVAR camposSnapshot com os campos ATUAIS
                    camposSnapshot: { ...camposAtualizadosCriacao }
                  }
                ]
              };
              
              // ⚡ Salvar APENAS o card movido (operação atômica)
              const responsePut = await fetch(`/api/flows/${flow.id}/cards/${newCard.id}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'X-User-Data': JSON.stringify({
                    id: userPermissions?.currentUser?.id || 'admin_user',
                    empresaId: flow.empresaId || 'empresa_principal',
                    role: 'admin'
                  })
                },
                body: JSON.stringify(cardMovido)
              });
              
              if (!responsePut.ok) {
                console.error('❌ Erro ao mover card:', responsePut.status);
                return;
              }
              
              // ⏱️ Aguardar 100ms para o backend aplicar as regras de responsável
              await new Promise(resolve => setTimeout(resolve, 100));
              
              // Buscar card atualizado do backend (com responsável aplicado pelas regras)
              const responseGet = await fetch(`/api/flows/${flow.id}/cards/${newCard.id}`);
              const cardAtualizado = await responseGet.json();
              
              console.log('✅ Card movido por automação. Responsável:', cardAtualizado.card?.responsavelIndividualId);
              
              // ⚡ Atualizar estado local REMOVENDO da etapa antiga e atualizando com dados do backend
              setCards(prevCards => {
                // Remover todas as instâncias do card (previne duplicação)
                const semCardAntigo = prevCards.filter(c => c.id !== newCard.id);
                // Adicionar card com dados atualizados do backend
                const cardFinal = { ...cardMovido, ...cardAtualizado.card };
                console.log(`🔄 Card ${newCard.id} movido - Etapa: ${cardFinal.etapaId}, Responsável: ${cardFinal.responsavelIndividualId || 'nenhum'}`);
                
                // 💾 Atualizar cache IndexedDB com os cards atualizados
                const cardsAtualizados = [...semCardAntigo, cardFinal];
                onUpdateCache?.(cardsAtualizados);
                
                return cardsAtualizados;
              });
              
              // ✅ Mostrar mensagem de sucesso por 2 segundos
              setSalvandoGlobal(`✅ Card movido para "${etapaDestino?.nome}" com sucesso!`);
              await new Promise(resolve => setTimeout(resolve, 2000));
              setSalvandoGlobal(null);
              
              // Notificar movimentação no chat
              const etapaOrigem = etapas.find(e => e.id === newCard.etapaId);
              if (flow.empresaId && flow.id && etapaOrigem && etapaDestino) {
                notifyCardMoved(
                  flow.id,
                  flow.name,
                  newCard.titulo,
                  etapaOrigem.nome,
                  etapaDestino?.nome || '',
                  userPermissions?.currentUser?.nome || 'Sistema',
                  flow.empresaId
                ).catch(err => console.error('Erro ao notificar movimentação:', err));
              }
            } catch (error) {
              console.error('❌ Erro ao mover card por automação:', error);
              setSalvandoGlobal(null); // Fechar loading em caso de erro
            }
          },
          newCard.id
        );
      }
      
      // ✅ Fechar modal APENAS após salvar com sucesso
      console.log('🚪 Fechando modal após salvamento bem-sucedido...');
      setNovoCartao(null);
    } catch (error) {
      console.error('❌ Erro ao salvar card:', error);
      alert('❌ Erro ao salvar o card. Tente novamente.');
      // ❌ Não fecha o modal se der erro - usuário pode tentar novamente
      return;
    }

    // 💬 Notificar no chat
    if (flow.empresaId && flow.id) {
      notifyCardCreated(
        flow.id,
        flow.name,
        newCard.titulo,
        userPermissions?.currentUser?.nome || 'Usuário',
        flow.empresaId
      ).catch(err => console.error('Erro ao notificar criação de card:', err));
    }
  };

  const handleUpdateCard = async (cardAtualizado: Card) => {
    console.log('📝 handleUpdateCard - Iniciando atualização do card:', cardAtualizado.id);
    // IMPORTANTE: Log para verificar se etapaHistory está vindo com as alterações do histórico
    // Corrigido em 22/01/2026 - Verificar se camposSnapshot atualizado está presente
    console.log('📝 handleUpdateCard - etapaHistory recebido:', JSON.stringify(cardAtualizado.etapaHistory, null, 2));

    // 🔧 Auto-save de anexo (upload) não deve disparar automações de mover_card —
    // o move só deve ocorrer no "Salvar Alterações" explícito. Removemos o flag aqui
    // para não vazar pro estado local nem para o Mongo.
    const skipAutomacoes = (cardAtualizado as any)._skipAutomacoes === true;
    if (skipAutomacoes) { try { delete (cardAtualizado as any)._skipAutomacoes; } catch { /* noop */ } }

    const newCards = cards.map(c =>
      String(c.id) === String(cardAtualizado.id) 
        ? { ...cardAtualizado, id: String(cardAtualizado.id), etapaId: String(cardAtualizado.etapaId), updatedAt: new Date() } 
        : c
    );
    
    console.log('💾 Atualizando estado local com novos cards');
    setCards(newCards);
    
    const updatedFlow = { 
      ...flow, 
      cards: newCards,
      updatedAt: new Date()
    };
    
    console.log('⚡ Salvando APENAS o card atualizado (operação atômica)...');
    console.log('⚡ Parâmetros:', { flowId: flow.id, cardId: cardAtualizado.id });
    
    // ✅ Cards em coleção separada - não precisa mais limitar histórico
    // Cada card pode ter até 16MB individualmente
    const cardParaEnviar = { ...cardAtualizado };
    
    // Remover campos temporários que não devem ser salvos
    delete (cardParaEnviar as any).camposSnapshot;
    delete (cardParaEnviar as any).tempData;
    delete (cardParaEnviar as any)._skipClose;
    
    // Salvar ANTES de executar automações (igual criação de card)
    console.log('💾 Salvando card no backend primeiro...');

    // 📊 DIAGNÓSTICO: Análise de tamanho do card
    console.log('📊 ===== DIAGNÓSTICO DE TAMANHO DO CARD =====');
    console.log('📊 Card ID:', cardParaEnviar.id);
    console.log('📊 Título:', cardParaEnviar.titulo);
    
    // Tamanho do etapaHistory
    if (cardParaEnviar.etapaHistory) {
      const etapaHistorySize = JSON.stringify(cardParaEnviar.etapaHistory).length;
      console.log(`📊 etapaHistory: ${cardParaEnviar.etapaHistory.length} entradas, ${(etapaHistorySize / 1024).toFixed(2)} KB`);
    }
    
    // Tamanho do fieldHistory
    if (cardParaEnviar.fieldHistory) {
      const fieldHistoryKeys = Object.keys(cardParaEnviar.fieldHistory);
      const fieldHistorySize = JSON.stringify(cardParaEnviar.fieldHistory).length;
      console.log(`📊 fieldHistory: ${fieldHistoryKeys.length} campos, ${(fieldHistorySize / 1024).toFixed(2)} KB`);
      
      // Detalhe por campo
      fieldHistoryKeys.forEach(key => {
        const entries = cardParaEnviar.fieldHistory![key];
        const size = JSON.stringify(entries).length;
        if (size > 10000) { // Mostra apenas campos > 10KB
          console.log(`   📊 Campo ${key}: ${entries.length} entradas, ${(size / 1024).toFixed(2)} KB`);
        }
      });
    }
    
    // Tamanho dos campos
    if (cardParaEnviar.campos) {
      const camposKeys = Object.keys(cardParaEnviar.campos);
      const camposSize = JSON.stringify(cardParaEnviar.campos).length;
      console.log(`📊 campos: ${camposKeys.length} campos, ${(camposSize / 1024).toFixed(2)} KB`);
      
      // Detalhe por campo
      camposKeys.forEach(key => {
        const value = cardParaEnviar.campos[key];
        const size = JSON.stringify(value).length;
        if (size > 10000) { // Mostra apenas campos > 10KB
          console.log(`   📊 Campo ${key}: ${(size / 1024).toFixed(2)} KB`);
        }
      });
    }
    
    // Tamanho total do card
    const totalSize = JSON.stringify(cardParaEnviar).length;
    console.log(`📊 TAMANHO TOTAL: ${(totalSize / 1024).toFixed(2)} KB (${(totalSize / 1024 / 1024).toFixed(2)} MB)`);
    console.log(`📊 Limite MongoDB: 16 MB`);
    console.log('📊 ========================================');
    
    // 🧹 LIMPEZA CRÍTICA: Remover base64 de anexos no fieldHistory
    // Problema: fieldHistory estava salvando PDFs inteiros em base64 (1.5MB+)
    // Solução: Manter apenas metadata nos anexos do histórico
    if (cardParaEnviar.fieldHistory) {
      console.log('🧹 Limpando anexos do fieldHistory...');
      Object.keys(cardParaEnviar.fieldHistory).forEach(fieldId => {
        const history = cardParaEnviar.fieldHistory![fieldId];
        if (Array.isArray(history)) {
          cardParaEnviar.fieldHistory![fieldId] = history.map((entry: any) => {
            if (entry && typeof entry === 'object' && entry.value) {
              // Se for array (anexos múltiplos)
              if (Array.isArray(entry.value)) {
                const cleanedFiles = entry.value.map((file: any) => {
                  if (file && file.dataUrl) {
                    // Remove o base64 pesado, mantém apenas metadata
                    return {
                      name: file.name,
                      type: file.type,
                      size: file.size,
                      // dataUrl removido para economizar espaço!
                    };
                  }
                  return file;
                });
                return { ...entry, value: cleanedFiles };
              }
              // Se for objeto único (anexo único)
              else if (entry.value.dataUrl) {
                return {
                  ...entry,
                  value: {
                    name: entry.value.name,
                    type: entry.value.type,
                    size: entry.value.size,
                    // dataUrl removido para economizar espaço!
                  }
                };
              }
            }
            return entry;
          });
        }
      });
      
      const cleanedSize = JSON.stringify(cardParaEnviar).length;
      console.log(`🧹 Após limpeza: ${(cleanedSize / 1024).toFixed(2)} KB (reduziu ${((totalSize - cleanedSize) / 1024).toFixed(2)} KB)`);
    }
    
    try {
      // Usar o novo endpoint atômico ao invés de enviar o fluxo inteiro
      const { mongoService } = await import('../../utils/mongoService');
      const resultado = await mongoService.updateCard(flow.id, cardAtualizado.id, cardParaEnviar);
      console.log('✅ Card salvo no banco usando operação atômica!', resultado);
      
      // 💾 Atualizar cache IndexedDB com cards atuais
      onUpdateCache?.(newCards);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('❌ ERRO AO SALVAR CARD ATOMICAMENTE:', errorMessage);
      // Fallback para o método antigo
      console.log('⚠️ Tentando método alternativo (fallback)...');
      try {
        await onUpdateFlow(updatedFlow);
        console.log('✅ Fallback executado com sucesso');
      } catch (fallbackError) {
        const fallbackMsg = fallbackError instanceof Error ? fallbackError.message : 'Erro desconhecido';
        console.error('❌ Erro no fallback:', fallbackMsg);
        alert(`⚠️ Erro ao salvar card: ${errorMessage}. Fallback também falhou: ${fallbackMsg}`);
        setCardSelecionado(null);
        return;
      }
    }
    
    const etapaCard = etapas.find(e => String(e.id) === String(cardAtualizado.etapaId));
    
    // 🔧 FIX: Verificar se houve mudança real nos CAMPOS (não apenas responsável/título)
    // Se só mudou responsável ou título, NÃO executar automações para evitar movimentos indesejados
    const cardAnterior = cards.find(c => String(c.id) === String(cardAtualizado.id));
    const camposAnteriores = JSON.stringify(cardAnterior?.campos || {});
    const camposNovos = JSON.stringify(cardAtualizado.campos || {});
    const houveMudancaNosCAMPOS = camposAnteriores !== camposNovos;
    
    // 🔧 FIX: O modal de edição pode ter sinalizado um movimento pendente
    // (quando o motor avaliou e marcou destino, mas os campos em JSON ficaram iguais
    // — ex.: campo já estava no valor que dispara a regra antes de abrir o modal)
    const pendingAutomacaoMove = (cardAtualizado as any)._pendingAutomacaoMove as
      | { etapaDestinoId: string; automacaoId?: string; automacaoNome?: string; campoGatilho?: string }
      | undefined;
    if (pendingAutomacaoMove) {
      // Removemos o flag para não persistir no Mongo
      delete (cardAtualizado as any)._pendingAutomacaoMove;
    }
    
    console.log('🔍 Verificando mudanças nos campos:', { houveMudancaNosCAMPOS, pendingAutomacaoMove: !!pendingAutomacaoMove });
    
    const deveExecutarAutomacoes = (houveMudancaNosCAMPOS || !!pendingAutomacaoMove) && !skipAutomacoes;
    if (etapaCard && automacoes && automacoes.length > 0 && deveExecutarAutomacoes) {
      console.log('🤖 Executando automações após edição do card', { houveMudancaNosCAMPOS, pendingAutomacaoMove: !!pendingAutomacaoMove });
      automationEngine.executeAutomations(
        automacoes,
        etapaCard,
        cardAtualizado.campos || {},
        'initial', // Usar 'initial' para avaliar todas as regras (igual criação)
        () => {}, // Não precisa manipular visibilidade aqui
        async (etapaDestinoId: string, meta?: { automacaoId?: string; automacaoNome?: string; campoGatilho?: string }) => {
          // Mover card para nova etapa
          console.log('🔄 Automação quer mover card para etapa:', etapaDestinoId);
          console.log('🔧 Campo gatilho a ser limpo:', meta?.campoGatilho);
          
          const etapaDestino = etapas.find(e => String(e.id) === String(etapaDestinoId));
          if (!etapaDestino) {
            console.error('❌ Etapa destino não encontrada:', etapaDestinoId);
            return;
          }

          // 📎 Etapa de reprovação que pede reenvio: a automação não pode mover
          // direto, senão o card cai em Reprovados sem dizer ao solicitante qual
          // arquivo refazer. Segura e abre o mesmo modal do arraste.
          const etapaDestinoCfg = flow.etapas?.find(e => String(e.id) === String(etapaDestinoId)) || etapaDestino;
          const cardParaReenvio = newCards.find(c => String(c.id) === String(cardAtualizado.id)) || cardAtualizado;
          if (etapaDestinoCfg.solicitarReenvioAnexos && !reprovacaoSoPorMotivoInterno(cardParaReenvio, flow, etapaDestinoCfg)) {
            console.log('📎 [AUTOMAÇÃO] Destino pede reenvio — abrindo modal antes de mover');
            setReenvioPendente({
              card: cardParaReenvio,
              draggableId: String(cardAtualizado.id),
              sourceId: String(etapaCard.id),
              destinationId: String(etapaDestinoId),
              destIndexRelativo: 0,
              viaAutomacao: true,
            });
            return;
          }

          // 🎯 APLICAR REGRAS DE RESPONSÁVEL AUTOMÁTICO PARA MOVIMENTAÇÃO POR AUTOMAÇÃO
          let novoResponsavelId: string | undefined = undefined;

          if (etapaDestino.regrasResponsavel && etapaDestino.regrasResponsavel.condicoes.length > 0) {
            const regras = etapaDestino.regrasResponsavel;

            if (regras.tipo === 'criador') {
              const criadorId = String(resolverCriadorId(cardAtualizado) || userPermissions?.currentUser?.id || '');
              const condicaoDireta = regras.condicoes.find(c => matchCriadorCondicao(criadorId, c.quando));
              const condicaoGenerica = regras.condicoes.find(c => c.quando === 'card_creator');
              const condicao = condicaoDireta || condicaoGenerica;
              if (condicao?.atribuirPara) {
                novoResponsavelId = condicao.atribuirPara;
                console.log('🎯 Regra aplicada (automação - criador):', { criadorId, novoResponsavelId });
              }
            } else if (regras.tipo === 'responsavel_anterior') {
              const responsavelAnterior = String(cardAtualizado.responsavelIndividualId || '');
              const condicao = regras.condicoes.find(c => String(c.quando) === responsavelAnterior);
              if (condicao?.atribuirPara) {
                novoResponsavelId = condicao.atribuirPara;
                console.log('🎯 Regra aplicada (automação - resp. anterior):', { responsavelAnterior, novoResponsavelId });
              }
            }
          }

          if (!novoResponsavelId && etapaDestino.responsavelPadraoId) {
            novoResponsavelId = etapaDestino.responsavelPadraoId;
            console.log('👤 Responsável padrão aplicado (automação):', novoResponsavelId);
          }
          
          // � FIX: Pegar o card mais recente do newCards (pode ter sido atualizado por automações anteriores)
          const cardMaisRecente = newCards.find(c => String(c.id) === String(cardAtualizado.id)) || cardAtualizado;
          console.log('📊 Card mais recente - etapaHistory:', cardMaisRecente.etapaHistory?.length || 0, 'entradas');
          
          // 🔄 LIMPAR CAMPOS DA ETAPA DE DESTINO QUE JÁ FORAM PREENCHIDOS ANTES
          // Quando automação move o card para frente, limpa os campos da etapa destino
          // para que o usuário possa preencher novamente (novo ciclo)
          const indexEtapaAtual = etapas.findIndex(e => String(e.id) === String(etapaCard.id));
          const indexEtapaDestino = etapas.findIndex(e => String(e.id) === String(etapaDestinoId));
          const isMovimentoParaFrente = indexEtapaDestino > indexEtapaAtual;
          void isMovimentoParaFrente; // mantido para uso futuro
          
          // Preparar campos atualizados - usar campos do card mais recente
          let camposAtualizados = { ...cardMaisRecente.campos };
          const camposLimposDestinoInfo: { campoId: string; nomeCampo: string; valorAnterior: any }[] = [];
          
          // 🔄 VERIFICAR SE O CARD JÁ PASSOU PELA ETAPA DESTINO ANTES (apenas para log)
          const jaPassouPelaEtapaDestino = cardMaisRecente.etapaHistory?.some(
            (h: any) => String(h.etapaId) === String(etapaDestinoId) && h.leftAt);
          
          console.log('📊 Verificação de histórico:', {
            etapaDestino: etapaDestino.nome,
            jaPassouPelaEtapaDestino,
            historicoLength: cardMaisRecente.etapaHistory?.length || 0
          });
          
          // 🔄 LIMPAR CAMPOS GATILHO QUANDO AUTOMAÇÃO MOVE CARD PARA TRÁS
          // Mesma lógica que o movimento manual: limpa os campos que disparam automações
          // de saída da etapa destino, para que o usuário possa preencher novamente
          const isMovimentoParaTras = indexEtapaDestino < indexEtapaAtual;
          
          if (isMovimentoParaTras && !(etapaDestino as any).naoLimparGatilhosAoVoltar && automacoes && automacoes.length > 0) {
            console.log('🔙 [AUTOMAÇÃO] Movimento para etapa ANTERIOR detectado:', {
              de: etapaCard.nome,
              para: etapaDestino.nome
            });
            
            // Buscar automações que SAEM da etapa de destino (onde o card está indo)
            const automacoesEtapaDestino = automacoes.filter(
              (a: any) => String(a.etapaOrigemId) === String(etapaDestinoId)
            );
            
            const camposParaLimparAutomacao: string[] = [];
            
            automacoesEtapaDestino.forEach((automacaoDestino: any) => {
              automacaoDestino.acoes?.forEach((acaoDestino: any) => {
                const tipoAcao = acaoDestino.tipo;
                const ehAcaoMover = tipoAcao === 'mover_card' || tipoAcao === 'moverCard' || tipoAcao === 'moveCard';
                
                if (ehAcaoMover) {
                  // Estrutura 1: Condições em automacao.condicoes
                  if (automacaoDestino.condicoes && automacaoDestino.condicoes.length > 0) {
                    automacaoDestino.condicoes.forEach((condicao: any) => {
                      if (condicao.campoId && !camposParaLimparAutomacao.includes(condicao.campoId)) {
                        camposParaLimparAutomacao.push(condicao.campoId);
                        console.log('  🧹 [AUTOMAÇÃO] Campo encontrado (estrutura 1):', condicao.campoId);
                      }
                    });
                  }
                  // Estrutura 2: Condições em acao.configuracao.regrasMover[].condicao
                  if (acaoDestino.configuracao?.regrasMover && acaoDestino.configuracao.regrasMover.length > 0) {
                    acaoDestino.configuracao.regrasMover.forEach((regraDestino: any) => {
                      const condicoes = regraDestino.condicoes?.length > 0 ? regraDestino.condicoes : (regraDestino.condicao ? [regraDestino.condicao] : []);
                      condicoes.forEach((cond: any) => {
                        const campoId = cond.campoId;
                        if (campoId && !camposParaLimparAutomacao.includes(campoId)) {
                          camposParaLimparAutomacao.push(campoId);
                          console.log('  🧹 [AUTOMAÇÃO] Campo encontrado (estrutura 2):', campoId);
                        }
                      });
                    });
                  }
                  // Estrutura 3: Condições em acao.configuracao.condicoes
                  if (acaoDestino.configuracao?.condicoes && acaoDestino.configuracao.condicoes.length > 0) {
                    acaoDestino.configuracao.condicoes.forEach((condicao: any) => {
                      if (condicao.campoId && !camposParaLimparAutomacao.includes(condicao.campoId)) {
                        camposParaLimparAutomacao.push(condicao.campoId);
                        console.log('  🧹 [AUTOMAÇÃO] Campo encontrado (estrutura 3):', condicao.campoId);
                      }
                    });
                  }
                }
              });
            });
            
            console.log('📋 [AUTOMAÇÃO] Total de campos para limpar:', camposParaLimparAutomacao.length, camposParaLimparAutomacao);
            
            if (camposParaLimparAutomacao.length > 0) {
              console.log('🧹 [AUTOMAÇÃO] Limpando', camposParaLimparAutomacao.length, 'campos gatilho para evitar automação automática');
              
              camposParaLimparAutomacao.forEach(campoId => {
                const valorAtual = camposAtualizados[campoId];
                
                // Buscar nome do campo para log e histórico
                const campoConfig = etapaDestino.campos?.find(c => c.id === campoId) ||
                                    etapas.flatMap(e => e.campos || []).find(c => c.id === campoId);
                const nomeCampo = campoConfig?.nome || campoId;
                
                // Salvar info do campo limpo para histórico
                if (valorAtual !== undefined && valorAtual !== '' && 
                    !(Array.isArray(valorAtual) && valorAtual.length === 0)) {
                  camposLimposDestinoInfo.push({
                    campoId,
                    nomeCampo,
                    valorAnterior: valorAtual
                  });
                }
                
                // Limpar o campo
                if (Array.isArray(valorAtual)) {
                  camposAtualizados[campoId] = [];
                } else {
                  camposAtualizados[campoId] = '';
                }
                
                console.log('  ✓ [AUTOMAÇÃO] Campo', campoId, `(${nomeCampo})`, 'limpo (era:', typeof valorAtual === 'string' ? valorAtual.substring(0, 30) : valorAtual, ')');
              });
            }
          } else {
            console.log('✅ Movimento para frente - campos mantidos');
          }
          
          // 🧹 LIMPAR CAMPOS CONFIGURADOS NA ETAPA DESTINO (configuração da etapa)
          if (etapaDestino.limparCamposAoRetornar?.ativo && etapaDestino.limparCamposAoRetornar.campos.length > 0) {
            const jaPassouPelaEtapa = cardMaisRecente.etapaHistory?.some(
              (h: any) => String(h.etapaId) === String(etapaDestinoId)
            );
            if (jaPassouPelaEtapa) {
              console.log('🧹 [CONFIG-ETAPA-AUTOMAÇÃO] Card retornando para etapa com limparCamposAoRetornar configurado');
              etapaDestino.limparCamposAoRetornar.campos.forEach(campoId => {
                const valorAtual = camposAtualizados[campoId];
                const campoConfig = etapaDestino.campos?.find(c => c.id === campoId) ||
                                    etapas.flatMap(e => e.campos || []).find(c => c.id === campoId);
                const nomeCampo = campoConfig?.nome || campoId;
                if (valorAtual !== undefined && valorAtual !== '' && !(Array.isArray(valorAtual) && valorAtual.length === 0)) {
                  camposLimposDestinoInfo.push({ campoId, nomeCampo, valorAnterior: valorAtual });
                }
                camposAtualizados[campoId] = Array.isArray(valorAtual) ? [] : '';
                console.log('  ✓ [CONFIG-ETAPA-AUTOMAÇÃO] Campo', campoId, `(${nomeCampo})`, 'limpo');
              });
            }
          }
          
          // 🔄 Mostrar loading global
          setSalvandoGlobal(`Movendo card para "${etapaDestino.nome}"...`);
          
          try {
            // Atualizar card com nova etapa
            const cardMovido = {
              ...cardMaisRecente,
              etapaId: String(etapaDestinoId),
              campos: camposAtualizados, // Campos limpos se movimento para trás, mantidos se para frente
              responsavelIndividualId: novoResponsavelId, // Aplicar responsável automático na automação
              isExplicitMove: true, // 🛡️ Flag para indicar movimentação intencional (automação)
              updatedAt: new Date(),
              etapaHistory: [
                ...((cardMaisRecente as any).etapaHistory || []).map((h: any) => 
                  String(h.etapaId) === String(cardMaisRecente.etapaId) && !h.leftAt
                    ? { ...h, leftAt: new Date() }
                    : h
                ),
                {
                  etapaId: String(etapaDestinoId),
                  movedAt: new Date(),
                  movedBy: userPermissions?.currentUser?.id,
                  motivo: 'automacao',
                  automacaoId: meta?.automacaoId,
                  automacaoNome: meta?.automacaoNome,
                  // 🔧 FIX: SALVAR camposSnapshot com os campos ATUAIS (com as edições recentes)
                  camposSnapshot: { ...camposAtualizados },
                  // �📋 Salvar campos da etapa destino que foram limpos (novo ciclo)
                  camposLimposAutomaticamente: camposLimposDestinoInfo.length > 0 ? camposLimposDestinoInfo.map(info => ({
                    campoId: info.campoId,
                    nomeCampo: info.nomeCampo,
                    valorAnterior: info.valorAnterior,
                    motivoLimpeza: 'Automação moveu card para frente - campos da etapa destino limpos para novo ciclo'
                  })) : undefined
                }
              ]
            };
            
            // ✅ Cards em coleção separada - não precisa mais limitar histórico
            
            // Remover campos temporários
            delete (cardMovido as any).camposSnapshot;
            delete (cardMovido as any).tempData;
            
            // 🔧 FIX: Salvar no backend PRIMEIRO, só depois atualizar a tela
            const { mongoService } = await import('../../utils/mongoService');
            await mongoService.updateCard(flow.id, cardMovido.id, cardMovido);
            console.log('✅ Card salvo no backend - agora atualizando tela');
            
            // Só atualizar estado local APÓS sucesso no backend
            const cardsAtualizados = newCards.map(c => 
              String(c.id) === String(cardMovido.id) ? cardMovido : c
            );
            setCards(cardsAtualizados);
            
            // 🔄 FIX: Atualizar newCards para próximas iterações de automação
            // Isso garante que múltiplas automações sequenciais usem os dados mais recentes
            newCards.length = 0;
            cardsAtualizados.forEach(c => newCards.push(c));
            
            // 💾 Atualizar cache IndexedDB com os cards atualizados
            onUpdateCache?.(cardsAtualizados);
            
            console.log('✅ Card movido por automação após edição');
            
            // 🔗 AUTO-TRIGGER: Verificar se a etapa de destino tem configs de Cards Conectados do tipo "mover_existente"
            const configsMoverAutoTrigger = cardsConectadosConfigs.filter(
              c => c.ativo && 
                   c.tipo === 'mover_existente' && 
                   String(c.etapaOrigemId) === String(etapaDestinoId)
            );
            
            if (configsMoverAutoTrigger.length > 0) {
              console.log(`🔗 [AUTO-TRIGGER-AUTOMAÇÃO] ${configsMoverAutoTrigger.length} config(s) "mover_existente" encontrada(s) para etapa "${etapaDestino.nome}"`);
              
              for (const configMover of configsMoverAutoTrigger) {
                try {
                  console.log(`🔗 [AUTO-TRIGGER-AUTOMAÇÃO] Executando config "${configMover.nome}" para card "${cardMovido.titulo}"...`);
                  
                  const email = userPermissions?.currentUser?.email || '';
                  const empresaId = flow.empresaId || '';
                  const params = new URLSearchParams({ userEmail: email, empresaId });
                  
                  const resMover = await fetch(`${apiUrl('/cards-conectados/criar')}?${params.toString()}`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'X-User-Data': JSON.stringify({
                        id: userPermissions?.currentUser?.id || 'admin_user',
                        empresaId: empresaId,
                        role: 'admin'
                      })
                    },
                    body: JSON.stringify({
                      configuracaoId: configMover.id,
                      cardOrigemId: cardMovido.id,
                      flowOrigemId: flow.id
                    })
                  });
                  
                  const dataMover = await resMover.json();
                  if (dataMover.success) {
                    console.log(`✅ [AUTO-TRIGGER-AUTOMAÇÃO] Card conectado movido: ${dataMover.message}`);
                    setToastInfo({
                      visible: true,
                      titulo: '🔗 Card Conectado Movido',
                      mensagem: dataMover.message || `Card conectado movido automaticamente pela config "${configMover.nome}"`,
                      camposLimpos: []
                    });
                    setTimeout(() => setToastInfo(null), 6000);
                  } else if (dataMover.condicoesNaoAtendidas) {
                    console.log(`⚠️ [AUTO-TRIGGER-AUTOMAÇÃO] Condições não atendidas para config "${configMover.nome}"`);
                  } else {
                    console.warn(`⚠️ [AUTO-TRIGGER-AUTOMAÇÃO] Erro: ${dataMover.error}`);
                  }
                } catch (errMover) {
                  console.error(`❌ [AUTO-TRIGGER-AUTOMAÇÃO] Erro:`, errMover);
                }
              }
            } else {
              console.log(`🔗 [AUTO-TRIGGER-AUTOMAÇÃO] Nenhuma config "mover_existente" para etapa "${etapaDestino.nome}" (id=${etapaDestinoId})`);
            }
            
            // ✅ Mostrar mensagem de sucesso por 2 segundos
            setSalvandoGlobal(`✅ Card movido para "${etapaDestino.nome}" com sucesso!`);
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // 🔄 Fechar loading global
            setSalvandoGlobal(null);
            
            // Notificar movimentação
            if (flow.empresaId && flow.id) {
              notifyCardMoved(
                flow.id,
                flow.name,
                cardMovido.titulo,
                etapaCard.nome,
                etapaDestino.nome,
                userPermissions?.currentUser?.nome || 'Sistema',
                flow.empresaId
              ).catch(err => console.error('Erro ao notificar movimentação:', err));
            }
          } catch (error) {
            console.error('❌ Erro ao mover card por automação após edição:', error);
            setSalvandoGlobal(null); // Fechar loading em caso de erro
            alert(`⚠️ Erro ao mover card automaticamente: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
          }
        },
        cardAtualizado.id
      );
    } else if (!houveMudancaNosCAMPOS) {
      console.log('⏭️ Pulando automações - apenas responsável/título foi alterado, campos não mudaram');
    }
    
    // Auto-save (ex.: upload de anexo) não deve fechar o modal nem notificar
    if ((cardAtualizado as any)._skipClose) {
      console.log('⏭️ _skipClose=true — mantendo modal aberto');
      return;
    }

    console.log('⏱️ Fechando modal após automações');
    setCardSelecionado(null);
    
    // 💬 Notificar no chat
    if (flow.empresaId && flow.id) {
      notifyCardUpdated(
        flow.id,
        flow.name,
        cardAtualizado.titulo,
        userPermissions?.currentUser?.nome || 'Usuário',
        flow.empresaId
      ).catch(err => console.error('Erro ao notificar atualização de card:', err));
    }
    
    return;
  };

  const handleDeleteCard = async (cardId: string) => {
    const cardToDelete = cards.find(c => String(c.id) === String(cardId));
    if (!cardToDelete) return;

    // Se for um card pai, perguntar o que fazer com os filhos
    if (cardToDelete.ehCardPai && cardToDelete.cardsFilhosIds && cardToDelete.cardsFilhosIds.length > 0) {
      const opcao = window.confirm(
        `Este card pai tem ${cardToDelete.cardsFilhosIds.length} filho(s).\n\nClique OK para DELETAR todos os filhos também.\nClique Cancelar para apenas LIBERAR os filhos (eles ficarão independentes).`
      );
      
      if (opcao) {
        // Deletar card pai E todos os filhos
        const idsParaDeletar = [cardId, ...cardToDelete.cardsFilhosIds];
        
        // 🔥 DELETAR CADA CARD NA API (coleção separada ou array)
        for (const id of idsParaDeletar) {
          try {
            await fetch(`/api/cards/${id}`, { method: 'DELETE' });
          } catch (error) {
            console.error(`Erro ao deletar card ${id}:`, error);
          }
        }
        
        const newCards = cards.filter(c => !idsParaDeletar.includes(String(c.id)));
        setCards(newCards);
        setCardSelecionado(null);
        
        // 💾 Atualizar cache IndexedDB com cards atuais
        onUpdateCache?.(newCards);
        return;
      } else {
        // Deletar apenas o card pai e liberar os filhos
        try {
          await fetch(`/api/cards/${cardId}`, { method: 'DELETE' });
        } catch (error) {
          console.error('Erro ao deletar card pai:', error);
        }
        
        const newCards = cards.map(c => {
          if (cardToDelete.cardsFilhosIds?.includes(String(c.id))) {
            return { ...c, cardPaiId: undefined };
          }
          return c;
        }).filter(c => String(c.id) !== String(cardId));
        
        setCards(newCards);
        setCardSelecionado(null);
        
        // 💾 Atualizar cache IndexedDB com cards atuais
        onUpdateCache?.(newCards);
        
        // Atualizar filhos liberados no backend
        onUpdateFlow({ 
          ...flow, 
          cards: newCards,
          updatedAt: new Date()
        }).catch(error => {
          console.error('Erro ao atualizar filhos:', error);
        });
        return;
      }
    }

    // Se for um card filho, remover referência do pai
    if (cardToDelete.cardPaiId) {
      try {
        await fetch(`/api/cards/${cardId}`, { method: 'DELETE' });
      } catch (error) {
        console.error('Erro ao deletar card filho:', error);
      }
      
      const newCards = cards.map(c => {
        if (String(c.id) === String(cardToDelete.cardPaiId)) {
          return {
            ...c,
            cardsFilhosIds: c.cardsFilhosIds?.filter(fId => String(fId) !== String(cardId)) || []
          };
        }
        return c;
      }).filter(c => String(c.id) !== String(cardId));

      setCards(newCards);
      setCardSelecionado(null);
      
      // 💾 Atualizar cache IndexedDB com cards atuais
      onUpdateCache?.(newCards);
      
      // Atualizar pai no backend
      onUpdateFlow({ 
        ...flow, 
        cards: newCards,
        updatedAt: new Date()
      }).catch(error => {
        console.error('Erro ao atualizar card pai:', error);
      });
      return;
    }

    // Card normal sem hierarquia
    if (!window.confirm('Tem certeza que deseja excluir este cartão?')) return;

    console.log('🗑️ Deletando card via API:', cardId);
    
    // 🔥 DELETAR VIA API (coleção separada ou array)
    try {
      const response = await fetch(`/api/cards/${cardId}`, { method: 'DELETE' });
      const result = await response.json();
      
      if (response.ok) {
        console.log('✅ Card deletado:', result);
        const newCards = cards.filter(c => String(c.id) !== String(cardId));
        setCards(newCards);
        setCardSelecionado(null);
        
        // � Atualizar cache IndexedDB com cards atuais
        onUpdateCache?.(newCards);
        
        // �💬 Notificar no chat
        if (flow.empresaId && flow.id && cardToDelete) {
          notifyCardDeleted(
            flow.id,
            flow.name,
            cardToDelete.titulo,
            userPermissions?.currentUser?.nome || 'Usuário',
            flow.empresaId
          ).catch(err => console.error('Erro ao notificar exclusão:', err));
        }
      } else {
        console.error('❌ Erro ao deletar card:', result);
        alert('Erro ao deletar card. Tente novamente.');
      }
    } catch (error) {
      console.error('❌ Erro na requisição DELETE:', error);
      alert('Erro ao deletar card. Tente novamente.');
    }
  };

  // Função para salvar a ordenação dos cards de uma etapa
  const handleSalvarOrdenacaoEtapa = async (etapaId: string, ordenacao: string) => {
    console.log('💾 Salvando ordenação da etapa:', { etapaId, ordenacao });
    
    const etapasAtualizadas = etapas.map(e => 
      String(e.id) === String(etapaId) 
        ? { ...e, ordenacaoCards: ordenacao as any }
        : e
    );
    
    setEtapas(etapasAtualizadas);
    
    try {
      await onUpdateFlow({
        ...flow,
        etapas: etapasAtualizadas
      });
      
      console.log('✅ Ordenação salva com sucesso');
      setConfigurandoOrdenacao(null);
    } catch (error) {
      console.error('❌ Erro ao salvar ordenação:', error);
    }
  };

  // Função para ordenar cards com base na configuração da etapa
  const ordenarCards = (cards: Card[], etapa: Etapa): Card[] => {
    const ordenacao = etapa.ordenacaoCards || 'mais_recente'; // Padrão: mais recente
    
    const cardsOrdenados = [...cards];
    
    switch (ordenacao) {
      case 'mais_recente':
        return cardsOrdenados.sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      
      case 'mais_antigo':
        return cardsOrdenados.sort((a, b) => 
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      
      case 'vencimento_proximo':
        return cardsOrdenados.sort((a, b) => {
          const campoVencimento = etapa.campos?.find(c => c.tipo === 'data_vencimento');
          if (!campoVencimento) return 0;
          
          const vencA = a.campos?.[campoVencimento.id];
          const vencB = b.campos?.[campoVencimento.id];
          
          if (!vencA && !vencB) return 0;
          if (!vencA) return 1;
          if (!vencB) return -1;
          
          return new Date(vencA).getTime() - new Date(vencB).getTime();
        });
      
      case 'vencimento_distante':
        return cardsOrdenados.sort((a, b) => {
          const campoVencimento = etapa.campos?.find(c => c.tipo === 'data_vencimento');
          if (!campoVencimento) return 0;
          
          const vencA = a.campos?.[campoVencimento.id];
          const vencB = b.campos?.[campoVencimento.id];
          
          if (!vencA && !vencB) return 0;
          if (!vencA) return 1;
          if (!vencB) return -1;
          
          return new Date(vencB).getTime() - new Date(vencA).getTime();
        });
      
      case 'titulo_az':
        return cardsOrdenados.sort((a, b) => 
          (a.titulo || '').localeCompare(b.titulo || '')
        );
      
      case 'titulo_za':
        return cardsOrdenados.sort((a, b) => 
          (b.titulo || '').localeCompare(a.titulo || '')
        );
      
      default:
        return cardsOrdenados;
    }
  };

  // (removido) handleColaboradorClick não utilizado

  // 🔧 CORREÇÃO 23/01/2026: Usar todasEtapas para garantir que anexos apareçam no histórico
  // Anteriormente usava 'etapas' que é filtrado por permissões do usuário
  const getEtapasAnteriores = (etapaId: string, card?: Card): Etapa[] => {
    // Usar todasEtapas para incluir etapas que o usuário talvez não veja no kanban
    // mas que contêm campos com valores salvos (como anexos)
    const etapasParaUsar = todasEtapas.length > 0 ? todasEtapas : etapas;
    const currentIndex = etapasParaUsar.findIndex(e => String(e.id) === String(etapaId));

    // Além das etapas que vêm antes no desenho do fluxo, entram as que o card
    // realmente percorreu — mesmo as que ficam DEPOIS da atual. Sem isso, mandar
    // um card para trás esconde o que foi preenchido na etapa sucessora: em Obras,
    // voltar da Financeiro fazia o Comprovante sumir para quem precisava vê-lo.
    const visitadas = new Set(
      (card?.etapaHistory || []).map(h => String(h.etapaId))
    );

    return etapasParaUsar.filter((e, idx) =>
      String(e.id) !== String(etapaId) &&
      (idx < currentIndex || visitadas.has(String(e.id)))
    );
  };

  // (removido) getCardVencimentoStatus não utilizado

  // Componente de Visualização Lista
  const renderListaView = () => {
    console.log('📝 Renderizando Lista View:', { cards: cards.length, etapas: etapas.length });
    
    const toggleEtapa = (etapaId: string) => {
      const newSet = new Set(etapasExpandidas);
      if (newSet.has(etapaId)) {
        newSet.delete(etapaId);
      } else {
        newSet.add(etapaId);
      }
      setEtapasExpandidas(newSet);
    };
    
    return (
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50">
          <div className="px-3 md:px-4 py-3 h-full flex flex-col">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Visualização em Lista</h2>
                <p className="text-gray-500 text-xs">Clique nas etapas para expandir/recolher</p>
              </div>
            </div>

            {/* 🔀 Droppable para reordenação de etapas */}
            <Droppable droppableId="etapas-list" direction="vertical" type="ETAPA">
              {(provided) => (
                <div 
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="space-y-2 min-h-[100px] flex-1 overflow-y-auto"
                >
                  {etapas && etapas.length > 0 ? (
                    etapas.map((etapa, index) => {
                      const cardsNaEtapa = cards.filter(c => String(c.etapaId) === String(etapa.id));
                      const cardsFiltradosLista = filtrarCards(cardsNaEtapa, String(etapa.id));
                      // A Lista mostra exatamente os mesmos cards do Kanban: card filho
                      // aparece dentro do card pai, não solto na coluna. Sem isto a mesma
                      // etapa listava 3 linhas onde o quadro desenha 1.
                      const cardsListaVisiveis = obterCardsRaiz(cardsFiltradosLista);
                      const isExpanded = etapasExpandidas.has(String(etapa.id));
                      const canAddEtapaLista = userPermissions?.canAdmin || checkEtapaEditPermission?.();
                      
                      return (
                        <Draggable 
                          key={String(etapa.id)} 
                          draggableId={String(etapa.id)} 
                          index={index}
                          isDragDisabled={!(userPermissions?.canAdmin || checkEtapaEditPermission?.())}
                        >
                          {(provided, snapshot) => (
                            <div 
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`bg-white rounded-xl shadow-sm border overflow-hidden relative ${
                                snapshot.isDragging ? 'opacity-50 shadow-2xl' : ''
                              }`}
                            >
                              {/* Botão inserir etapa antes desta (hover apenas na faixa superior) */}
                              {canAddEtapaLista && index > 0 && !snapshot.isDragging && (
                                <div className="absolute left-0 right-0 -top-3 h-6 z-20 group/insert-lista flex items-center justify-center">
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); addEtapaAt(index); }}
                                    title="Inserir etapa aqui"
                                    className="w-6 h-6 rounded-full bg-violet-600 hover:bg-violet-700 text-white shadow-lg flex items-center justify-center text-sm font-bold opacity-0 group-hover/insert-lista:opacity-100 transition-opacity"
                                  >
                                    +
                                  </button>
                                </div>
                              )}
                              {/* Cabeçalho da Etapa - Clicável */}
                              <div 
                                className="px-3 py-2.5 bg-gray-50 border-b border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
                                onClick={() => toggleEtapa(String(etapa.id))}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    {/* Drag handle icon */}
                                    <div 
                                      {...provided.dragHandleProps}
                                      onClick={(e) => e.stopPropagation()}
                                      className="cursor-move text-gray-300 hover:text-gray-500"
                                      title="Arrastar para reordenar etapas"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                      </svg>
                                    </div>
                                    <div className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                                      <svg className="w-3.5 h-3.5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                    <div>
                                      <h3 className="text-sm font-semibold text-gray-900">{etapa.nome}</h3>
                                      <p className="text-xs text-gray-500">
                                        {rotuloContagemEtapa(cardsNaEtapa, cardsFiltradosLista)}
                                      </p>
                                    </div>
                                  </div>
                                                  <div className="flex items-center gap-2">
                                    {/* Avatares dos participantes */}
                                    {etapa.participantes && etapa.participantes.length > 0 && (
                                      <div className="flex -space-x-2">
                                        {etapa.participantes.slice(0, 3).map((participanteId, avatarIndex) => {
                                          const colaborador = colaboradores.find(c => c.id === participanteId);
                                          return (
                                            <div
                                              key={participanteId}
                                              className="w-6 h-6 rounded-full border-[1.5px] border-white shadow-sm overflow-hidden"
                                              title={colaborador?.nome || 'Participante'}
                                            >
                                              {colaborador?.foto ? (
                                                <img src={colaborador.foto} alt={colaborador.nome} className="w-full h-full object-cover" />
                                              ) : (
                                                <div className={`w-full h-full ${getAvatarColor(avatarIndex)} flex items-center justify-center text-white text-[9px] font-semibold`}>
                                                  {getInitials(colaborador?.nome || '??')}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                        {etapa.participantes.length > 3 && (
                                          <div className="w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center text-white text-[9px] font-semibold border-[1.5px] border-white shadow-sm">
                                            +{etapa.participantes.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    {etapa.departamentoId && (
                                      <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] rounded-full font-medium">
                                        {departamentos.find(d => d.id === etapa.departamentoId)?.nome || 'Depto'}
                                      </span>
                                    )}
                                    {(userPermissions?.canAdmin || checkEtapaEditPermission?.()) && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setConfiguring(etapa);
                                          onOpenEtapaConfig?.();
                                        }}
                                        className="p-1.5 text-gray-300 hover:text-gray-500 hover:bg-white rounded-md transition-colors"
                                      >
                                        <Cog6ToothIcon className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Lista de Cards (Expansível) */}
                              {isExpanded && (
                                <div className="p-3 max-h-[50vh] overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                                  {cardsListaVisiveis.length > 0 ? (
                                    <div className="space-y-2">
                          {cardsListaVisiveis.map(card => {
                            // Verificar vencimento
                            const campoVencimento = etapa.campos?.find(c => c.tipo === 'data_vencimento');
                            let statusVencimento = null;
                            if (campoVencimento && card.campos?.[campoVencimento.id]) {
                              try {
                                const dataVencimento = new Date(card.campos[campoVencimento.id]);
                                const agora = new Date();
                                const diferencaHoras = (dataVencimento.getTime() - agora.getTime()) / (1000 * 60 * 60);
                                
                                if (dataVencimento < agora) {
                                  statusVencimento = { label: 'VENCIDO', color: 'bg-red-100 text-red-700' };
                                } else if (diferencaHoras <= 24) {
                                  statusVencimento = { label: 'VENCE HOJE', color: 'bg-yellow-100 text-yellow-700' };
                                }
                              } catch (error) {
                                // Ignorar erros de data
                              }
                            }

                            return (
                              <div 
                                key={card.id}
                                className="px-3 py-2 bg-white rounded-lg hover:bg-indigo-50 transition-all cursor-pointer border border-gray-150 hover:border-indigo-200"
                                onClick={() => {
                                  console.log('🔧 Clicando no card:', card.titulo);
                                  setCardSelecionado(card);
                                }}
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    {/* Avatar do responsável inline */}
                                    {card.responsavelIndividualId && (() => {
                                      const responsavel = colaboradores.find(c => String(c.id) === String(card.responsavelIndividualId));
                                      if (responsavel) {
                                        const initials = responsavel.nome.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
                                        const avatarColor = getAvatarColorFromId(String(responsavel.id));
                                        return (
                                          <div className={`w-5 h-5 rounded-full ${avatarColor} flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0`} title={responsavel.nome}>
                                            {initials}
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                    <h4 className="text-xs font-medium text-gray-900 truncate">{card.titulo || 'Sem título'}</h4>
                                    {statusVencimento && (
                                      <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full flex-shrink-0 ${statusVencimento.color}`}>
                                        {statusVencimento.label}
                                      </span>
                                    )}
                                  </div>
                                  
                                  {/* Campos inline */}
                                  <div className="hidden md:flex items-center gap-3 text-[10px] text-gray-500 flex-shrink-0">
                                    {(() => {
                                        // 1. Buscar TODOS os campos travados de TODAS as etapas do fluxo
                                        const todosCamposTravados = new Map(); // id -> campo
                                        flow.etapas?.forEach(e => {
                                          if (e.camposTravados && e.camposTravados.length > 0) {
                                            e.campos.forEach(c => {
                                              if (e.camposTravados && e.camposTravados.includes(c.id)) {
                                                todosCamposTravados.set(c.id, c);
                                              }
                                            });
                                          }
                                        });
                                        
                                        // 2. Campos visíveis configurados da etapa atual
                                        let camposVisiveis = etapa.camposVisiveisNoCard && etapa.camposVisiveisNoCard.length > 0
                                          ? etapa.campos.filter(c => etapa.camposVisiveisNoCard?.includes(c.id))
                                          : [];
                                        
                                        // 3. Adicionar campos travados que não estão na lista de visíveis
                                        const camposTravadosArray = Array.from(todosCamposTravados.values());
                                        const travadosNaoVisiveis = camposTravadosArray.filter(
                                          c => !camposVisiveis.find(v => v.id === c.id)
                                        );
                                        
                                        // 4. Combinar: visíveis + travados não visíveis
                                        let camposParaMostrar = [...camposVisiveis, ...travadosNaoVisiveis];
                                        
                                        // 🔗 5. NOVO: Adicionar campos de outras etapas configurados
                                        const camposDeOutrasEtapas: { campo: any; etapaNome: string; isOutraEtapa: boolean }[] = [];
                                        if (etapa.camposOutrasEtapas && etapa.camposOutrasEtapas.length > 0) {
                                          etapa.camposOutrasEtapas.forEach((campoKey: string) => {
                                            // Formato: "etapaId:campoId"
                                            const [etapaId, campoId] = campoKey.split(':');
                                            const outraEtapa = flow.etapas?.find(e => String(e.id) === etapaId);
                                            if (outraEtapa) {
                                              const campo = outraEtapa.campos?.find(c => c.id === campoId);
                                              const vFix = card?.campos?.[campoId];
                                              const vazioFix = vFix === undefined || vFix === null || vFix === '' || (Array.isArray(vFix) && vFix.length === 0);
                                              if (campo && !((etapa as any).ocultarFixadosVazios && vazioFix)) {
                                                camposDeOutrasEtapas.push({
                                                  campo: { ...campo, id: campoId },
                                                  etapaNome: outraEtapa.nome,
                                                  isOutraEtapa: true
                                                });
                                              }
                                            }
                                          });
                                        }
                                        
                                        // 6. Se não tem nada configurado (sem visíveis, sem travados, sem outras etapas), mostrar primeiros 4
                                        if (camposParaMostrar.length === 0 && camposDeOutrasEtapas.length === 0) {
                                          camposParaMostrar = etapa.campos?.slice(0, 4) || [];
                                        }
                                        
                                        // 🔍 Função helper para buscar valor do campo na fonte correta
                                        // PRIORIDADE: etapaHistory.camposSnapshot > card.campos
                                        // ⚠️ IMPORTANTE - NÃO ALTERAR A ORDEM! (Corrigido em 22/01/2026)
                                        // card.campos SEMPRE tem prioridade sobre etapaHistory porque:
                                        // 1. Quando o usuário edita um campo no histórico, o valor é salvo em card.campos
                                        // 2. etapaHistory contém SNAPSHOTS antigos de quando o card passou por cada etapa
                                        // 3. Se buscarmos no etapaHistory primeiro, mostramos valores desatualizados!
                                        const getValorCampo = (campoId: string) => {
                                          // PRIMEIRO: card.campos (fonte da verdade - valores atuais/editados)
                                          if (card?.campos?.[campoId] !== undefined && card.campos[campoId] !== '') {
                                            return card.campos[campoId];
                                          }
                                          // SEGUNDO: etapaHistory (fallback para campos que só existem em etapas anteriores)
                                          if (card?.etapaHistory && Array.isArray(card.etapaHistory)) {
                                            for (let i = card.etapaHistory.length - 1; i >= 0; i--) {
                                              const histEntry = card.etapaHistory[i];
                                              if (histEntry?.camposSnapshot?.[campoId] !== undefined && histEntry.camposSnapshot[campoId] !== '') {
                                                return histEntry.camposSnapshot[campoId];
                                              }
                                            }
                                          }
                                          return undefined;
                                        };
                                        
                                        // Renderizar campos da etapa atual + travados (oculta vazios) - max 3 inline
                                        const formatarValorMoeda = (v: any) => { const s = String(v).replace(/[R$\s]/g, '').trim(); const n = s.includes(',') ? parseFloat(s.replace(/\./g, '').replace(',', '.')) : parseFloat(s); return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(isNaN(n) ? 0 : n); };
                                        const renderMiniValor = (campo: any, valor: any) => {
                                          const cores = campo?.coresOpcoes as Record<string, string> | undefined;
                                          const isColorido = ['lista','checkbox','checkbox_single','checkbox_multi'].includes(campo?.tipo);
                                          if (isColorido && cores && (typeof valor === 'string' || Array.isArray(valor))) {
                                            const itens = Array.isArray(valor) ? valor.filter(v => v !== undefined && v !== null && v !== '') : [valor];
                                            return (
                                              <span className="inline-flex flex-wrap gap-0.5 items-center">
                                                {itens.map((item: any, i: number) => {
                                                  const texto = String(item);
                                                  const cor = cores[texto];
                                                  return cor ? (
                                                    <span key={i} className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: cor, color: '#fff' }}>{texto}</span>
                                                  ) : (
                                                    <span key={i} className="text-gray-700">{texto}{i < itens.length - 1 ? ', ' : ''}</span>
                                                  );
                                                })}
                                              </span>
                                            );
                                          }
                                          if (Array.isArray(valor)) return <span className="text-gray-700">{valor.length + ' itens'}</span>;
                                          if (campo?.tipo === 'valor') return <span className="text-gray-700">{formatarValorMoeda(valor)}</span>;
                                          return <span className="text-gray-700">{String(valor).substring(0, 20)}</span>;
                                        };
                                        const camposAtuais = camposParaMostrar.slice(0, 3).map(campo => {
                                          const valor = getValorCampo(campo.id);
                                          if (valor === undefined || valor === null || valor === '' || (Array.isArray(valor) && valor.length === 0)) return null;
                                          const isTravado = todosCamposTravados.has(campo.id);
                                          
                                          return (
                                            <span key={campo.id} className="flex items-center gap-0.5 whitespace-nowrap">
                                              <span className={`${isTravado ? 'text-blue-500' : 'text-gray-400'} font-medium`}>
                                                {campo.nome}:
                                              </span>
                                              {renderMiniValor(campo, valor)}
                                            </span>
                                          );
                                        }).filter(Boolean);
                                        
                                        // Renderizar campos de outras etapas (oculta vazios) - max 2 inline
                                        const camposOutras = camposDeOutrasEtapas.slice(0, 2).map(({ campo, etapaNome }) => {
                                          const valor = getValorCampo(campo.id);
                                          if (valor === undefined || valor === null || valor === '' || (Array.isArray(valor) && valor.length === 0)) return null;
                                          
                                          return (
                                            <span key={`outras-${campo.id}`} className="flex items-center gap-0.5 whitespace-nowrap" title={`Da etapa: ${etapaNome}`}>
                                              <span className="text-purple-400 font-medium">
                                                {campo.nome}:
                                              </span>
                                              {renderMiniValor(campo, valor)}
                                            </span>
                                          );
                                        }).filter(Boolean);
                                        
                                        return [...camposAtuais, ...camposOutras];
                                      })()}
                                    </div>
                                  
                                  <div className="text-[10px] text-gray-400 flex-shrink-0">
                                    {card.updatedAt ? (
                                      new Date(card.updatedAt).toLocaleDateString('pt-BR')
                                    ) : (
                                      'Sem data'
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-400 text-xs">
                          <p>Nenhum cartão nesta etapa</p>
                          {checkCardEditPermission?.(etapa) && (etapa.permiteCriarCards ?? true) && (
                            <button
                              onClick={() => setNovoCartao(etapa)}
                              className="mt-1.5 text-indigo-500 hover:text-indigo-600 font-medium text-xs"
                            >
                              + Adicionar cartão
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Draggable>
          );
        })
        ) : (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border">
            <p className="text-gray-500">Nenhuma etapa encontrada</p>
          </div>
        )}
        {provided.placeholder}
      </div>
    )}
  </Droppable>
          </div>
        </div>
      </DragDropContext>
    );
  };

  // Componente de Visualização Fluxo - Estilo BPMN/Bizagi
  const renderFluxoView = () => {
    console.log('🔄 Renderizando Fluxo View:', { 
      etapas: etapas.length, 
      cards: cards.length, 
      automacoes: automacoes?.length || 0,
      automacoesData: automacoes 
    });
    
    // Função para gerar descrição detalhada da automação
    const gerarDescricaoAutomacao = (automacao: any, regra?: any, tipoAcao?: 'entao' | 'senao') => {
      const partes: string[] = [];
      
      // Condição (SE)
      if (regra?.condicao) {
        const campo = etapas.flatMap(e => e.campos || []).find(c => c.id === regra.condicao.campoId);
        
        // Para SENÃO, inverter a condição
        if (tipoAcao === 'senao') {
          const operadorInversoMap: Record<string, string> = {
            'preenchido': 'vazio',
            'vazio': 'preenchido',
            'igual': `≠ "${regra.condicao.valor}"`,
            'diferente': `= "${regra.condicao.valor}"`,
            'maior': `≤ ${regra.condicao.valor}`,
            'menor': `≥ ${regra.condicao.valor}`,
            'maior_igual': 'data não passou',
            'contem': `não contém "${regra.condicao.valor}"`,
            'dias_restantes': `> ${regra.condicao.valor} dias`
          };
          partes.push(`SE ${campo?.nome || 'campo'} ${operadorInversoMap[regra.condicao.operador] || ''}`);
        } else {
          const operadorMap: Record<string, string> = {
            'preenchido': 'preenchido',
            'vazio': 'vazio',
            'igual': `= "${regra.condicao.valor}"`,
            'diferente': `≠ "${regra.condicao.valor}"`,
            'maior': `> ${regra.condicao.valor}`,
            'menor': `< ${regra.condicao.valor}`,
            'maior_igual': 'data passou',
            'contem': `contém "${regra.condicao.valor}"`,
            'dias_restantes': `≤ ${regra.condicao.valor} dias`
          };
          partes.push(`SE ${campo?.nome || 'campo'} ${operadorMap[regra.condicao.operador] || ''}`);
        }
      }
      
      // Ação principal (apenas a ação ENTÃO, sem o SENÃO)
      automacao.acoes?.forEach((acao: any) => {
        if (acao.tipo === 'moverCard' || acao.tipo === 'mover_card') {
          if (regra?.acaoEntao?.tipo === 'mover') {
            const etapaDest = etapas.find(e => String(e.id) === String(regra.acaoEntao.etapaDestinoId));
            partes.push(`→ ${etapaDest?.nome || 'outra etapa'}`);
          }
          // Removido o SENÃO - será outra automação separada
        } else if (acao.tipo === 'enviarEmail' || acao.tipo === 'enviar_email') {
          partes.push('📧');
        } else if (acao.tipo === 'enviarWhatsapp' || acao.tipo === 'enviar_whatsapp') {
          partes.push('📱');
        } else if (acao.tipo === 'exibirOcultar' || acao.tipo === 'exibir_ocultar') {
          partes.push('👁️');
        }
      });
      
      return partes.length > 0 ? partes.join(' ') : automacao.nome;
    };
    
    // Criar mapa de conexões entre etapas baseado em permiteMoverPara e automações
    const criarMapaConexoes = () => {
      const conexoes: { origem: string; destino: string; tipo: 'permitido' | 'automacao'; label?: string; descricao?: string }[] = [];
      
      // Conexões baseadas em permiteMoverPara
      etapas.forEach(etapa => {
        if (etapa.permiteMoverPara && etapa.permiteMoverPara.length > 0) {
          etapa.permiteMoverPara.forEach(destinoId => {
            conexoes.push({
              origem: String(etapa.id),
              destino: String(destinoId),
              tipo: 'permitido'
            });
          });
        }
      });
      
      // Conexões baseadas em automações que movem cards
      console.log('🔍 Total de automações:', automacoes?.length || 0);
      automacoes?.forEach((automacao: any) => {
        // Usar etapaOrigemId ao invés de etapaId
        const etapaOrigemId = automacao.etapaOrigemId || automacao.etapaId;
        const etapaOrigem = etapas.find(e => String(e.id) === String(etapaOrigemId));
        
        console.log('🔍 Processando automação:', automacao.nome);
        console.log('   Etapa origem:', etapaOrigem?.nome || 'NÃO ENCONTRADA', '(ID:', etapaOrigemId, ')');
        console.log('   Ações:', automacao.acoes?.length || 0);
        
        if (!automacao.acoes || automacao.acoes.length === 0) {
          console.log('⚠️ Automação sem ações:', automacao.nome);
          return;
        }
        
        automacao.acoes?.forEach((acao: any, idx: number) => {
          console.log(`  📌 Ação ${idx + 1}:`, acao.tipo);
          
          // Verificar se é ação de mover card (com ou sem underscore)
          if (acao.tipo === 'moverCard' || acao.tipo === 'mover_card' || acao.tipo === 'moveCard') {
            // Buscar destino na configuração ou diretamente na ação
            const destino = acao.configuracao?.etapaDestinoId || acao.etapaDestinoId || acao.etapaDestino;
            
            // Se tem regras de mover, processar cada regra
            if (acao.configuracao?.regrasMover && acao.configuracao.regrasMover.length > 0) {
              console.log(`  🔀 Processando ${acao.configuracao.regrasMover.length} regra(s) de movimentação`);
              
              acao.configuracao.regrasMover.forEach((regra: any, rIdx: number) => {
                // Processar ação ENTÃO
                if (regra.acaoEntao?.tipo === 'mover' && regra.acaoEntao.etapaDestinoId) {
                  const origem = String(etapaOrigemId);
                  const destinoStr = String(regra.acaoEntao.etapaDestinoId);
                  const etapaDestino = etapas.find(e => String(e.id) === destinoStr);
                  
                  const existe = conexoes.some(c => c.origem === origem && c.destino === destinoStr);
                  if (!existe) {
                    console.log(`    ✅ [Regra ${rIdx + 1} - ENTÃO] Adicionando:`, etapaOrigem?.nome, '→', etapaDestino?.nome);
                    conexoes.push({
                      origem,
                      destino: destinoStr,
                      tipo: 'automacao',
                      label: automacao.nome,
                      descricao: gerarDescricaoAutomacao(automacao, regra, 'entao')
                    });
                  }
                }
                
                // Processar ação SENÃO
                if (regra.acaoSenao?.tipo === 'mover' && regra.acaoSenao.etapaDestinoId) {
                  const origem = String(etapaOrigemId);
                  const destinoStr = String(regra.acaoSenao.etapaDestinoId);
                  const etapaDestino = etapas.find(e => String(e.id) === destinoStr);
                  
                  const existe = conexoes.some(c => c.origem === origem && c.destino === destinoStr);
                  if (!existe) {
                    console.log(`    ✅ [Regra ${rIdx + 1} - SENÃO] Adicionando:`, etapaOrigem?.nome, '→', etapaDestino?.nome);
                    conexoes.push({
                      origem,
                      destino: destinoStr,
                      tipo: 'automacao',
                      label: automacao.nome,
                      descricao: gerarDescricaoAutomacao(automacao, regra, 'senao')
                    });
                  }
                }
              });
            } 
            // Se não tem regras mas tem destino direto
            else if (destino) {
              const origem = String(etapaOrigemId);
              const destinoStr = String(destino);
              const etapaDestino = etapas.find(e => String(e.id) === destinoStr);
              
              const existe = conexoes.some(c => c.origem === origem && c.destino === destinoStr);
              if (!existe) {
                console.log('  ✅ Adicionando conexão:', etapaOrigem?.nome, '→', etapaDestino?.nome);
                conexoes.push({
                  origem,
                  destino: destinoStr,
                  tipo: 'automacao',
                  label: automacao.nome
                });
              } else {
                // Atualizar para tipo automação se já existe como permitido
                const conexaoExistente = conexoes.find(c => c.origem === origem && c.destino === destinoStr);
                if (conexaoExistente && conexaoExistente.tipo === 'permitido') {
                  conexaoExistente.tipo = 'automacao';
                  conexaoExistente.label = automacao.nome;
                }
              }
            } else {
              console.log('  ⚠️ Ação de mover sem destino definido');
            }
          }
        });
      });
      
      console.log('📊 Total de conexões criadas:', conexoes.length);
      console.log('📊 Conexões:', conexoes);
      
      return conexoes;
    };

    const conexoes = criarMapaConexoes();
    
    // Calcular layout das etapas - Estilo Bizagi (horizontal sequencial)
    // Ordem oficial do fluxo (ordemFluxo). É a MESMA lista usada para posicionar as
    // etapas na tela e para numerá-las - se as duas coisas usarem ordens diferentes,
    // a etapa que aparece primeiro acaba exibindo um número fora de sequência.
    const etapasOrdenadas = [...etapas].sort((a, b) => {
      // Ordenar por ordemFluxo se existir
      if (a.ordemFluxo !== undefined && b.ordemFluxo !== undefined) {
        return a.ordemFluxo - b.ordemFluxo;
      }
      // Etapas com ordemFluxo vêm primeiro
      if (a.ordemFluxo !== undefined) return -1;
      if (b.ordemFluxo !== undefined) return 1;
      return 0;
    });

    const calcularLayout = () => {
      const layout: { [key: string]: { x: number; y: number } } = {};
      const cardWidth = 240;
      const cardHeight = 200;
      const espacamentoH = 100; // Espaço horizontal entre cards
      const espacamentoV = 80; // Espaço vertical entre linhas
      const maxPorLinha = 5; // Máximo de etapas por linha antes de fazer wrap

      // Layout horizontal em linhas - estilo Bizagi
      etapasOrdenadas.forEach((etapa, idx) => {
        const coluna = idx % maxPorLinha;
        const linha = Math.floor(idx / maxPorLinha);
        
        layout[String(etapa.id)] = {
          x: coluna * (cardWidth + espacamentoH),
          y: linha * (cardHeight + espacamentoV)
        };
      });
      
      return layout;
    };

    const layout = calcularLayout();
    
    // Funções de análise comentadas - não utilizadas no momento
    // const calcularCardsEmAtraso = (cardsNaEtapa: Card[], etapa: Etapa) => { ... }
    // const calcularTempoMedio = (cardsNaEtapa: Card[], etapaId: string) => { ... }
    
    // Agrupar etapas por departamento (Pools BPMN) - comentado, mantido para uso futuro
    /* const agruparPorDepartamento = () => {
      const grupos: Record<string, { departamento: any; etapas: Etapa[]; minY: number; maxY: number }> = {};
      
      etapas.forEach(etapa => {
        const deptId = etapa.departamentoId || 'sem-departamento';
        const dept = departamentos.find(d => d.id === deptId);
        
        if (!grupos[deptId]) {
          grupos[deptId] = {
            departamento: dept || { id: deptId, nome: 'Sem Departamento', cor: '#6B7280' },
            etapas: [],
            minY: Infinity,
            maxY: -Infinity
          };
        }
        
        grupos[deptId].etapas.push(etapa);
        
        // Calcular limites Y para o pool
        const pos = layout[String(etapa.id)];
        if (pos) {
          grupos[deptId].minY = Math.min(grupos[deptId].minY, pos.y);
          grupos[deptId].maxY = Math.max(grupos[deptId].maxY, pos.y);
        }
      });
      
      return Object.values(grupos);
    }; */
    
    // pools removido - não mais usado no layout Bizagi simplificado
    
    // Calcular dimensões totais do fluxograma
    const margemX = 150; // Aumentado para acomodar labels de pools
    const margemY = 100;
    const posicoes = Object.values(layout);
    const minX = Math.min(...posicoes.map(p => p.x)) - margemX;
    const maxX = Math.max(...posicoes.map(p => p.x)) + 300 + margemX;
    const minY = Math.min(...posicoes.map(p => p.y)) - margemY;
    const maxY = Math.max(...posicoes.map(p => p.y)) + 250 + margemY;
    
    const totalWidth = maxX - minX;
    const totalHeight = maxY - minY;
    
    // Handlers para zoom e pan (usando estados do componente principal)
    const handleZoomIn = () => setFluxoZoom(prev => Math.min(prev + 0.2, 3));
    const handleZoomOut = () => setFluxoZoom(prev => Math.max(prev - 0.2, 0.5));
    const handleResetZoom = () => { setFluxoZoom(1); setFluxoPan({ x: 0, y: 0 }); };
    
    const handleMouseDown = (e: React.MouseEvent) => {
      if (fluxoIsPanMode) {
        setFluxoIsDragging(true);
        setFluxoDragStart({ x: e.clientX - fluxoPan.x, y: e.clientY - fluxoPan.y });
      }
    };
    
    const handleMouseMove = (e: React.MouseEvent) => {
      if (fluxoIsDragging && fluxoIsPanMode) {
        setFluxoPan({
          x: e.clientX - fluxoDragStart.x,
          y: e.clientY - fluxoDragStart.y
        });
      }
    };
    
    const handleMouseUp = () => {
      setFluxoIsDragging(false);
    };
    
    const handleFullscreen = () => {
      const container = fluxoContainerRef.current?.parentElement;
      if (!container) return;
      
      if (!document.fullscreenElement) {
        container.requestFullscreen().catch(err => {
          console.error('Erro ao entrar em fullscreen:', err);
        });
      } else {
        document.exitFullscreen();
      }
    };
    
    return (
      <div className="flex-1 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50 relative">
        {/* Controles de Zoom e Navegação - compactos */}
        <div className="absolute top-2 right-2 z-[9999] flex flex-col gap-1">
          {/* Botão para limpar destaque */}
          {conexaoSelecionada && (
            <button
              onClick={() => setConexaoSelecionada(null)}
              className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded shadow text-xs font-medium"
              title="Limpar seleção de conexão"
            >
              🔗 Limpar
            </button>
          )}
          
          {etapaDestacada && (
            <div className="bg-white rounded shadow border border-gray-200 p-1.5 flex flex-col gap-1">
              <button
                onClick={() => setEtapaDestacada(null)}
                className="px-2 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded text-xs font-medium"
                title="Remover destaque"
              >
                ✨ Limpar
              </button>
              
              {/* Modos de filtro */}
              <div className="text-[10px] font-semibold text-gray-700 px-1 pt-1 border-t border-gray-200">Filtrar:</div>
              <button
                onClick={() => setModoFiltroConexao('todos')}
                className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                  modoFiltroConexao === 'todos'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title="Mostrar todas as conexões"
              >
                🔄 Todas
              </button>
              <button
                onClick={() => setModoFiltroConexao('destino')}
                className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                  modoFiltroConexao === 'destino'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title="Mostrar apenas para onde vai"
              >
                ➡️ Saídas
              </button>
              <button
                onClick={() => setModoFiltroConexao('origem')}
                className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                  modoFiltroConexao === 'origem'
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title="Mostrar apenas de onde vem"
              >
                ⬅️ Entradas
              </button>
            </div>
          )}
          
          <div className="bg-white rounded shadow border border-gray-200 p-1 flex flex-col gap-1">
            <button
              onClick={handleZoomIn}
              className="w-7 h-7 bg-blue-500 hover:bg-blue-600 text-white rounded font-bold text-sm flex items-center justify-center"
              title="Zoom In"
            >
              +
            </button>
            <button
              onClick={handleResetZoom}
              className="w-7 h-7 bg-gray-500 hover:bg-gray-600 text-white rounded text-[10px] flex items-center justify-center"
              title="Reset Zoom"
            >
              100%
            </button>
            <button
              onClick={handleZoomOut}
              className="w-7 h-7 bg-blue-500 hover:bg-blue-600 text-white rounded font-bold text-sm flex items-center justify-center"
              title="Zoom Out"
            >
              −
            </button>
          </div>
          
          <button
            onClick={() => setFluxoIsPanMode(!fluxoIsPanMode)}
            className={`w-7 h-7 rounded shadow border flex items-center justify-center text-sm ${
              fluxoIsPanMode 
                ? 'bg-green-500 hover:bg-green-600 text-white border-green-600' 
                : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200'
            }`}
            title={fluxoIsPanMode ? "Desabilitar modo pan" : "Habilitar modo pan (mão)"}
          >
            {fluxoIsPanMode ? '✋' : '👆'}
          </button>
          
          <button
            onClick={handleFullscreen}
            className="w-7 h-7 bg-purple-500 hover:bg-purple-600 text-white rounded shadow border border-purple-600 flex items-center justify-center text-sm"
            title="Modo tela cheia"
          >
            ⛶
          </button>
        </div>
        
        <div className="h-full flex flex-col">
          {etapas && etapas.length > 0 ? (
            <div 
              ref={fluxoContainerRef}
              className="flex-1 bg-white rounded-xl shadow-sm border overflow-auto"
              style={{ cursor: fluxoIsPanMode ? 'grab' : 'default' }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {/* Fluxograma estilo BPMN com Zoom e Pan */}
              <div 
                className="relative p-6"
                style={{ 
                  width: `${totalWidth}px`, 
                  minHeight: `${totalHeight}px`,
                  transform: `scale(${fluxoZoom}) translate(${fluxoPan.x / fluxoZoom}px, ${fluxoPan.y / fluxoZoom}px)`,
                  transformOrigin: '0 0',
                  transition: fluxoIsDragging ? 'none' : 'transform 0.2s ease-out'
                }}
              >
                {/* Pools removidos para layout mais limpo estilo Bizagi */}
                {/* SVG para as conexões */}
                <svg 
                  className="absolute inset-0 pointer-events-none"
                  style={{ width: '100%', height: '100%' }}
                >
                  <defs>
                    {/* Marcador de seta para conexões permitidas */}
                    <marker
                      id="arrowPermitido"
                      viewBox="0 0 10 10"
                      refX="9"
                      refY="5"
                      markerWidth="6"
                      markerHeight="6"
                      orient="auto-start-reverse"
                    >
                      <path d="M 0 0 L 10 5 L 0 10 z" fill="#3B82F6" />
                    </marker>
                    {/* Marcador de seta para automações */}
                    <marker
                      id="arrowAutomacao"
                      viewBox="0 0 10 10"
                      refX="9"
                      refY="5"
                      markerWidth="6"
                      markerHeight="6"
                      orient="auto-start-reverse"
                    >
                      <path d="M 0 0 L 10 5 L 0 10 z" fill="#8B5CF6" />
                    </marker>
                  </defs>
                  
                  {/* Desenhar conexões */}
                  {conexoes.map((conexao, idx) => {
                    const origem = layout[conexao.origem];
                    const destino = layout[conexao.destino];
                    
                    if (!origem || !destino) return null;
                    
                    // Buscar nomes das etapas
                    const etapaOrigem = etapas.find(e => String(e.id) === conexao.origem);
                    const etapaDestino = etapas.find(e => String(e.id) === conexao.destino);
                    
                    if (!etapaOrigem || !etapaDestino) return null;
                    
                    const cor = conexao.tipo === 'automacao' ? '#8B5CF6' : '#3B82F6';
                    const markerId = conexao.tipo === 'automacao' ? 'arrowAutomacao' : 'arrowPermitido';
                    const dasharray = conexao.tipo === 'automacao' ? '5,5' : 'none';
                    
                    // Dimensões do card para cálculo de pontos de saída/entrada
                    const cardWidth = 200;
                    const cardHeight = 80;
                    
                    // Posições ajustadas (centro do card)
                    const x1Center = origem.x - minX + cardWidth / 2;
                    const y1Center = origem.y - minY + cardHeight / 2;
                    const x2Center = destino.x - minX + cardWidth / 2;
                    const y2Center = destino.y - minY + cardHeight / 2;
                    
                    // Determinar direção e pontos de conexão ortogonais (estilo Bizagi)
                    let path = '';
                    let labelX = (x1Center + x2Center) / 2;
                    let labelY = (y1Center + y2Center) / 2;
                    
                    const mesmaLinha = Math.abs(y1Center - y2Center) < 50;
                    const mesmaColuna = Math.abs(x1Center - x2Center) < 50;
                    
                    if (mesmaLinha) {
                      // Conexão horizontal simples (próxima etapa na mesma linha)
                      const x1 = origem.x - minX + cardWidth + 5;
                      const y1 = origem.y - minY + cardHeight / 2;
                      const x2 = destino.x - minX - 5;
                      const y2 = destino.y - minY + cardHeight / 2;
                      path = `M ${x1} ${y1} L ${x2} ${y2}`;
                      labelX = (x1 + x2) / 2;
                      labelY = y1 - 15;
                    } else if (mesmaColuna) {
                      // Conexão vertical simples
                      const x1 = origem.x - minX + cardWidth / 2;
                      const y1 = origem.y - minY + cardHeight + 5;
                      const x2 = destino.x - minX + cardWidth / 2;
                      const y2 = destino.y - minY - 5;
                      path = `M ${x1} ${y1} L ${x2} ${y2}`;
                      labelX = x1 + 15;
                      labelY = (y1 + y2) / 2;
                    } else if (x2Center > x1Center && y2Center > y1Center) {
                      // Indo para direita e para baixo (layout em L)
                      const x1 = origem.x - minX + cardWidth + 5;
                      const y1 = origem.y - minY + cardHeight / 2;
                      const x2 = destino.x - minX + cardWidth / 2;
                      const y2 = destino.y - minY - 5;
                      const midX = x2;
                      path = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2}`;
                      labelX = midX + 15;
                      labelY = (y1 + y2) / 2;
                    } else if (x2Center < x1Center && y2Center > y1Center) {
                      // Indo para esquerda e para baixo (retorno - linha que sobe e desce)
                      const x1 = origem.x - minX - 5;
                      const y1 = origem.y - minY + cardHeight / 2;
                      const x2 = destino.x - minX + cardWidth / 2;
                      const y2 = destino.y - minY - 5;
                      const offset = 30;
                      path = `M ${x1} ${y1} L ${x1 - offset} ${y1} L ${x1 - offset} ${y2 - offset} L ${x2} ${y2 - offset} L ${x2} ${y2}`;
                      labelX = x1 - offset - 60;
                      labelY = (y1 + y2) / 2;
                    } else if (x2Center > x1Center && y2Center < y1Center) {
                      // Indo para direita e para cima
                      const x1 = origem.x - minX + cardWidth / 2;
                      const y1 = origem.y - minY - 5;
                      const x2 = destino.x - minX - 5;
                      const y2 = destino.y - minY + cardHeight / 2;
                      const midY = y1 - 30;
                      path = `M ${x1} ${y1} L ${x1} ${midY} L ${x2 + cardWidth + 5} ${midY} L ${x2 + cardWidth + 5} ${y2}`;
                      labelX = (x1 + x2) / 2;
                      labelY = midY - 15;
                    } else {
                      // Indo para esquerda e para cima (retorno)
                      const x1 = origem.x - minX - 5;
                      const y1 = origem.y - minY + cardHeight / 2;
                      const x2 = destino.x - minX + cardWidth + 5;
                      const y2 = destino.y - minY + cardHeight / 2;
                      const offset = 30;
                      path = `M ${x1} ${y1} L ${x1 - offset} ${y1} L ${x1 - offset} ${y2} L ${x2} ${y2}`;
                      labelX = x1 - offset - 60;
                      labelY = (y1 + y2) / 2;
                    }
                    
                    // Criar tooltip com descrição detalhada
                    const tooltipText = conexao.tipo === 'automacao' 
                      ? `${conexao.descricao || conexao.label || 'Automação'}`
                      : `Movimentação: ${etapaOrigem.nome} → ${etapaDestino.nome}`;
                    
                    // Verificar se esta conexão está relacionada à etapa destacada ou se é a conexão selecionada
                    let estaDestacada = false;
                    
                    // Se uma conexão específica foi selecionada, mostrar apenas ela
                    if (conexaoSelecionada) {
                      estaDestacada = conexao.origem === conexaoSelecionada.origem && conexao.destino === conexaoSelecionada.destino;
                    } else if (etapaDestacada) {
                      if (modoFiltroConexao === 'todos') {
                        estaDestacada = conexao.origem === etapaDestacada || conexao.destino === etapaDestacada;
                      } else if (modoFiltroConexao === 'destino') {
                        // Modo "Para onde vai" - mostrar apenas saídas
                        estaDestacada = conexao.origem === etapaDestacada;
                      } else if (modoFiltroConexao === 'origem') {
                        // Modo "De onde vem" - mostrar apenas entradas
                        estaDestacada = conexao.destino === etapaDestacada;
                      }
                    }
                    
                    const opacidade = (!etapaDestacada && !conexaoSelecionada) || estaDestacada ? 1 : 0.2;
                    
                    return (
                      <g key={`${conexao.origem}-${conexao.destino}-${idx}`}>
                        {/* Linha invisível mais grossa para facilitar hover e click */}
                        <path
                          d={path}
                          fill="none"
                          stroke="transparent"
                          strokeWidth="12"
                          className="pointer-events-auto cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Toggle: se já está selecionada, desseleciona
                            if (conexaoSelecionada?.origem === conexao.origem && conexaoSelecionada?.destino === conexao.destino) {
                              setConexaoSelecionada(null);
                            } else {
                              setConexaoSelecionada({ origem: conexao.origem, destino: conexao.destino });
                              // Limpar destaque de etapa ao selecionar conexão
                              setEtapaDestacada(null);
                            }
                          }}
                        >
                          <title>{tooltipText}</title>
                        </path>
                        
                        {/* Linha visível */}
                        <path
                          d={path}
                          fill="none"
                          stroke={cor}
                          strokeWidth="2"
                          strokeDasharray={dasharray}
                          markerEnd={`url(#${markerId})`}
                          opacity={opacidade}
                          className="transition-all pointer-events-none"
                        />
                        
                        {/* Label no meio da linha */}
                        <g className="pointer-events-auto" opacity={opacidade}>
                          {/* Fundo para o texto */}
                          <rect
                            x={labelX - 60}
                            y={labelY - 12}
                            width="120"
                            height="24"
                            rx="4"
                            fill="white"
                            stroke={cor}
                            strokeWidth="1"
                            opacity="0.95"
                            className="cursor-pointer"
                          >
                            <title>{tooltipText}</title>
                          </rect>
                          
                          {/* Texto descritivo da automação */}
                          <text
                            x={labelX}
                            y={labelY + 4}
                            fill="#374151"
                            fontSize="9"
                            fontWeight="600"
                            textAnchor="middle"
                            className="pointer-events-none"
                          >
                            {tooltipText.length > 20 ? tooltipText.substring(0, 18) + '...' : tooltipText}
                          </text>
                          
                          {/* Ícone indicando tipo de conexão */}
                          <text
                            x={labelX + 55}
                            y={labelY + 5}
                            fill={cor}
                            fontSize="12"
                            textAnchor="middle"
                            className="pointer-events-none"
                          >
                            {conexao.tipo === 'automacao' ? '⚡' : '➜'}
                          </text>
                        </g>
                      </g>
                    );
                  })}
                </svg>

                {/* Renderizar cards das etapas */}
                {etapas.map((etapa) => {
                  const cardsNaEtapa = cards.filter(c => String(c.etapaId) === String(etapa.id));
                  const totalCards = cards.length;
                  const percentual = totalCards > 0 ? Math.round((cardsNaEtapa.length / totalCards) * 100) : 0;
                  const departamento = departamentos.find(d => d.id === etapa.departamentoId);
                  const posicao = layout[String(etapa.id)];
                  
                  if (!posicao) return null;
                  
                  // Número da etapa: usa a MESMA ordem que posiciona os cards na tela
                  // (etapasOrdenadas / ordemFluxo). Antes vinha de `etapas`, que está na
                  // ordem crua do fluxo - por isso a 1ª etapa da tela podia exibir "10"
                  // e uma etapa posterior exibir "8".
                  const idxEtapa = etapasOrdenadas.findIndex(e => String(e.id) === String(etapa.id));
                  const nivelEtapa = idxEtapa + 1;
                  
                  // Participantes da etapa
                  const participantesEtapa = etapa.participantes?.map(pId => 
                    colaboradores.find(c => c.id === pId)
                  ).filter(Boolean) || [];

                  return (
                    <div 
                      key={etapa.id} 
                      className="absolute"
                      style={{
                        left: `${posicao.x - minX}px`,
                        top: `${posicao.y - minY}px`,
                        width: '200px'
                      }}
                    >
                      {/* Indicador de INÍCIO do processo - etapas que permitem criar cards */}
                      {etapa.permiteCriarCards && (
                        <div className="absolute -left-24 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
                          <div className="px-3 py-1.5 bg-gradient-to-br from-green-400 to-green-600 rounded-md flex items-center justify-center shadow-lg border border-white/60">
                            <span className="text-white text-xs font-bold tracking-wide">INÍCIO</span>
                          </div>
                          <div className="w-6 h-0.5 bg-green-500"></div>
                        </div>
                      )}
                      
                      {/* Número da Etapa (sequência) com cores diferentes */}
                      {(() => {
                        const cores = [
                          'from-red-500 to-red-600',
                          'from-blue-500 to-blue-600',
                          'from-green-500 to-green-600',
                          'from-yellow-500 to-yellow-600',
                          'from-purple-500 to-purple-600',
                          'from-pink-500 to-pink-600',
                          'from-indigo-500 to-indigo-600',
                          'from-teal-500 to-teal-600',
                          'from-orange-500 to-orange-600',
                          'from-cyan-500 to-cyan-600'
                        ];
                        const corIndex = (nivelEtapa - 1) % cores.length;
                        const estaDestacada = etapaDestacada === String(etapa.id);
                        
                        return (
                          <div 
                            className={`absolute -top-3 -left-3 w-8 h-8 bg-gradient-to-br ${cores[corIndex]} text-white rounded-full flex items-center justify-center text-xs font-bold shadow-lg z-20 border-2 border-white cursor-pointer hover:scale-110 transition-transform ${estaDestacada ? 'ring-4 ring-yellow-400 scale-110' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEtapaDestacada(estaDestacada ? null : String(etapa.id));
                            }}
                            title={estaDestacada ? "Clique para remover destaque" : "Clique para destacar esta etapa"}
                          >
                            {nivelEtapa}
                          </div>
                        );
                      })()}
                      
                      {/* Card da Etapa - Estilo BPMN */}
                      <div 
                        className={`relative bg-white rounded-2xl shadow-md border-3 border-gray-800 hover:shadow-xl transition-all cursor-pointer group ${etapaDestacada && etapaDestacada !== String(etapa.id) ? 'opacity-30' : 'opacity-100'}`}
                        style={{ minHeight: '80px' }}
                        onClick={() => { 
                          console.log('🔧 Abrindo cards da etapa:', etapa.nome);
                          setEtapaFluxoSelecionada(etapa);
                        }}
                      >
                        {/* Botão de Configuração */}
                        {(userPermissions?.canAdmin || checkEtapaEditPermission?.()) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfiguring(etapa);
                              onOpenEtapaConfig?.();
                            }}
                            className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded transition-colors opacity-0 group-hover:opacity-100 z-10"
                          >
                            <Cog6ToothIcon className="h-4 w-4 text-gray-600" />
                          </button>
                        )}

                        <div className="p-4">
                          {/* Nome da Etapa */}
                          <h3 className="text-gray-900 font-bold text-sm text-center leading-tight mb-3">
                            {etapa.nome || 'Sem nome'}
                          </h3>
                          
                          {/* Informações Compactas */}
                          <div className="flex items-center justify-center gap-3 text-xs">
                            {/* Quantidade de Cards */}
                            <div className="flex items-center gap-1" title={`${cardsNaEtapa.length} cartões nesta etapa`}>
                              <svg className="w-3.5 h-3.5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                              </svg>
                              <span className="font-semibold text-gray-700">{cardsNaEtapa.length}</span>
                            </div>
                            
                            {/* Percentual */}
                            {totalCards > 0 && (
                              <div className="text-gray-500">
                                {percentual}%
                              </div>
                            )}
                            
                            {/* Departamento */}
                            {departamento && (
                              <div className="flex items-center gap-1" title={departamento.nome}>
                                <svg className="w-3.5 h-3.5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>

                          {/* Avatares dos Participantes */}
                          {participantesEtapa.length > 0 && (
                            <div className="mt-3 flex items-center justify-center -space-x-2">
                              {participantesEtapa.slice(0, 4).map((participante: any, idx: number) => (
                                <div
                                  key={participante?.id || idx}
                                  className="relative inline-block"
                                  title={participante?.nome || 'Participante'}
                                >
                                  {participante?.foto ? (
                                    <img
                                      src={participante.foto}
                                      alt={participante.nome}
                                      className="w-6 h-6 rounded-full border-2 border-white object-cover"
                                    />
                                  ) : (
                                    <div className={`w-6 h-6 rounded-full border-2 border-white ${getAvatarColor(idx)} flex items-center justify-center text-white text-[10px] font-bold`}>
                                      {getInitials(participante?.nome || '??')}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {participantesEtapa.length > 4 && (
                                <div
                                  className="w-6 h-6 rounded-full border-2 border-white bg-gray-400 flex items-center justify-center text-white text-[9px] font-bold"
                                  title={`+${participantesEtapa.length - 4} participantes`}
                                >
                                  +{participantesEtapa.length - 4}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm border">
              <p className="text-gray-500 mb-4">Nenhuma etapa encontrada</p>
              {(userPermissions?.canAdmin) && (
                <button
                  onClick={addEtapa}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  + Criar primeira etapa
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Componente de Visualização Relatórios - Construtor de Relatórios Drag & Drop
  const renderRelatoriosView = () => {
    console.log('📊 Renderizando Construtor de Relatórios:', { cards: cards.length, etapas: etapas.length });
    
    // Verificações de segurança
    if (!etapas || etapas.length === 0) {
      return (
        <div className="flex-1 overflow-auto bg-gray-50 flex items-center justify-center p-8">
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
            </svg>
            <p className="text-gray-500 font-medium">Nenhuma etapa disponível</p>
            <p className="text-gray-400 text-sm mt-2">Crie etapas no fluxo para gerar relatórios</p>
          </div>
        </div>
      );
    }
    
    // Construir lista de todos os campos disponíveis organizados por etapa
    const camposPorEtapa = etapas.map(etapa => ({
      etapa: etapa,
      campos: [
        { id: `etapa_${etapa.id}_nome`, nome: 'Nome da Etapa', tipo: 'dimensao', etapaId: etapa.id },
        { id: `etapa_${etapa.id}_count`, nome: 'Contagem de Cards', tipo: 'medida', etapaId: etapa.id },
        ...(etapa.campos || []).map(campo => ({
          id: `campo_${etapa.id}_${campo.id}`,
          nome: campo.nome,
          tipo: ['texto', 'lista', 'checkbox', 'checkbox_single', 'checkbox_multi'].includes(campo.tipo) ? 'dimensao' : 'medida',
          tipoCampo: campo.tipo,
          etapaId: etapa.id,
          campoId: campo.id
        }))
      ]
    }));

    // 🏷️ Campos globais (válidos para qualquer card do fluxo) — ex.: Etiqueta
    const camposGlobais = etiquetasFlow.length > 0
      ? [{ id: 'etiqueta', nome: 'Etiqueta', tipo: 'dimensao' as const }]
      : [];

    // 🤖 Resultado do robô de lançamento de NF no GIX. O robô grava em
    // card.gixNfLancamento { ok, tipo, mensagem, em }. Os rótulos saem só do que
    // está gravado — nada de adivinhar pelo nome da etapa, que a pessoa move à mão.
    // "já constava lançada" é o robô confirmando que ALGUÉM já tinha lançado —
    // não é lançamento nosso. Misturar os dois num rótulo só ("Lançada") escondia
    // a diferença que mais importa para conferir o trabalho do robô.
    const gixJaConstava = (reg: any) => {
      const msg = String(reg?.mensagem || '');
      return msg.includes('já constava') || msg.includes('já estava');
    };

    const gixStatusDoCard = (card: Card): string => {
      const reg = (card as any).gixNfLancamento;
      if (!reg || typeof reg !== 'object') return 'Não processada pelo robô';
      if (reg.ok) return gixJaConstava(reg) ? 'Já estava lançada (robô só conferiu)' : 'Lançada pelo robô';
      if (reg.tipo === 'servico') return 'Nota de serviço — parada';
      return 'Não lançada';
    };
    const gixMotivoDoCard = (card: Card): string => {
      const reg = (card as any).gixNfLancamento;
      const msg = reg && typeof reg === 'object' ? String(reg.mensagem || '').trim() : '';
      return msg || '(sem registro do robô)';
    };
    // Valor de um campo do card procurando pelo NOME, em qualquer etapa do fluxo.
    // O dimensionamento normal (campo_<etapa>_<campo>) só resolve quando o card
    // está NAQUELA etapa, então num relatório do fluxo inteiro ele vem vazio para
    // quase todo mundo. Aqui é por nome, que é o que a pessoa enxerga.
    const norm = (t: any) => String(t ?? '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();

    const idsPorNome = (() => {
      const mapa = new Map<string, string[]>();
      (flow.etapas || []).forEach(et => (et.campos || []).forEach(c => {
        const chave = norm(c.nome);
        if (!mapa.has(chave)) mapa.set(chave, []);
        mapa.get(chave)!.push(String(c.id));
      }));
      return mapa;
    })();

    const valorPorNomeDeCampo = (card: Card, ...nomes: string[]): string => {
      for (const nome of nomes) {
        for (const [chave, ids] of idsPorNome) {
          if (!chave.includes(norm(nome))) continue;
          for (const id of ids) {
            const v = (card.campos || {})[id];
            if (v === undefined || v === null || v === '' ||
                (Array.isArray(v) && v.length === 0)) continue;
            const texto = Array.isArray(v) ? v.join(', ') : String(v);
            if (texto.trim()) return texto.trim();
          }
        }
      }
      return '';
    };

    const gixNfNumeroDoCard = (card: Card): string => {
      // 1º o número que a pessoa digitou no card — é por ele que se consulta o GIX
      const doCampo = valorPorNomeDeCampo(card, 'n nota fiscal', 'numero da nota fiscal');
      const so = doCampo.replace(/\D/g, '');
      if (so) return so;
      // 2º o número que o robô citou na mensagem, quando o card não tem o campo
      const reg = (card as any).gixNfLancamento;
      const msg = reg && typeof reg === 'object' ? String(reg.mensagem || '') : '';
      // "NFS-e 154 lançada" (serviço) e "NF 646 lançada" (mercadoria)
      const m = msg.match(/NFS?-?e?\s+(\d+)/i);
      return m ? m[1] : '(sem NF)';
    };

    const gixDataDoCard = (card: Card): string => {
      const reg = (card as any).gixNfLancamento;
      const em = reg && typeof reg === 'object' ? String(reg.em || '') : '';
      if (!em) return '(sem data)';
      const d = new Date(em);
      return isNaN(d.getTime()) ? '(sem data)' : d.toLocaleDateString('pt-BR');
    };

    // 📋 Campos do próprio card (válidos para qualquer card do fluxo) — ex.: ID do Card
    const camposCard = [
      { id: 'card_id', nome: 'ID do Card', tipo: 'dimensao' as const },
      { id: 'gix_nf_status', nome: '🤖 NF no GIX (situação)', tipo: 'dimensao' as const },
      { id: 'gix_nf_motivo', nome: '🤖 NF no GIX (motivo)', tipo: 'dimensao' as const },
      { id: 'gix_nf_data', nome: '🤖 NF no GIX (data)', tipo: 'dimensao' as const },
      { id: 'gix_nf_numero', nome: '🤖 Nº da NF', tipo: 'dimensao' as const },
      { id: 'card_titulo', nome: 'Título do Card', tipo: 'dimensao' as const },
      { id: 'card_fornecedor', nome: 'Fornecedor (card)', tipo: 'dimensao' as const },
      { id: 'card_oc', nome: 'Nº da OC (card)', tipo: 'dimensao' as const },
      { id: 'card_valor', nome: 'Valor do card', tipo: 'dimensao' as const },
    ];
    
    // Função para calcular dados da tabela dinâmica
    const calcularRelatorio = () => {
      if (dimensoesSelecionadas.length === 0 && medidasSelecionadas.length === 0) {
        return { linhas: [], colunas: [], colunasNomes: [], dados: [] };
      }
      
      // Agrupar cards por dimensões selecionadas
      const grupos: any = {};

      const passaFiltroGix = (card: Card) => {
        if (filtroGix === 'todos') return true;
        const reg = (card as any).gixNfLancamento;
        if (!reg || typeof reg !== 'object') return false;
        if (filtroGix === 'lancadas') return !!reg.ok && !gixJaConstava(reg);
        if (filtroGix === 'ja_constava') return !!reg.ok && gixJaConstava(reg);
        return !reg.ok;  // nao_lancadas
      };

      cards.filter(passaFiltroGix).forEach(card => {
        const etapa = etapas.find(e => String(e.id) === String(card.etapaId));
        if (!etapa) return;
        
        let chave = '';
        const valores: any = {};
        
        // Construir chave baseada nas dimensões
        dimensoesSelecionadas.forEach(dimId => {
          if (dimId.startsWith('etapa_') && dimId.endsWith('_nome')) {
            valores[dimId] = etapa.nome;
            chave += `${etapa.nome}|`;
          } else if (dimId === 'card_id') {
            const cardId = String(card.id || '');
            valores[dimId] = cardId;
            chave += `${cardId}|`;
          } else if (dimId === 'gix_nf_numero') {
            const label = gixNfNumeroDoCard(card);
            valores[dimId] = label;
            chave += `${label}|`;
          } else if (dimId === 'card_titulo') {
            const label = String(card.titulo || '(sem título)');
            valores[dimId] = label;
            chave += `${label}|`;
          } else if (dimId === 'card_fornecedor') {
            const label = valorPorNomeDeCampo(card, 'fornecedor') || '(sem fornecedor)';
            valores[dimId] = label;
            chave += `${label}|`;
          } else if (dimId === 'card_oc') {
            const label = valorPorNomeDeCampo(card, 'ordem de compra') || '(sem OC)';
            valores[dimId] = label;
            chave += `${label}|`;
          } else if (dimId === 'card_valor') {
            const label = valorPorNomeDeCampo(card, 'valor medio', 'valor total') || '-';
            valores[dimId] = label;
            chave += `${label}|`;
          } else if (dimId === 'gix_nf_status') {
            const label = gixStatusDoCard(card);
            valores[dimId] = label;
            chave += `${label}|`;
          } else if (dimId === 'gix_nf_motivo') {
            const label = gixMotivoDoCard(card);
            valores[dimId] = label;
            chave += `${label}|`;
          } else if (dimId === 'gix_nf_data') {
            const label = gixDataDoCard(card);
            valores[dimId] = label;
            chave += `${label}|`;
          } else if (dimId === 'etiqueta') {
            const nomes = (card.etiquetasIds || [])
              .map(eid => etiquetasFlow.find(et => et.id === eid)?.nome)
              .filter((n): n is string => !!n)
              .sort();
            const label = nomes.length > 0 ? nomes.join(', ') : '(sem etiqueta)';
            valores[dimId] = label;
            chave += `${label}|`;
          } else if (dimId.startsWith('campo_')) {
            const [, etapaId, campoId] = dimId.split('_');
            if (String(etapaId) === String(etapa.id)) {
              const valor = card.campos?.[campoId] || '-';
              valores[dimId] = Array.isArray(valor) ? valor.join(', ') : valor;
              chave += `${valor}|`;
            }
          }
        });
        
        if (!grupos[chave]) {
          grupos[chave] = { valores, count: 0, cards: [] };
        }
        grupos[chave].count++;
        grupos[chave].cards.push(card);
      });
      
      // Converter para array de linhas
      const linhas = Object.values(grupos).map((grupo: any) => {
        const linha: any = { ...grupo.valores };
        
        // Calcular medidas
        medidasSelecionadas.forEach(medId => {
          if (medId.includes('_count')) {
            linha[medId] = grupo.count;
          } else if (medId.startsWith('campo_')) {
            // Calcular soma/média para campos numéricos
            const parts = medId.split('_');
            const campoId = parts[2];
            const valores = grupo.cards
              .map((card: Card) => {
                const valor = card.campos?.[campoId];
                // Tentar converter para número
                const numero = typeof valor === 'number' ? valor : parseFloat(String(valor).replace(/[^\d.,-]/g, '').replace(',', '.'));
                return isNaN(numero) ? 0 : numero;
              })
              .filter((v: number) => v !== 0);
            
            if (valores.length > 0) {
              const soma = valores.reduce((acc: number, val: number) => acc + val, 0);
              const media = soma / valores.length;
              // Exibir a média formatada
              linha[medId] = media.toFixed(2);
            } else {
              linha[medId] = 0;
            }
          }
        });
        
        return linha;
      });
      
      // Mapear IDs para nomes de campos
      const colunasIds = [...dimensoesSelecionadas, ...medidasSelecionadas];
      const colunasComNomes = colunasIds.map(id => {
      const campo = [...camposPorEtapa.flatMap(e => e.campos), ...camposGlobais, ...camposCard].find(c => c.id === id);
        return campo?.nome || id;
      });
      
      return { 
        linhas, 
        colunas: colunasIds, 
        colunasNomes: colunasComNomes 
      };
    };

    // Funções de exportação
    const exportarCSV = () => {
      const resultado = calcularRelatorio();
      if (resultado.linhas.length === 0) return;
      
      const csv = [
        (resultado.colunasNomes || resultado.colunas).join(','),
        ...resultado.linhas.map(linha => 
          resultado.colunas.map(col => {
            const valor = (linha as any)[col] || '';
            return `"${String(valor).replace(/"/g, '""')}"`;
          }).join(',')
        )
      ].join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `relatorio_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    };

    const exportarExcel = () => {
      const resultado = calcularRelatorio();
      if (resultado.linhas.length === 0) return;
      
      // Criar HTML table para Excel
      let html = '<table><thead><tr>';
      (resultado.colunasNomes || resultado.colunas).forEach(col => {
        html += `<th>${col}</th>`;
      });
      html += '</tr></thead><tbody>';
      
      resultado.linhas.forEach(linha => {
        html += '<tr>';
        resultado.colunas.forEach(col => {
          html += `<td>${(linha as any)[col] || ''}</td>`;
        });
        html += '</tr>';
      });
      html += '</tbody></table>';
      
      const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `relatorio_${new Date().toISOString().split('T')[0]}.xls`;
      link.click();
    };

    const exportarPDF = () => {
      window.print();
    };

    // Funções para salvar/carregar relatórios (MongoDB)
    const salvarRelatorio = async () => {
      if (!nomeRelatorioSalvar.trim()) {
        alert('Por favor, digite um nome para o relatório');
        return;
      }
      
      if (!flow.empresaId || !userPermissions?.currentUser?.id) {
        alert('Erro: Informações de empresa ou usuário não disponíveis');
        return;
      }
      
      try {
        setSalvandoRelatorio(true);
        const novoRelatorio = {
          nome: nomeRelatorioSalvar,
          dimensoes: [...dimensoesSelecionadas],
          medidas: [...medidasSelecionadas],
          // sem isto, abrir o relatório salvo traz o fluxo inteiro de volta e a
          // visão "só as que o robô lançou" se perde
          filtroGix,
          empresaId: flow.empresaId,
          usuarioId: userPermissions.currentUser.id,
          flowId: flow.id,
          dataCriacao: new Date().toISOString()
        };
        
        console.log('📤 Enviando relatório para salvar:', novoRelatorio);
        
        const response = await fetch('/api/relatorios', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(novoRelatorio)
        });
        
        console.log('📥 Resposta do servidor:', response.status, response.statusText);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ Erro do servidor:', errorText);
          throw new Error(`Erro ${response.status}: ${errorText}`);
        }
        
        const relatorioSalvo = await response.json();
        console.log('✅ Relatório salvo com sucesso:', relatorioSalvo);
        setRelatoriosSalvos([...relatoriosSalvos, relatorioSalvo]);
        
        setMostrarModalSalvar(false);
        setNomeRelatorioSalvar('');
        alert('Relatório salvo com sucesso! ✅');
      } catch (error: any) {
        console.error('❌ Erro completo ao salvar relatório:', error);
        const errorMessage = error?.message || 'Erro desconhecido';
        alert(`❌ Erro ao salvar relatório: ${errorMessage}`);
      } finally {
        setSalvandoRelatorio(false);
      }
    };

    const carregarRelatorio = (relatorio: typeof relatoriosSalvos[0]) => {
      setDimensoesSelecionadas([...relatorio.dimensoes]);
      setMedidasSelecionadas([...relatorio.medidas]);
      setFiltroGix((relatorio.filtroGix as any) || 'todos');
      setMostrarRelatoriosSalvos(false);
    };

    const excluirRelatorio = async (id: string, _id?: string) => {
      if (!confirm('Deseja realmente excluir este relatório?')) return;
      
      try {
        const response = await fetch(`/api/relatorios/${_id || id}`, {
          method: 'DELETE'
        });
        
        if (!response.ok) throw new Error('Erro ao excluir relatório');
        
        setRelatoriosSalvos(relatoriosSalvos.filter(r => r.id !== id && r._id !== _id));
        alert('Relatório excluído com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir relatório:', error);
        alert('Erro ao excluir relatório. Tente novamente.');
      }
    };

    return (
      <div className="flex-1 overflow-auto bg-gray-50">
        <div className="p-4 md:p-6">
          {/* Header com Controles */}
          <div className="mb-6 bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Tabela Dinâmica</h2>
                  <p className="text-sm text-gray-600">Arraste campos para Linhas e Valores</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 flex-wrap">
                {/* Botão Mostrar/Ocultar Campos */}
                <button
                  onClick={() => setMostrarCamposRelatorio(!mostrarCamposRelatorio)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    {mostrarCamposRelatorio ? (
                      <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                    ) : (
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    )}
                  </svg>
                  {mostrarCamposRelatorio ? 'Ocultar Campos' : 'Mostrar Campos'}
                </button>

                {/* 🤖 Filtro por situação do lançamento de NF */}
                <select
                  value={filtroGix}
                  onChange={(e) => setFiltroGix(e.target.value as any)}
                  title="Filtra os cards antes de montar a tabela"
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    filtroGix === 'todos'
                      ? 'bg-white text-gray-700 border-gray-300'
                      : 'bg-indigo-50 text-indigo-700 border-indigo-300'
                  }`}
                >
                  <option value="todos">Todos os cards</option>
                  <option value="lancadas">🤖 Só NFs lançadas pelo robô</option>
                  <option value="ja_constava">Só as que já constavam lançadas</option>
                  <option value="nao_lancadas">Só as não lançadas</option>
                </select>

                {/* Botão Relatórios Salvos */}
                <button
                  onClick={() => setMostrarRelatoriosSalvos(!mostrarRelatoriosSalvos)}
                  className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                  </svg>
                  Relatórios Salvos ({relatoriosSalvos.length})
                </button>

                {/* Botão Salvar Relatório */}
                {(dimensoesSelecionadas.length > 0 || medidasSelecionadas.length > 0) && (
                  <button
                    onClick={() => setMostrarModalSalvar(true)}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
                    </svg>
                    Salvar
                  </button>
                )}

                {/* Botões de Exportação */}
                {dimensoesSelecionadas.length > 0 && medidasSelecionadas.length > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={exportarCSV}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors flex items-center gap-2"
                      title="Exportar CSV"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      CSV
                    </button>
                    <button
                      onClick={exportarExcel}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
                      title="Exportar Excel"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      Excel
                    </button>
                    <button
                      onClick={exportarPDF}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
                      title="Imprimir/PDF"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd" />
                      </svg>
                      PDF
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Painel de Relatórios Salvos */}
          {mostrarRelatoriosSalvos && (
            <div className="mb-6 bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Relatórios Salvos</h3>
                <button
                  onClick={() => setMostrarRelatoriosSalvos(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              
              {carregandoRelatorios ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600"></div>
                  <p className="text-gray-500 mt-2">Carregando relatórios...</p>
                </div>
              ) : relatoriosSalvos.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                  </svg>
                  <p className="text-gray-500 font-medium">Nenhum relatório salvo ainda</p>
                  <p className="text-gray-400 text-sm mt-1">Crie um relatório e clique em "Salvar" para salvá-lo</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {relatoriosSalvos.map(rel => (
                    <div key={rel._id || rel.id} className="border rounded-lg p-4 hover:border-blue-500 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-gray-900 flex-1">{rel.nome}</h4>
                        <button
                          onClick={() => excluirRelatorio(rel.id, rel._id)}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        {new Date(rel.dataCriacao).toLocaleDateString('pt-BR')}
                      </p>
                      <p className="text-xs text-gray-600 mb-2">
                        📋 {rel.dimensoes.length} linhas • {rel.medidas.length} colunas
                      </p>
                      <button
                        onClick={() => carregarRelatorio(rel)}
                        className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
                      >
                        Carregar
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Layout Drag & Drop */}
          <div className={`grid gap-6 ${mostrarCamposRelatorio ? 'grid-cols-1 lg:grid-cols-4' : 'grid-cols-1'}`}>
            {/* Painel de Campos Disponíveis */}
            {mostrarCamposRelatorio && (
              <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
                <h3 className="font-semibold text-gray-900">Campos</h3>
              </div>
              
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {camposCard.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded">
                      📋 Card
                    </div>
                    {camposCard.map(campo => (
                      <div
                        key={campo.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('campo', JSON.stringify(campo));
                        }}
                        className="p-2 bg-gray-50 rounded cursor-move hover:bg-blue-50 transition-colors border border-gray-200 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                        </svg>
                        <span className="text-sm text-gray-900">{campo.nome}</span>
                      </div>
                    ))}
                  </div>
                )}
                {camposGlobais.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded">
                      🏷️ Etiquetas
                    </div>
                    {camposGlobais.map(campo => (
                      <div
                        key={campo.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('campo', JSON.stringify(campo));
                        }}
                        className="p-2 bg-gray-50 rounded cursor-move hover:bg-blue-50 transition-colors border border-gray-200 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                        </svg>
                        <span className="text-sm text-gray-900">{campo.nome}</span>
                      </div>
                    ))}
                  </div>
                )}
                {camposPorEtapa.map(({ etapa, campos }) => (
                  <div key={etapa.id} className="space-y-2">
                    <div className="text-sm font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded">
                      {etapa.nome}
                    </div>
                    {campos.map(campo => (
                      <div
                        key={campo.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('campo', JSON.stringify(campo));
                        }}
                        className="p-2 bg-gray-50 rounded cursor-move hover:bg-blue-50 transition-colors border border-gray-200 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                        </svg>
                        <span className="text-sm text-gray-900">{campo.nome}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* Área de LINHAS e VALORES (Drop Zones) */}
            <div className={mostrarCamposRelatorio ? 'lg:col-span-3 space-y-4' : 'space-y-4'}>
              <div
                className="bg-white rounded-xl shadow-sm border-2 border-dashed border-blue-300 p-6 min-h-[120px]"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const campo = JSON.parse(e.dataTransfer.getData('campo'));
                  if (!dimensoesSelecionadas.includes(campo.id)) {
                    setDimensoesSelecionadas([...dimensoesSelecionadas, campo.id]);
                  }
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                  <h3 className="font-semibold text-gray-900">Linhas</h3>
                  <span className="text-xs text-gray-500">(arraste campos para agrupar)</span>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {dimensoesSelecionadas.length === 0 ? (
                    <p className="text-gray-400 text-sm italic">Arraste qualquer campo para agrupar em linhas...</p>
                  ) : (
                    dimensoesSelecionadas.map((dimId) => {
                      const campo = [...camposPorEtapa.flatMap(e => e.campos), ...camposGlobais, ...camposCard]
                        .find(c => c.id === dimId);
                      return (
                        <div key={dimId} className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg">
                          <span className="text-sm font-medium">{campo?.nome}</span>
                          <button
                            onClick={() => setDimensoesSelecionadas(dimensoesSelecionadas.filter(d => d !== dimId))}
                            className="hover:bg-blue-200 rounded p-0.5"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Área de COLUNAS (Drop Zone) */}
              <div
                className="bg-white rounded-xl shadow-sm border-2 border-dashed border-purple-300 p-6 min-h-[120px]"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const campo = JSON.parse(e.dataTransfer.getData('campo'));
                  if (!medidasSelecionadas.includes(campo.id)) {
                    setMedidasSelecionadas([...medidasSelecionadas, campo.id]);
                  }
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                  <h3 className="font-semibold text-gray-900">Colunas</h3>
                  <span className="text-xs text-gray-500">(arraste campos para exibir)</span>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {medidasSelecionadas.length === 0 ? (
                    <p className="text-gray-400 text-sm italic">Arraste qualquer campo para exibir como coluna...</p>
                  ) : (
                    medidasSelecionadas.map((medId) => {
                      const campo = [...camposPorEtapa.flatMap(e => e.campos), ...camposGlobais, ...camposCard]
                        .find(c => c.id === medId);
                      return (
                        <div key={medId} className="flex items-center gap-2 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg">
                          <span className="text-sm font-medium">{campo?.nome}</span>
                          <button
                            onClick={() => setMedidasSelecionadas(medidasSelecionadas.filter(m => m !== medId))}
                            className="hover:bg-purple-200 rounded p-0.5"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabela Dinâmica de Resultados */}
          {dimensoesSelecionadas.length > 0 && medidasSelecionadas.length > 0 && (
            <div className="mt-6 bg-white rounded-xl shadow-sm border p-6 print:shadow-none print:border-0">
              <style>{`
                @media print {
                  body * {
                    visibility: hidden;
                  }
                  .print\\:shadow-none, .print\\:shadow-none * {
                    visibility: visible;
                  }
                  .print\\:shadow-none {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                  }
                  table {
                    page-break-inside: auto;
                  }
                  tr {
                    page-break-inside: avoid;
                    page-break-after: auto;
                  }
                }
              `}</style>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Resultado ({(() => {
                  try {
                    const res = calcularRelatorio();
                    return res.linhas.length;
                  } catch (error) {
                    console.error('Erro ao calcular relatório para contagem:', error);
                    return 0;
                  }
                })()} registros)</h3>
                <div className="text-sm text-gray-500 print:block hidden">
                  Gerado em: {new Date().toLocaleString('pt-BR')}
                </div>
              </div>
              <div className="overflow-x-auto">
                {(() => {
                  try {
                    const resultado = calcularRelatorio();
                    if (resultado.linhas.length === 0) {
                      return (
                        <div className="text-center py-8 text-gray-500">
                          <p>Nenhum dado disponível para os campos selecionados</p>
                      </div>
                    );
                  }
                  
                  return (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {(resultado.colunasNomes || resultado.colunas).map((col, idx) => (
                            <th 
                              key={idx}
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {resultado.linhas.map((linha, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            {Object.values(linha).map((valor, vIdx) => (
                              <td key={vIdx} className="px-4 py-3 text-sm text-gray-900">
                                {String(valor ?? '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                  } catch (error) {
                    console.error('Erro ao renderizar relatório:', error);
                    return (
                      <div className="text-center py-8 text-red-500">
                        <p>Erro ao gerar relatório</p>
                        <p className="text-sm mt-2 text-gray-500">Verifique os campos selecionados e tente novamente</p>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          )}

          {/* Modal Salvar Relatório */}
          {mostrarModalSalvar && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">Salvar Relatório</h3>
                  <button
                    onClick={() => {
                      setMostrarModalSalvar(false);
                      setNomeRelatorioSalvar('');
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome do Relatório
                  </label>
                  <input
                    type="text"
                    value={nomeRelatorioSalvar}
                    onChange={(e) => setNomeRelatorioSalvar(e.target.value)}
                    placeholder="Ex: Vendas por Região"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                </div>

                <div className="mb-6 text-sm text-gray-600">
                  <p className="mb-1">� {dimensoesSelecionadas.length} campos para linhas</p>
                  <p>� {medidasSelecionadas.length} campos para colunas</p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setMostrarModalSalvar(false);
                      setNomeRelatorioSalvar('');
                    }}
                    disabled={salvandoRelatorio}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={salvarRelatorio}
                    disabled={!nomeRelatorioSalvar.trim() || salvandoRelatorio}
                    className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {salvandoRelatorio ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Salvando...
                      </>
                    ) : (
                      'Salvar'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Componente de Configurador de Hierarquia de Cards
  const renderHierarquiaView = () => {
    console.log('🎴 Renderizando Configurador de Hierarquia');
    
    return (
      <div className="h-full overflow-y-auto p-4">
        <div className="max-w-4xl mx-auto space-y-3">
          {/* Header compacto */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Hierarquia de Cards</h2>
              <p className="text-xs text-gray-500">Configure o sistema pai/filho para cada etapa</p>
            </div>
            <span className="text-[10px] text-gray-400">{etapas.length} etapa(s)</span>
          </div>

          {/* Lista de Etapas */}
          {etapas.length === 0 ? (
            <div className="bg-white/80 backdrop-blur-md rounded-lg border border-gray-200/50 p-4 text-center">
              <p className="text-xs text-gray-500">Nenhuma etapa encontrada. Crie etapas no fluxo para configurar a hierarquia.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {etapas.map((etapa) => {
                const config = etapa.configuracaoHierarquia;
                const permitePai = config?.permitirCardsPai || false;
                const aceitaFilhos = config?.regraAgrupamento?.aceitaFilhos || false;
                
                return (
                  <div key={etapa.id} className="bg-white/80 backdrop-blur-md rounded-lg border border-gray-200/50 overflow-hidden">
                    {/* Header da Etapa */}
                    <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">{etapa.nome}</h3>
                          <p className="text-[10px] text-gray-400">
                            {obterCardsRaiz(cards.filter(c => c.etapaId === etapa.id)).length} card(s)
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1.5">
                        {permitePai && (
                          <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded text-[10px] font-medium">
                            Cards Pai
                          </span>
                        )}
                        {aceitaFilhos && (
                          <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded text-[10px] font-medium">
                            Aceita Filhos
                          </span>
                        )}
                        {(userPermissions?.canAdmin || checkEtapaEditPermission?.()) && (
                          <button
                            onClick={() => setConfiguringHierarquia(etapa)}
                            className="ml-1 px-2.5 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-[10px] font-medium flex items-center gap-1"
                          >
                            <Cog6ToothIcon className="h-3 w-3" />
                            Configurar
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Conteúdo da Etapa */}
                    <div className="px-4 py-2.5">
                      {!permitePai ? (
                        <p className="text-[10px] text-gray-400 italic">
                          Hierarquia desativada — clique em "Configurar" para ativar.
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px]">
                          <div className="flex items-center gap-1.5">
                            <div className="w-1 h-1 bg-indigo-500 rounded-full"></div>
                            <span className="text-gray-600">Permite Cards Pai</span>
                          </div>
                          {config?.visualCardPai?.mostrarContagemFilhos && (
                            <div className="flex items-center gap-1.5">
                              <div className="w-1 h-1 bg-indigo-500 rounded-full"></div>
                              <span className="text-gray-600">Mostra contagem de filhos</span>
                            </div>
                          )}
                          {aceitaFilhos && (
                            <div className="flex items-center gap-1.5">
                              <div className="w-1 h-1 bg-emerald-500 rounded-full"></div>
                              <span className="text-gray-600">Aceita filhos</span>
                            </div>
                          )}
                          {config?.regraAgrupamento?.limiteFilhos && config.regraAgrupamento.limiteFilhos > 0 && (
                            <div className="flex items-center gap-1.5">
                              <div className="w-1 h-1 bg-amber-500 rounded-full"></div>
                              <span className="text-gray-600">Limite: {config.regraAgrupamento.limiteFilhos}</span>
                            </div>
                          )}
                          {config?.regraAgrupamento?.etapasProibidasParaFilhos && 
                           config.regraAgrupamento.etapasProibidasParaFilhos.length > 0 && (
                            <div className="flex items-center gap-1.5">
                              <div className="w-1 h-1 bg-red-500 rounded-full"></div>
                              <span className="text-gray-600">{config.regraAgrupamento.etapasProibidasParaFilhos.length} etapa(s) bloqueadas</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // 🧬 Índice de filhos por card pai — evita varrer todos os cards a cada card filtrado
  const filhosPorPai = useMemo(() => {
    const porId = new Map<string, Card>();
    cards.forEach(c => porId.set(String(c.id), c));

    const mapa = new Map<string, Card[]>();
    const vincular = (paiId: string, filho: Card) => {
      const atuais = mapa.get(paiId);
      if (!atuais) {
        mapa.set(paiId, [filho]);
        return;
      }
      if (!atuais.some(f => String(f.id) === String(filho.id))) atuais.push(filho);
    };

    // Vínculo declarado pelo filho
    cards.forEach(c => {
      if (c.cardPaiId) vincular(String(c.cardPaiId), c);
    });
    // Vínculo declarado pelo pai (cobre filhos com cardPaiId ausente/dessincronizado)
    cards.forEach(c => {
      (c.cardsFilhosIds || []).forEach(fid => {
        const filho = porId.get(String(fid));
        if (filho) vincular(String(c.id), filho);
      });
    });

    return mapa;
  }, [cards]);

  // 🔎 Verifica se UM card casa com o termo de pesquisa
  // Busca em: ID completo, shortCode, título, responsável (nome/email) e valores dos campos
  const cardCasaComTermo = (card: Card, termo: string, termoUpperCase: string): boolean => {
    // 1️⃣ Busca pelo ID completo do card (ex: "1766750351768")
    if (card.id?.toLowerCase().includes(termo)) return true;

    // 2️⃣ Busca pelo shortCode - ID curto mostrado na interface (ex: "A1B2C3")
    const shortCode = generateShortCode(card.id);
    if (shortCode.includes(termoUpperCase)) return true;

    // 2️⃣.1 Busca pelo protocolo do portal (ex: "P202607311234") - é o código que o
    // solicitante externo recebe; quem atende cola aqui para achar o card no fluxo.
    // Cards novos já têm o protocolo no documento; os antigos vêm do mapa do backend.
    const protocolo = (card as any).protocolo || protocoloByCard[String(card.id)];
    if (protocolo && String(protocolo).toUpperCase().includes(termoUpperCase)) return true;

    // 3️⃣ Buscar no título
    if (card.titulo?.toLowerCase().includes(termo)) return true;

    // 4️⃣ Buscar pelo responsável atribuído (nome ou email)
    if (card.responsavelIndividualId) {
      const responsavel = colaboradores.find(c => String(c.id) === String(card.responsavelIndividualId));
      if (responsavel) {
        if (responsavel.nome?.toLowerCase().includes(termo)) return true;
        if (responsavel.email?.toLowerCase().includes(termo)) return true;
      }
    }

    // 5️⃣ Buscar em todos os valores dos campos do card
    if (card.campos) {
      for (const [, valor] of Object.entries(card.campos)) {
        if (valor === null || valor === undefined) continue;

        // Converter valor para string pesquisável
        let textoParaBuscar = '';

        if (Array.isArray(valor)) {
          // Array (checkbox, anexos, etc) - juntar todos os valores
          textoParaBuscar = valor.map(v => {
            if (typeof v === 'object' && v !== null) {
              return v.name || v.fileName || v.originalName || v.nome || '';
            }
            return String(v);
          }).join(' ');
        } else if (typeof valor === 'object' && valor !== null) {
          // Objeto único - extrair texto útil
          textoParaBuscar = valor.name || valor.fileName || valor.originalName || JSON.stringify(valor);
        } else {
          // Valor simples (string, number, boolean)
          textoParaBuscar = String(valor);
        }

        if (textoParaBuscar.toLowerCase().includes(termo)) return true;
      }
    }

    return false;
  };

  // 🔍 Função para filtrar cards baseado no termo de pesquisa
  const filtrarCards = (cardsParaFiltrar: Card[], etapaId: string) => {
    let resultado = cardsParaFiltrar;
    
    // 🧬 Filtrar por tipo de card (pais, conectados, filhos, normais)
    if (filtroTipoCard !== 'todos') {
      resultado = resultado.filter(card => {
        const isPai = !!card.ehCardPai;
        const isConectado = !!card.isCardConectado;
        const isFilho = !!card.cardPaiId && !isPai;
        if (filtroTipoCard === 'pais') return isPai;
        if (filtroTipoCard === 'conectados') return isConectado;
        if (filtroTipoCard === 'filhos') return isFilho;
        if (filtroTipoCard === 'normais') return !isPai && !isConectado && !isFilho;
        if (filtroTipoCard === 'comChat') return chatByCard[String(card.id)] > 0;
        return true;
      });
    }
    
    // 🎯 Filtrar por responsável individual (se houver filtro ativo para esta etapa)
    const responsavelFiltroId = filtroResponsavel.get(etapaId);
    if (responsavelFiltroId) {
      resultado = resultado.filter(card => 
        String(card.responsavelIndividualId) === responsavelFiltroId
      );
    }

    // 🏷️ Filtrar por etiquetas (card deve ter ao menos uma das etiquetas selecionadas)
    if (filtroEtiquetas.length > 0) {
      resultado = resultado.filter(card =>
        (card.etiquetasIds || []).some(id => filtroEtiquetas.includes(id))
      );
    }
    
    // 🔎 Filtrar por termo de pesquisa
    if (!termoPesquisa.trim()) return resultado;
    
    const termo = termoPesquisa.toLowerCase().trim();
    const termoUpperCase = termoPesquisa.toUpperCase().trim();
    
    return resultado.filter(card => {
      // O próprio card casa com o termo
      if (cardCasaComTermo(card, termo, termoUpperCase)) return true;

      // 🧬 Ou um dos FILHOS casa. O Kanban esconde cards filhos da coluna
      // (obterCardsRaiz), então promovemos o PAI para o resultado: buscar pelo
      // código de um filho (ex: "CR5ZPNA") traz o pai, que pode ser aberto para
      // chegar até ele. O pai costuma estar em outra etapa que o filho.
      const filhos = filhosPorPai.get(String(card.id));
      if (filhos?.some(filho => cardCasaComTermo(filho, termo, termoUpperCase))) return true;

      return false;
    });
  };

  // Função para renderizar o conteúdo baseado no modo de visualização
  const renderContent = () => {
    switch (viewMode) {
      case 'lista':
        return renderListaView();
      case 'fluxo':
        return renderFluxoView();
      case 'relatorios':
        return renderRelatoriosView();
      case 'documentos':
        return <Documentos flow={flow} onClose={() => setViewMode('kanban')} userPermissions={userPermissions} />;
      case 'portais':
        return <Portais flow={flow} onClose={() => setViewMode('kanban')} userPermissions={userPermissions} permissoesGranulares={permissoesGranulares} colaboradores={colaboradores} />;
      case 'hierarquia':
        return renderHierarquiaView();
      case 'qualidade':
        // 📊 qualidade das aprovações (só em fluxo com campo de lista "Decisão …")
        if (!fluxoTemQualidade(flow)) return renderKanbanView();
        return (
          <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-gray-400">Carregando…</div>}>
            <QualidadeFluxo flow={flow} cards={cards} colaboradores={colaboradores} onAbrirCard={(card) => setCardSelecionado(card)} />
          </Suspense>
        );
      case 'verbas':
        // 💰 verbas e NDs de fornecedor (só em fluxo com 'Fornecedor' e 'Valor da ND')
        if (!fluxoTemVerbas(flow)) return renderKanbanView();
        return (
          <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-gray-400">Carregando…</div>}>
            <VerbasFluxo flow={flow} cards={cards} onAbrirCard={(card) => setCardSelecionado(card)} />
          </Suspense>
        );
      case 'atas':
        // 📋 atas de reunião / plano de ação (só em fluxo com 'Vezes na ata' e 'Reunião de origem')
        if (!fluxoTemAtas(flow)) return renderKanbanView();
        return (
          <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-gray-400">Carregando…</div>}>
            <AtasFluxo flow={flow} cards={cards} colaboradores={colaboradores} currentUser={userPermissions?.currentUser as any}
              onAbrirCard={(card) => setCardSelecionado(card)} onCardSalvo={atasCardSalvo} />
          </Suspense>
        );
      case 'indicadoresTI':
        // 🖥️ indicadores dos chamados de TI (só em fluxo "Chamados…" com Categoria e Prioridade) — 04/11/2026
        if (!fluxoTemChamadosTI(flow)) return renderKanbanView();
        return (
          <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-gray-400">Carregando…</div>}>
            <ChamadosTIFluxo flow={flow} cards={cards} colaboradores={colaboradores} onAbrirCard={(card) => setCardSelecionado(card)} />
          </Suspense>
        );
      case 'agenda':
        // 📅 agenda dos compradores (só em fluxo com campo de data "agend…")
        if (!fluxoTemAgenda(flow)) return renderKanbanView();
        return (
          <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-gray-400">Carregando a agenda…</div>}>
            <AgendaFluxo flow={flow} cards={cards} colaboradores={colaboradores} onAbrirCard={(card) => setCardSelecionado(card)} />
          </Suspense>
        );
      case 'vila':
        // trava de segurança: se alguém forçar o estado, cai no Kanban
        if (!podeVerVila) return renderKanbanView();
        return (
          <Suspense fallback={
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
              Carregando a vila…
            </div>
          }>
            <VilaFluxo
              flow={flow}
              cards={cards}
              colaboradores={colaboradores}
              onAbrirEtapa={(etapa) => {
                // volta pro Kanban destacando a etapa clicada na vila
                setEtapaDestacada(String(etapa.id));
                setViewMode('kanban');
              }}
            />
          </Suspense>
        );
      case 'kanban':
      default:
        console.log('📋 Renderizando visualização Kanban');
        return renderKanbanView();
    }
  };

  // Componente de Visualização Kanban (conteúdo original)
  const renderKanbanView = () => (
    <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      {/* Container - altura total, overflow-x no container externo */}
      <div className="h-full kanban-desktop-scale">
        <div className="h-full p-3 md:p-4">
          {/* 🔀 Droppable para reordenação de etapas */}
          <Droppable 
            droppableId="etapas-list" 
            direction="horizontal" 
            type="ETAPA"
            ignoreContainerClipping={true}
          >
            {(provided) => (
              <div 
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="flex gap-3 md:gap-4 min-w-min kanban-columns-container h-full"
              >
                {etapas.map((etapa, index) => {
                  const cardsEtapa = cards.filter(card => card.etapaId === etapa.id);
                  const cardsFiltrados = aplicarFiltroData(filtrarCards(cardsEtapa, etapa.id), String(etapa.id));
                  // Aplicar ordenação configurada na etapa
                  const cardsOrdenados = ordenarCards(cardsFiltrados, etapa);
                  const cardsRaizFiltrados = obterCardsRaiz(cardsOrdenados);
                  const cardsRaizTotal = obterCardsRaiz(cardsEtapa);
                  
                  const canDragEtapa = userPermissions?.canAdmin || checkEtapaEditPermission?.();
                  const canAddEtapa = canDragEtapa;
                  
                  return (
                    <Draggable 
                      key={String(etapa.id)} 
                      draggableId={String(etapa.id)} 
                      index={index}
                      isDragDisabled={!canDragEtapa}
                    >
                      {(provided, snapshot) => (
                        <div 
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className="flex-none w-56 md:w-60 kanban-column relative"
                        >
                          {/* Botão inserir etapa antes desta (hover apenas na faixa da esquerda) */}
                          {canAddEtapa && index > 0 && !snapshot.isDragging && (
                            <div className="absolute -left-3 top-0 w-6 h-14 z-20 group/insert flex items-center justify-center">
                              <button
                                type="button"
                                onClick={() => addEtapaAt(index)}
                                title="Inserir etapa aqui"
                                className="w-6 h-6 rounded-full bg-violet-600 hover:bg-violet-700 text-white shadow-lg flex items-center justify-center text-sm font-bold opacity-0 group-hover/insert:opacity-100 transition-opacity"
                              >
                                +
                              </button>
                            </div>
                          )}
                          <div className={`bg-white/80 backdrop-blur-sm rounded-lg shadow-md border border-white/20 hover:shadow-lg transition-all h-full flex flex-col min-h-0 ${
                            snapshot.isDragging ? 'opacity-50 shadow-2xl' : ''
                          }`}>
                            {/* Etapa Header com drag handle */}
                            <div className="p-2 md:p-2.5 border-b border-gray-100/80 flex items-center justify-between kanban-column-header overflow-hidden">
                              {/* Drag handle icon */}
                              <div 
                                {...provided.dragHandleProps}
                                className="mr-1.5 cursor-move text-gray-400 hover:text-gray-600 flex-shrink-0"
                                title="Arrastar para reordenar etapas"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0 overflow-hidden">
                                <h3 className="text-sm md:text-base font-semibold text-gray-900 truncate">
                                  {etapa.nome}
                                </h3>
                                <p className="text-[10px] text-gray-600">
                                  {(() => {
                                    const isFiltroResponsavelAtivo = filtroResponsavel.has(etapa.id);
                                    if (isFiltroResponsavelAtivo && termoPesquisa) {
                                      return `${cardsRaizFiltrados.length} de ${cardsRaizTotal.length} cartão(s) (filtrado por responsável e pesquisa)`;
                                    }
                                    if (isFiltroResponsavelAtivo) {
                                      const colaborador = colaboradores.find(c => c.id === filtroResponsavel.get(etapa.id));
                                      return `${cardsRaizFiltrados.length} de ${cardsRaizTotal.length} cartão(s) (${colaborador?.nome?.split(' ')[0] || 'filtrado'})`;
                                    }
                                    if (termoPesquisa) {
                                      return `${cardsRaizFiltrados.length} de ${cardsRaizTotal.length} cartão(s)`;
                                    }
                                    {
                                      const fp = filtroDataEtapa[String(etapa.id)];
                                      if (fp && (fp.de || fp.ate)) {
                                        const br = (d?: string) => d ? d.split('-').reverse().slice(0, 2).join('/') : '…';
                                        return `${cardsRaizFiltrados.length} de ${cardsRaizTotal.length} cartão(s) · ${br(fp.de)} a ${br(fp.ate)}`;
                                      }
                                    }
                                    return rotuloContagemEtapa(cardsEtapa);
                                  })()}
                                </p>
                                {/* Avatares dos participantes - Clicável para filtrar */}
                                {etapa.participantes && etapa.participantes.length > 0 && (
                                  <div className="flex flex-wrap gap-0.5 mt-1">
                                    {etapa.participantes.map((participanteId, avatarIndex) => {
                                      const colaborador = colaboradores.find(c => c.id === participanteId);
                                      const isFiltroAtivo = filtroResponsavel.get(etapa.id) === participanteId;
                                      const temFiltroAtivo = filtroResponsavel.has(etapa.id);
                                      return (
                                        <div
                                          key={participanteId}
                                          className={`w-5 h-5 rounded-full border-2 shadow-sm overflow-hidden flex-shrink-0 cursor-pointer transition-all duration-200 ${
                                            isFiltroAtivo 
                                              ? 'border-blue-500 ring-2 ring-blue-300 z-10' 
                                              : temFiltroAtivo 
                                                ? 'border-white opacity-40 hover:opacity-100 hover:scale-125 hover:z-10' 
                                                : 'border-white hover:scale-125 hover:z-10'
                                          }`}
                                          title={`${colaborador?.nome || 'Participante'}${isFiltroAtivo ? ' (filtro ativo - clique para remover)' : ' - clique para filtrar'}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const novoFiltro = new Map(filtroResponsavel);
                                            if (isFiltroAtivo) {
                                              novoFiltro.delete(etapa.id);
                                            } else {
                                              novoFiltro.set(etapa.id, participanteId);
                                            }
                                            setFiltroResponsavel(novoFiltro);
                                          }}
                                        >
                                          {colaborador?.foto ? (
                                            <img src={colaborador.foto} alt={colaborador.nome} className="w-full h-full object-cover" />
                                          ) : (
                                            <div className={`w-full h-full ${getAvatarColor(avatarIndex)} flex items-center justify-center text-white text-[8px] font-semibold`}>
                                              {getInitials(colaborador?.nome || '??')}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {/* 🧾 Lançar notas no GIX — só na etapa que tem a chave ligada */}
                                {(etapa as any).gixLancarNfMov96 && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); varrerLancamentoNf(); }}
                                    disabled={varrendoNf}
                                    className={`p-1 rounded transition-colors ${
                                      varrendoNf
                                        ? 'text-gray-300 cursor-wait'
                                        : 'text-emerald-600 hover:text-emerald-700 hover:bg-white/60'
                                    }`}
                                    title={varrendoNf
                                      ? 'Conferindo as notas no GIX...'
                                      : 'Lançar as notas fiscais desta etapa no GIX'}
                                  >
                                    {varrendoNf ? (
                                      <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                      </svg>
                                    ) : (
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                    )}
                                  </button>
                                )}
                                {/* Botão de ordenação - editores da etapa também podem usar */}
                                {(userPermissions?.canAdmin || checkEtapaEditPermission?.() || checkCardEditPermission?.(etapa)) && (
                                  <button
                                    onClick={() => setConfigurandoOrdenacao(etapa)}
                                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-white/60 rounded transition-colors"
                                    title="Ordenar cards"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                                    </svg>
                                  </button>
                                )}
                                {/* Botão de configuração - apenas admins */}
                                {(userPermissions?.canAdmin || checkEtapaEditPermission?.()) && (
                                  <button
                                    onClick={() => { setConfiguring(etapa); onOpenEtapaConfig?.(); }}
                                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-white/60 rounded transition-colors"
                                  >
                                    <Cog6ToothIcon className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Cards Container - scroll vertical com touch */}
                            <Droppable droppableId={String(etapa.id)}>
                              {(provided, snapshot) => (
                                <div
                                  {...provided.droppableProps}
                                  ref={provided.innerRef}
                                  className={`flex-1 p-3 md:p-4 space-y-3 md:space-y-4 overflow-y-scroll overscroll-y-contain ${
                                    snapshot.isDraggingOver ? 'bg-blue-50/50' : ''
                                  } transition-colors`}
                                  style={{ 
                                    WebkitOverflowScrolling: 'touch',
                                    minHeight: 0
                                  }}
                                >
                        {cardsRaizFiltrados
                          .map((card, index) => {
                            // Se for um card pai, usar componente especial
                            if (card.ehCardPai) {
                              return (
                                <Draggable 
                                  key={String(card.id)} 
                                  draggableId={String(card.id)} 
                                  index={index}
                                  isDragDisabled={!(userPermissions?.canEdit || userPermissions?.canAdmin)}
                                >
                                  {(provided, snapshot) => {
                                    const cardNode = (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        style={provided.draggableProps.style}
                                        className="relative"
                                      >
                                        {/* Drag Handle para card pai */}
                                        <div 
                                          {...provided.dragHandleProps}
                                          className="absolute top-2 right-2 p-1.5 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing z-10"
                                          onClick={(e) => e.stopPropagation()}
                                          style={{ touchAction: 'none' }}
                                        >
                                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M8 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM8 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM8 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM14 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM14 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM14 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"/>
                                          </svg>
                                        </div>
                                        <CardPai
                                          card={card}
                                          etapa={etapa}
                                          todasEtapas={flow.etapas || []}
                                          onClick={() => setCardSelecionado(card)}
                                          onClickFilho={(filho) => setCardSelecionado(filho)}
                                          onAdicionarFilho={(cardPai) => setAdicionandoFilhoAoPai(cardPai)}
                                          onRemoverFilho={(cardPai, filhoId) => handleRemoverFilho(cardPai.id, filhoId)}
                                          onDestravarPai={handleDestravarCardPai}
                                          onUpdateValorTotal={handleUpdateValorTotal}
                                          isDragging={snapshot.isDragging}
                                          mostrarContagemFilhos={etapa.configuracaoHierarquia?.visualCardPai?.mostrarContagemFilhos}
                                        />
                                      </div>
                                    );

                                    return snapshot.isDragging ? createPortal(cardNode, document.body) : cardNode;
                                  }}
                                </Draggable>
                              );
                            }
                            
                            // Card normal
                            return (
                              <Draggable 
                                key={String(card.id)} 
                                draggableId={String(card.id)} 
                                index={index}
                                isDragDisabled={!(userPermissions?.canEdit || userPermissions?.canAdmin)}
                              >
                                {(provided, snapshot) => {
                                  const isConectado = card.isCardConectado;
                                  const temConexoes = card.cardsConectadosIds && card.cardsConectadosIds.length > 0;
                                  const corConectado = card.corCardConectado || '#8B5CF6';
                                  // cor validada (#RRGGBB) para poder anexar alpha hex no selo suave
                                  const corHex = /^#[0-9a-fA-F]{6}$/.test(corConectado) ? corConectado : '#8B5CF6';

                                  // Passou de 2x o prazo da etapa: o card inteiro fica vermelho
                                  // e pulsando, não só o badge (ver index.css).
                                  const slaEscalonado = (() => {
                                    if (!etapa.tempoMaximoHoras) return false;
                                    const entrada = entradaNaEtapa(card.etapaHistory as any, String(etapa.id));
                                    if (!entrada) return false;
                                    const horas = calcularHorasSLA(entrada, agoraSla, etapa.slaIgnorarFimDeSemana);
                                    return nivelEstouroSLA(horas, etapa.tempoMaximoHoras) >= NIVEL_ESCALONAMENTO_SLA;
                                  })();
                                  // ✈️ Viagens (Dayane 30/10/2026): viagem a 20 dias ou menos da solicitação
                                  const vgUrgente = (card as any).campos?.campo_vg_urgente === 'Sim';

                                  const cardNode = (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className={`
                                        rounded-lg p-2 shadow-sm transition-all cursor-pointer
                                        backdrop-blur-sm kanban-card
                                        ${isConectado 
                                          ? 'bg-gradient-to-r from-purple-50 to-white border-l-[3px] border-r border-t border-b border-gray-100/60' 
                                          : 'bg-white border border-gray-100/60'
                                        }
                                        ${temConexoes ? 'ring-1 ring-purple-200' : ''}
                                        hover:shadow-md hover:border-gray-200
                                        ${snapshot.isDragging ? 'rotate-2 shadow-xl' : ''}
                                        ${cardMovendo === String(card.id) ? 'relative' : ''}
                                        ${slaEscalonado ? 'kanban-card-sla-escalonado' : ''}
                                        ${vgUrgente ? '!bg-red-50 !border-red-400 border-l-4' : ''}
                                      `}
                                      style={{
                                        ...provided.draggableProps.style,
                                        ...(isConectado ? { borderLeftColor: corConectado } : {}),
                                        ...(vgUrgente ? { borderColor: '#f87171', borderLeftColor: '#dc2626', borderLeftWidth: 4, backgroundColor: '#fef2f2' } : {})
                                      }}
                                      onClick={() => setCardSelecionado(card)}
                                    >
                                      {/* Selo de Card Conectado - pílula suave na cor da conexão */}
                                      {isConectado && (
                                        <div
                                          className="inline-flex items-center gap-1 mb-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide w-fit border"
                                          style={{ color: corHex, backgroundColor: corHex + '14', borderColor: corHex + '3d' }}
                                        >
                                          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                          </svg>
                                          {(card as any).seloCard || 'Conectado'}
                                        </div>
                                      )}
                                      {vgUrgente && (
                                        <div
                                          className="inline-flex items-center gap-1 mb-1.5 mr-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide w-fit border"
                                          style={{ color: '#b91c1c', backgroundColor: '#fee2e2', borderColor: '#fca5a5' }}
                                          title="Viagem com 20 dias ou menos entre a solicitação e a data da viagem"
                                        >
                                          🚨 Viagem urgente
                                        </div>
                                      )}
                                      {/* Cotação respondida pelo fornecedor (portal), na etapa que pede cotação */}
                                      {(card as any).cotacaoRecebida && (etapa as any).cotacaoCampos && (
                                        <div
                                          className="inline-flex items-center gap-1 mb-1.5 ml-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide w-fit border"
                                          style={{ color: '#15803d', backgroundColor: '#dcfce7', borderColor: '#86efac' }}
                                          title={`Recebida em ${new Date((card as any).cotacaoRecebida.em).toLocaleString('pt-BR')}`}
                                        >
                                          💰 Cotação recebida · {(card as any).cotacaoRecebida.fornecedor}
                                        </div>
                                      )}
                                      {/* Badge indicando que TEM cards conectados */}
                                      {temConexoes && !isConectado && (
                                        <div className="inline-flex items-center gap-1 mb-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold bg-purple-100 text-purple-700 border border-purple-200 w-fit">
                                          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                          </svg>
                                          {card.cardsConectadosIds!.length} subprocesso{card.cardsConectadosIds!.length > 1 ? 's' : ''}
                                        </div>
                                      )}
                                      {/* 🔎 Protocolo do portal - código que o solicitante recebeu.
                                          Fica visível para conferir de bate-pronto se a solicitação chegou,
                                          e é o mesmo texto aceito pela pesquisa do fluxo. */}
                                      {(() => {
                                        const protocoloCard = (card as any).protocolo || protocoloByCard[String(card.id)];
                                        if (!protocoloCard) return null;
                                        return (
                                          <div
                                            title={`Protocolo do portal: ${protocoloCard} (clique para copiar)`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigator.clipboard?.writeText(String(protocoloCard));
                                            }}
                                            className="inline-flex items-center gap-1 mb-1 px-1.5 py-[1px] rounded bg-cyan-50 text-cyan-700 border border-cyan-200 text-[9px] leading-[13px] font-semibold font-mono tracking-tight w-fit hover:bg-cyan-100 transition-colors"
                                          >
                                            <svg className="w-2.5 h-2.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            {protocoloCard}
                                          </div>
                                        );
                                      })()}
                                      {/* Drag Handle - visível apenas para arrastar */}
                                      <div 
                                        {...provided.dragHandleProps}
                                        className="absolute top-1 right-1 p-0.5 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing z-10"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ touchAction: 'none' }}
                                      >
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                          <path d="M8 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM8 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM8 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM14 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM14 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM14 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"/>
                                        </svg>
                                      </div>
                                      {/* Overlay de loading durante movimentação */}
                                      {cardMovendo === String(card.id) && (
                                        <div className="absolute inset-0 bg-white/90 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center z-50">
                                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                                          <p className="mt-1.5 text-[10px] font-medium text-purple-600">Salvando...</p>
                                        </div>
                                      )}
                                      {/* Status Badge */}
                                      <div className="flex items-start justify-between gap-1 mb-1">
                                        {/* min-w-0 + break-words: sem isso os selos da direita (SLA,
                                            vencimento) espremiam o título até sobrar uma letra por linha */}
                                        <h4
                                          title={card.titulo?.replace(/^(\[CONECTADO\]\s*)+/g, '')}
                                          className="text-[10px] md:text-[11px] font-bold text-gray-900 leading-tight flex-1 min-w-0 break-words line-clamp-2"
                                        >
                                          {card.titulo?.replace(/^(\[CONECTADO\]\s*)+/g, '')}
                                        </h4>

                                        {/* Os selos nunca passam de ~45% do card e quebram em outra
                                            linha antes de roubar espaço do título */}
                                        <div className="flex items-center justify-end flex-wrap gap-1 shrink-0 max-w-[45%]">
                                          {/* 💬 Badge de mensagens novas do solicitante (portal) */}
                                          {unreadByCard[String(card.id)] > 0 && (
                                            <span
                                              title="Mensagens novas do solicitante"
                                              className="flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold animate-pulse"
                                            >
                                              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                              </svg>
                                              {unreadByCard[String(card.id)] > 99 ? '99+' : unreadByCard[String(card.id)]}
                                            </span>
                                          )}
                                          {/* 💬 Selo de conversa com o solicitante (sem mensagens novas) */}
                                          {!(unreadByCard[String(card.id)] > 0) && chatByCard[String(card.id)] > 0 && (
                                            <span
                                              title={`Conversa com o solicitante (${chatByCard[String(card.id)]} mensagem${chatByCard[String(card.id)] > 1 ? 's' : ''})`}
                                              className="flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-indigo-100 text-indigo-600 text-[9px] font-semibold"
                                            >
                                              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                              </svg>
                                              {chatByCard[String(card.id)] > 99 ? '99+' : chatByCard[String(card.id)]}
                                            </span>
                                          )}
                                          {/* 🧾 GIX: badge de aprovação de OC (movimento 96) */}
                                          {gixOcByCard[String(card.id)] === 'aprovada' && (
                                            <span
                                              title="OC (movimento 96) aprovada no GIX"
                                              className="flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-bold"
                                            >
                                              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                              </svg>
                                              OC
                                            </span>
                                          )}
                                          {gixOcByCard[String(card.id)] === 'elegivel' && (
                                            <span
                                              title="OC com movimento 96 — elegível para aprovação (modo simulação)"
                                              className="flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-semibold"
                                            >
                                              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                              </svg>
                                              OC 96
                                            </span>
                                          )}
                                          {/* 🧾 GIX: lançar a NF SÓ deste card.
                                              O botão da etapa serve para a fila inteira; aqui é
                                              para quando se quer resolver um card específico e
                                              deixar o resto parado. Mesmo endpoint, com cardId. */}
                                          {etapasComLancamentoNf.has(String(etapa.id)) && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                // Sem detalhar "gera título e baixa a OC": isso vale
                                                // para NF-e de mercadoria, e nota de serviço não faz
                                                // nem um nem outro. Prometer o que não acontece faz a
                                                // pessoa procurar depois um título que nunca existiu.
                                                if (!window.confirm(
                                                  `Lançar a nota fiscal deste card no GIX?

${card.titulo}

`
                                                  + 'Com a escrita ligada isso cria a entrada de verdade no ERP. '
                                                  + 'Desfazer exige estorno, não dá para apagar.')) return;
                                                varrerLancamentoNf(String(card.id));
                                              }}
                                              disabled={varrendoNf || !!varrendoNfCardId}
                                              className={`p-0.5 rounded transition-colors ${
                                                varrendoNf || varrendoNfCardId
                                                  ? 'text-gray-300 cursor-wait'
                                                  : 'text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50'}`}
                                              title="Lançar a nota fiscal só deste card no GIX"
                                            >
                                              {varrendoNfCardId === String(card.id) ? (
                                                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                                </svg>
                                              ) : (
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                              )}
                                            </button>
                                          )}
                                          {/* Botão para transformar em Card Pai */}
                                          {etapa.configuracaoHierarquia?.permitirCardsPai && !card.cardPaiId && podeTransformarEmPai(String(etapa.id)) && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleTransformarEmCardPai(card, etapa);
                                              }}
                                              className="p-0.5 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
                                              title="Transformar em Card Pai"
                                            >
                                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                              </svg>
                                            </button>
                                          )}
                                          
                                          {/* Botão para criar Card Conectado - só aparece em etapas configuradas */}
                                          {(userPermissions?.canEdit || userPermissions?.canAdmin) && etapasComCardConectado.has(String(card.etapaId)) && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setCardConectadoModal(card);
                                              }}
                                              className="p-0.5 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 rounded transition-colors"
                                              title="Criar Card Conectado (Subprocesso)"
                                            >
                                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                              </svg>
                                            </button>
                                          )}
                                          
                                          {/* 🗓 Quando o card foi criado — linha própria (basis-full), logo
                                              acima dos selos de prazo. Pedido do André em 06/10/2026. */}
                                          {/* Card do portal só tem dataCriacao, gravada em UTC sem fuso
                                              ("2026-10-05T14:30:59.99"): sem o Z o navegador lia como
                                              hora local e mostrava 3h a mais. */}
                                          {((card as any).createdAt || (card as any).dataCriacao) && (() => {
                                            let bruto: any = (card as any).createdAt || (card as any).dataCriacao;
                                            if (typeof bruto === 'string' && /T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(bruto)) bruto += 'Z';
                                            const criado = new Date(bruto);
                                            if (isNaN(criado.getTime())) return null;
                                            return (
                                              <span
                                                className="basis-full text-right text-[8px] text-gray-500 whitespace-nowrap"
                                                title={`Card criado em ${criado.toLocaleString('pt-BR')}`}
                                              >
                                                🗓 {criado.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                              </span>
                                            );
                                          })()}

                                          {/* 03/11/2026 André: OC liberada no GIX com movimento que esta etapa não aceita — o card não anda sozinho */}
                                          {(card as any).gixOcAprovacao?.status === 'mov_nao_aceito' && (
                                            <span
                                              className="px-1 py-0.5 text-[8px] font-medium bg-amber-100 text-amber-800 rounded-full whitespace-nowrap"
                                              title={`OC ${(card as any).gixOcAprovacao.numeroOc} liberada no GIX com movimento ${(card as any).gixOcAprovacao.movimento}, mas esta etapa só anda sozinha com ${((card as any).gixOcAprovacao.aceitos || ['E96']).join(', ')}. Mova o card à mão.`}
                                            >
                                              ⚠ OC {(card as any).gixOcAprovacao.numeroOc} {(card as any).gixOcAprovacao.movimento}: mover à mão
                                            </span>
                                          )}

                                          {/* ⏱️ Prazo do processo: um relógio para o card inteiro */}
                                          {flow.prazoProcesso?.habilitado && (() => {
                                            const prazo = calcularPrazoProcesso(card as any, flow, agoraSla);
                                            if (!prazo || !prazo.ciclos.length) return null;
                                            const ordinal = (n: number) => `${n}º prazo`;
                                            const detalhe = prazo.ciclos.map(c => c.fim
                                              ? `${ordinal(c.numero)}: levou ${formatarDuracao(c.horasGastas)} (congelado em ${c.fim.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })})`
                                              : `${ordinal(c.numero)}: ${formatarDuracao(c.horasGastas)} de ${prazo.horasPrazo}h, em andamento`
                                            ).join('\n');
                                            const prefixo = prazo.ciclos.length > 1 ? `${ordinal(prazo.ciclos.length)} · ` : '';

                                            if (!prazo.atual) {
                                              const ultimo = prazo.ciclos[prazo.ciclos.length - 1];
                                              return (
                                                <span
                                                  className="px-1 py-0.5 text-[8px] font-medium bg-slate-100 text-slate-600 rounded-full whitespace-nowrap"
                                                  title={`Prazo do processo congelado.\n${detalhe}`}
                                                >
                                                  ⏸ {prefixo}levou {formatarDuracao(ultimo.horasGastas)}
                                                </span>
                                              );
                                            }

                                            const restante = prazo.horasRestantes ?? 0;
                                            const cor = restante < 0
                                              ? 'bg-red-100 text-red-700'
                                              : restante <= prazo.horasPrazo * 0.2
                                                ? 'bg-orange-100 text-orange-700'
                                                : 'bg-sky-50 text-sky-700';
                                            return (
                                              <span
                                                className={`px-1 py-0.5 text-[8px] font-medium rounded-full whitespace-nowrap ${cor}`}
                                                title={`Prazo do processo (${prazo.horasPrazo}h).\n${detalhe}`}
                                              >
                                                ⏱ {prefixo}{restante < 0 ? `atrasado ${formatarDuracao(-restante)}` : `faltam ${formatarDuracao(restante)}`}
                                              </span>
                                            );
                                          })()}

                                          {/* Badge de Tempo na Etapa (SLA) */}
                                          {etapa.tempoMaximoHoras && (() => {
                                            // Última entrada na etapa: card que já passou por aqui e voltou
                                            // tem várias no histórico, e usar a primeira inflava o atraso.
                                            const entrada = entradaNaEtapa(card.etapaHistory as any, String(etapa.id));
                                            if (!entrada) return null;

                                            const horasNaEtapa = calcularHorasSLA(entrada, agoraSla, etapa.slaIgnorarFimDeSemana);
                                            const percentual = (horasNaEtapa / etapa.tempoMaximoHoras) * 100;
                                            const nivel = nivelEstouroSLA(horasNaEtapa, etapa.tempoMaximoHoras);
                                            const saldo = etapa.tempoMaximoHoras - horasNaEtapa;
                                            // Com fim de semana ignorado, as horas são úteis, não de relógio
                                            const tipoHoras = etapa.slaIgnorarFimDeSemana ? 'horas úteis' : 'horas';
                                            const resumo = `${formatarHorasSLA(horasNaEtapa)} de ${etapa.tempoMaximoHoras}h na etapa (${tipoHoras})`;

                                            // Dobrou o prazo: badge sólido com o multiplicador e as horas de atraso
                                            if (nivel >= NIVEL_ESCALONAMENTO_SLA) {
                                              return (
                                                <span
                                                  className="px-1.5 py-0.5 text-[8px] font-bold bg-red-600 text-white rounded-full whitespace-nowrap"
                                                  title={`${nivel}x o prazo da etapa — atrasado ${formatarHorasSLA(-saldo)}. ${resumo}`}
                                                >
                                                  {nivel}x · atrasado {formatarHorasSLA(-saldo)}
                                                </span>
                                              );
                                            }

                                            if (saldo < 0) {
                                              return (
                                                <span
                                                  className="px-1 py-0.5 text-[8px] font-medium bg-red-100 text-red-700 rounded-full whitespace-nowrap"
                                                  title={`Passou do prazo da etapa há ${formatarHorasSLA(-saldo)}. ${resumo}`}
                                                >
                                                  ⏰ atrasado {formatarHorasSLA(-saldo)}
                                                </span>
                                              );
                                            }

                                            // Dentro do prazo: quanto falta. Laranja nos últimos 20%.
                                            const cor = percentual > 80
                                              ? 'bg-orange-100 text-orange-700'
                                              : 'bg-emerald-50 text-emerald-700';
                                            return (
                                              <span
                                                className={`px-1 py-0.5 text-[8px] font-medium rounded-full whitespace-nowrap ${cor}`}
                                                title={`Faltam ${formatarHorasSLA(saldo)} para o prazo da etapa. ${resumo}`}
                                              >
                                                ⏳ faltam {formatarHorasSLA(saldo)}
                                              </span>
                                            );
                                          })()}
                                          
                                          {/* Status de vencimento */}
                                          {(() => {
                                            // Procurar campo de data de vencimento
                                            const campoVencimento = etapa.campos?.find(c => c.tipo === 'data_vencimento');
                                            if (!campoVencimento || !card.campos || !card.campos[campoVencimento.id]) {
                                              return null;
                                            }
                                            
                                            try {
                                              const dataVencimento = new Date(card.campos[campoVencimento.id]);
                                              if (isNaN(dataVencimento.getTime())) return null;
                                              
                                              const agora = new Date();
                                              const diferencaHoras = (dataVencimento.getTime() - agora.getTime()) / (1000 * 60 * 60);
                                              
                                              if (dataVencimento < agora) {
                                                return (
                                                  <span className="px-1 py-0.5 text-[8px] font-medium bg-red-100 text-red-700 rounded-full whitespace-nowrap">
                                                    VENCIDO
                                                  </span>
                                                );
                                              } else if (diferencaHoras <= 24) {
                                                return (
                                                  <span
                                                    title="Vence em menos de 24h"
                                                    className="px-1 py-0.5 text-[8px] font-medium bg-yellow-100 text-yellow-700 rounded-full whitespace-nowrap"
                                                  >
                                                    VENCE EM BREVE
                                                  </span>
                                                );
                                              }
                                            } catch (error) {
                                              return null;
                                            }
                                            return null;
                                          })()}
                                        </div>
                                      </div>

                                      {/* 🏷️ Etiquetas do card */}
                                      {(card.etiquetasIds && card.etiquetasIds.length > 0 && etiquetasFlow.length > 0) && (
                                        <div className="mb-1">
                                          <EtiquetaBadges etiquetasIds={card.etiquetasIds} etiquetas={etiquetasFlow} tamanho="xs" max={4} />
                                        </div>
                                      )}

                                      {/* Responsável Individual do Card */}
                                      {card.responsavelIndividualId && (() => {
                                        const responsavel = colaboradores.find(c => String(c.id) === String(card.responsavelIndividualId));
                                        if (responsavel) {
                                          const initials = responsavel.nome
                                            .split(' ')
                                            .map(n => n[0])
                                            .join('')
                                            .substring(0, 2)
                                            .toUpperCase();
                                          const avatarColor = getAvatarColorFromId(String(responsavel.id));
                                          return (
                                            <div className="flex items-center gap-1 mb-1 pb-1 border-b border-gray-100">
                                              <div className={`w-4 h-4 rounded-full ${avatarColor} flex items-center justify-center text-white font-semibold text-[7px] shadow-sm`}>
                                                {initials}
                                              </div>
                                              <div className="flex-1 min-w-0">
                                                <div className="text-[9px] text-gray-700 font-medium truncate">
                                                  {responsavel.nome}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        }
                                        return null;
                                      })()}

                                      {/* Campos do Card */}
                                      <div className="space-y-1">
                                        {(() => {
                                          // 1. Buscar TODOS os campos travados de TODAS as etapas do fluxo
                                          const todosCamposTravados = new Map();
                                          flow.etapas?.forEach(e => {
                                            if (e.camposTravados && e.camposTravados.length > 0) {
                                              e.campos.forEach(c => {
                                                if (e.camposTravados && e.camposTravados.includes(c.id)) {
                                                  todosCamposTravados.set(c.id, c);
                                                }
                                              });
                                            }
                                          });
                                          
                                          // 2. Campos visíveis configurados da etapa atual
                                          let camposVisiveis = etapa.camposVisiveisNoCard && etapa.camposVisiveisNoCard.length > 0
                                            ? etapa.campos.filter(c => etapa.camposVisiveisNoCard?.includes(c.id))
                                            : [];
                                          
                                          // 3. Adicionar campos travados que não estão na lista de visíveis
                                          const camposTravadosArray = Array.from(todosCamposTravados.values());
                                          const travadosNaoVisiveis = camposTravadosArray.filter(
                                            c => !camposVisiveis.find(v => v.id === c.id)
                                          );
                                          
                                          // 4. Combinar: visíveis + travados não visíveis
                                          let camposParaMostrar = [...camposVisiveis, ...travadosNaoVisiveis];
                                          
                                          // 🔗 5. NOVO: Adicionar campos de outras etapas configurados
                                          const camposDeOutrasEtapas: { campo: any; etapaNome: string }[] = [];
                                          if (etapa.camposOutrasEtapas && etapa.camposOutrasEtapas.length > 0) {
                                            etapa.camposOutrasEtapas.forEach((campoKey: string) => {
                                              // Formato: "etapaId:campoId"
                                              const [etapaId, campoId] = campoKey.split(':');
                                              const outraEtapa = flow.etapas?.find(e => String(e.id) === etapaId);
                                              if (outraEtapa) {
                                                const campo = outraEtapa.campos?.find(c => c.id === campoId);
                                                const vFix = card?.campos?.[campoId];
                                                const vazioFix = vFix === undefined || vFix === null || vFix === '' || (Array.isArray(vFix) && vFix.length === 0);
                                                if (campo && !((etapa as any).ocultarFixadosVazios && vazioFix)) {
                                                  camposDeOutrasEtapas.push({
                                                    campo: { ...campo, id: campoId },
                                                    etapaNome: outraEtapa.nome
                                                  });
                                                }
                                              }
                                            });
                                          }
                                          
                                          // 6. Se não tem nada configurado, mostrar primeiros 4
                                          if (camposParaMostrar.length === 0 && camposDeOutrasEtapas.length === 0) {
                                            camposParaMostrar = etapa.campos?.slice(0, 4) || [];
                                          }
                                          // Etapa com "só os campos escolhidos" (Viagens, Dayane 28/10/2026): o card mostra
                                          // exatamente camposVisiveisNoCard, na ordem, com campos de qualquer etapa do fluxo.
                                          if ((etapa as any).cardSoVisiveis && etapa.camposVisiveisNoCard?.length) {
                                            const todos = new Map<string, any>();
                                            flow.etapas?.forEach(e => e.campos?.forEach(c => { if (!todos.has(c.id)) todos.set(c.id, c); }));
                                            camposParaMostrar = etapa.camposVisiveisNoCard.map(id => todos.get(id)).filter(Boolean);
                                            camposDeOutrasEtapas.length = 0;
                                          }
                                          
                                          // 🔍 Função helper para buscar valor do campo na fonte correta
                                          // PRIORIDADE: etapaHistory.camposSnapshot > card.campos
                                          // (porque o usuário pode ter editado o histórico)
                                          // IMPORTANTE: Corrigido em 22/01/2026 - Busca no camposSnapshot do histórico
                                          // para refletir alterações feitas pelo usuário no histórico de etapas
                                          // ⚠️ IMPORTANTE - NÃO ALTERAR A ORDEM! (Corrigido em 22/01/2026)
                                          // card.campos SEMPRE tem prioridade sobre etapaHistory porque:
                                          // 1. Quando o usuário edita um campo no histórico, o valor é salvo em card.campos
                                          // 2. etapaHistory contém SNAPSHOTS antigos de quando o card passou por cada etapa
                                          // 3. Se buscarmos no etapaHistory primeiro, mostramos valores desatualizados!
                                          const getValorCampo = (campoId: string) => {
                                            // PRIMEIRO: card.campos (fonte da verdade - valores atuais/editados)
                                            if (card?.campos?.[campoId] !== undefined && card.campos[campoId] !== '') {
                                              return card.campos[campoId];
                                            }
                                            
                                            // SEGUNDO: etapaHistory (fallback para campos que só existem em etapas anteriores)
                                            if (card?.etapaHistory && Array.isArray(card.etapaHistory)) {
                                              for (let i = card.etapaHistory.length - 1; i >= 0; i--) {
                                                const histEntry = card.etapaHistory[i];
                                                if (histEntry?.camposSnapshot?.[campoId] !== undefined && histEntry.camposSnapshot[campoId] !== '') {
                                                  return histEntry.camposSnapshot[campoId];
                                                }
                                              }
                                            }
                                            
                                            return undefined;
                                          };
                                          
                                          // Função para renderizar valor do campo
                                          const renderValorCampo = (campo: any) => {
                                            const valorBruto = getValorCampo(campo.id);
                                            // cardSoNome (Viagens, Dayane 30/10/2026): na frente do card só o nome, sem o "(...)" explicativo
                                            const valor = (campo as any).cardSoNome && typeof valorBruto === 'string'
                                              ? valorBruto.replace(/\s*\(.*\)\s*$/, '') : valorBruto;
                                            
                                            if (campo.tipo === 'data_vencimento') {
                                              if (!valor) return '-';
                                              try {
                                                const date = new Date(valor);
                                                if (isNaN(date.getTime())) return 'Data inválida';
                                                
                                                const now = new Date();
                                                const diferencaHoras = (date.getTime() - now.getTime()) / (1000 * 60 * 60);
                                                const isOverdue = date < now;
                                                const isSoon = diferencaHoras <= 24 && diferencaHoras > 0;
                                                
                                                const dateString = date.toLocaleDateString('pt-BR', {
                                                  day: '2-digit',
                                                  month: '2-digit',
                                                  year: '2-digit',
                                                  hour: '2-digit',
                                                  minute: '2-digit'
                                                });
                                                
                                                return (
                                                  <span className={`text-xs ${
                                                    isOverdue ? 'text-red-600 font-semibold' :
                                                    isSoon ? 'text-yellow-600 font-semibold' :
                                                    'text-green-600'
                                                  }`}>
                                                    📅 {dateString}
                                                  </span>
                                                );
                                              } catch (error) {
                                                return <span className="text-red-600 text-[11px]">Data inválida</span>;
                                              }
                                            } else if (campo.tipo === 'checkbox' || campo.tipo === 'checkbox_single' || campo.tipo === 'checkbox_multi') {
                                              const itens: string[] = Array.isArray(valor) ? valor.filter((v: any) => v !== undefined && v !== null && v !== '').map((v: any) => String(v)) : (valor ? [String(valor)] : []);
                                              const cores = (campo as any).coresOpcoes as Record<string, string> | undefined;
                                              if (itens.length === 0) return <span className="text-[11px]">-</span>;
                                              if (cores && itens.some(i => cores[i])) {
                                                return (
                                                  <span className="inline-flex flex-wrap gap-0.5">
                                                    {itens.map((item, i) => cores[item] ? (
                                                      <span key={i} className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: cores[item], color: '#fff' }}>{item}</span>
                                                    ) : (
                                                      <span key={i} className="text-[11px]">{item}{i < itens.length - 1 ? '; ' : ''}</span>
                                                    ))}
                                                  </span>
                                                );
                                              }
                                              return (
                                                <span className="text-[11px]">
                                                  {itens.join('; ')}
                                                </span>
                                              );
                                            } else if (campo.tipo === 'lista') {
                                              const texto = String(valor ?? '');
                                              const cor = (campo as any).coresOpcoes?.[texto];
                                              if (cor) {
                                                return (
                                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: cor, color: '#fff' }}>{texto}</span>
                                                );
                                              }
                                              return <span className="text-[11px]">{texto || '-'}</span>;
                                            } else if (campo.tipo === 'valor') {
                                              const s = String(valor || '0').replace(/[R$\s]/g, '').trim();
                                              const numVal = typeof valor === 'number' ? valor : s.includes(',') ? parseFloat(s.replace(/\./g, '').replace(',', '.')) : parseFloat(s);
                                              return (
                                                <span className="text-[11px] font-semibold text-green-700">
                                                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(isNaN(numVal) ? 0 : numVal)}
                                                </span>
                                              );
                                            } else if (campo.tipo === 'anexo') {
                                              return (
                                                <span className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs">
                                                  📎 {Array.isArray(valor) ? valor.length : 0}
                                                </span>
                                              );
                                            } else if (campo.tipo === 'justificativa_checkbox') {
                                              return (
                                                <span className="inline-flex items-center px-2 py-1 rounded bg-purple-100 text-purple-800 text-xs">
                                                  📝 {(valor && typeof valor === 'object') ? Object.keys(valor).length : 0} justificativas
                                                </span>
                                              );
                                            } else if ((campo.tipo as string) === 'data' && typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}/.test(valor)) {
                                              const [a, m, d] = valor.slice(0, 10).split('-');
                                              return <span className="text-[11px]">{`${d}/${m}/${a}`}</span>;
                                            } else if (((campo.tipo as string) === 'rotas' || (campo.tipo as string) === 'rota_terrestre') && Array.isArray(valor)) {
                                              const dm = (d: any) => { const m = String(d || '').match(/^\d{4}-(\d{2})-(\d{2})/); return m ? `${m[2]}/${m[1]}` : ''; };
                                              const lugar = (t: any, k: string) => t?.[`${k}Iata`] || String(t?.[k] || '').split(/[-,]/)[0].trim();
                                              return (
                                                <span className="text-[11px]">
                                                  {valor.filter((t: any) => t && typeof t === 'object').map((t: any, i: number) => (
                                                    <span key={i} className="block">{lugar(t, 'origem')} → {lugar(t, 'destino')} {dm(t.data)}</span>
                                                  ))}
                                                </span>
                                              );
                                            } else if ((campo.tipo as string) === 'viajantes' && Array.isArray(valor)) {
                                              const nomes = valor.filter((x: any) => x && x.nome).map((x: any) => String(x.nome));
                                              return <span className="text-[11px]">{nomes.join(', ') || '-'}</span>;
                                            } else if ((campo.tipo as string) === 'quadro_voos' && Array.isArray(valor)) {
                                              return <span className="text-[11px]">{valor.length} voo{valor.length === 1 ? '' : 's'}</span>;
                                            } else if (campo.tipo === 'parcelas') {
                                              return <span className="text-[11px]">{textoParcelas(valor) || '-'}</span>;
                                            } else if ((campo.tipo as string) === 'itens') {
                                              return <span className="text-[11px]">{textoItens(valor) || '-'}</span>;
                                            } else if (campo.tipo === 'responsavel') {
                                              // Guarda o id do colaborador — sem isso o minicard mostrava o id cru
                                              const pessoa = valor ? colaboradores.find(c => String(c.id) === String(valor)) : undefined;
                                              return <span className="text-[11px]">{valor ? (pessoa?.nome || 'Pessoa não encontrada') : '-'}</span>;
                                            } else if (Array.isArray(valor)) {
                                              // Qualquer outro campo cujo valor seja array de objetos (não string/número)
                                              // não pode ir direto pro JSX — React quebra a tela inteira nesse caso.
                                              return <span className="text-[11px]">{valor.length} item{valor.length === 1 ? '' : 's'}</span>;
                                            } else {
                                              // Limpar HTML/CSS que pode ter sido colado no campo
                                              const valorLimpo = typeof valor === 'string' && valor.includes('<')
                                                ? valor.replace(/<[^>]*>/g, '').replace(/\s{2,}/g, ' ').trim()
                                                : valor;
                                              return <span className="text-[11px]">{valorLimpo || '-'}</span>;
                                            }
                                          };
                                          
                                          // Renderizar campos da etapa atual + travados (oculta vazios)
                                          const camposAtuais = camposParaMostrar.map((campo) => {
                                            const valor = getValorCampo(campo.id);
                                            if (valor === undefined || valor === null || valor === '' || (Array.isArray(valor) && valor.length === 0)) return null;
                                            return (
                                              <div key={campo.id} className="flex items-start justify-between text-[9px] leading-tight">
                                                <span className="text-gray-500 font-medium min-w-0 truncate max-w-[40%]">
                                                  {campo.nome}:
                                                </span>
                                                <span className="text-gray-800 ml-1 text-right break-words max-w-[58%]">
                                                  {renderValorCampo(campo)}
                                                </span>
                                              </div>
                                            );
                                          }).filter(Boolean);
                                          
                                          // Renderizar campos de outras etapas (oculta vazios)
                                          const camposOutras = camposDeOutrasEtapas.map(({ campo, etapaNome }) => {
                                            const valor = getValorCampo(campo.id);
                                            if (valor === undefined || valor === null || valor === '' || (Array.isArray(valor) && valor.length === 0)) return null;
                                            return (
                                              <div key={`outras-${campo.id}`} className="flex items-start justify-between text-[9px] leading-tight">
                                                <span className="text-purple-600 font-medium min-w-0 truncate max-w-[40%]" title={`Da etapa: ${etapaNome}`}>
                                                  🔗 {campo.nome}:
                                                </span>
                                                <span className="text-gray-800 ml-1 text-right break-words max-w-[58%]">
                                                  {renderValorCampo(campo)}
                                                </span>
                                              </div>
                                            );
                                          }).filter(Boolean);
                                          
                                          return [...camposAtuais, ...camposOutras];
                                        })()}
                                      </div>
                                    </div>
                                  );
                                  // Portal while dragging to escape stacking contexts (e.g., backdrop-filter)
                                  return snapshot.isDragging ? createPortal(cardNode, document.body) : cardNode;
                                }}
                              </Draggable>
                            );
                          })}

                        {provided.placeholder}
                        {/* Botão Adicionar Cartão - mostrar apenas se etapa permite criar */}
                        {(() => {
                          const canEdit = checkCardEditPermission?.(etapa);
                          const permiteCriar = etapa.permiteCriarCards ?? true;
                          return canEdit && permiteCriar ? (
                            <button
                              onClick={() => setNovoCartao(etapa)}
                              className="w-full p-3 md:p-4 border-2 border-dashed border-gray-200 text-gray-500 rounded-xl hover:border-gray-300 hover:text-gray-600 hover:bg-white/30 transition-all group backdrop-blur-sm"
                            >
                              <div className="flex items-center justify-center gap-1.5">
                                <PlusIcon className="h-4 w-4 group-hover:scale-110 transition-transform" />
                                <span className="font-medium text-xs">Adicionar Cartão</span>
                              </div>
                            </button>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </Droppable>
                </div>
              </div>
            )}
          </Draggable>
          );
        })}
        {provided.placeholder}
      </div>
    )}
  </Droppable>
        </div>
      </div>
    </DragDropContext>
  );

  
  return (
    <div className="h-full overflow-hidden bg-gray-50">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 flex-shrink-0">
          <div className="px-3 md:px-5 py-2">
            {/* Top row: Back + Title + Search + Actions */}
            <div className="flex items-center gap-3">
              {/* Botão Voltar */}
              <button
                onClick={onBack}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
              >
                <ChevronLeftIcon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Voltar</span>
              </button>
              
              <div className="w-px h-4 bg-gray-200 hidden sm:block"></div>
              
              {/* Título do fluxo */}
              <h1 className="text-sm font-semibold text-gray-900 truncate flex items-center gap-2">
                {flow?.name}
                <span 
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                    isConnected 
                      ? 'bg-emerald-50 text-emerald-600' 
                      : 'bg-red-50 text-red-600'
                  }`}
                  title={isConnected ? 'Sincronização em tempo real ativa' : 'Desconectado - F5 para atualizar'}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`}></span>
                  {isConnected ? 'Ao vivo' : 'Offline'}
                </span>
              </h1>

              {/* Spacer */}
              <div className="flex-1"></div>

              {/* Pesquisa + Ações */}
              <div className="flex items-center gap-2">
                {/* Barra de Pesquisa */}
                <div className="block w-32 sm:w-44 lg:w-56">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={termoPesquisa}
                      onChange={(e) => setTermoPesquisa(e.target.value)}
                      placeholder="Pesquisar..."
                      className="w-full pl-7 pr-7 py-1.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 focus:bg-white transition-all text-xs"
                    />
                    {termoPesquisa && (
                      <button
                        onClick={() => setTermoPesquisa('')}
                        className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* 🧬 Filtro por tipo de card (pais / conectados / filhos / normais) */}
                <div className="relative flex-shrink-0">
                  <button
                    onClick={() => setMostrarMenuFiltroTipo(v => !v)}
                    title="Filtrar por tipo de card"
                    className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                      filtroTipoCard !== 'todos'
                        ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    <span className="hidden sm:inline">
                      {filtroTipoCard === 'todos' && 'Filtrar'}
                      {filtroTipoCard === 'pais' && 'Cards pais'}
                      {filtroTipoCard === 'conectados' && 'Conectados'}
                      {filtroTipoCard === 'filhos' && 'Cards filhos'}
                      {filtroTipoCard === 'normais' && 'Sem vínculo'}
                      {filtroTipoCard === 'comChat' && 'Com chat'}
                    </span>
                  </button>
                  {mostrarMenuFiltroTipo && (
                    <>
                      <div
                        className="fixed inset-0 z-20"
                        onClick={() => setMostrarMenuFiltroTipo(false)}
                      />
                      <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1">
                        {([
                          { id: 'todos', label: 'Todos os cards', desc: 'Sem filtro' },
                          { id: 'pais', label: '👑 Cards pais', desc: 'Apenas cards com filhos' },
                          { id: 'conectados', label: '🔗 Conectados', desc: 'Subprocessos gerados' },
                          { id: 'filhos', label: '🧩 Cards filhos', desc: 'Vinculados a um pai' },
                          { id: 'normais', label: '📄 Sem vínculo', desc: 'Cards independentes' },
                          { id: 'comChat', label: '💬 Com chat', desc: 'Conversa com o solicitante' },
                        ] as const).map(opt => (
                          <button
                            key={opt.id}
                            onClick={() => {
                              setFiltroTipoCard(opt.id);
                              setMostrarMenuFiltroTipo(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors ${
                              filtroTipoCard === opt.id ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                            }`}
                          >
                            <div className="font-medium">{opt.label}</div>
                            <div className="text-[10px] text-gray-500">{opt.desc}</div>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* 🏷️ Filtro por etiqueta */}
                <div className="relative flex-shrink-0">
                  <button
                    onClick={() => setMostrarMenuEtiquetas(v => !v)}
                    title="Filtrar por etiqueta"
                    className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                      filtroEtiquetas.length > 0
                        ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 014 12V7a4 4 0 014-4z" />
                    </svg>
                    <span className="hidden sm:inline">
                      {filtroEtiquetas.length > 0 ? `Etiquetas (${filtroEtiquetas.length})` : 'Etiquetas'}
                    </span>
                  </button>
                  {mostrarMenuEtiquetas && (
                    <>
                      <div className="fixed inset-0 z-20" onClick={() => setMostrarMenuEtiquetas(false)} />
                      <div className="absolute right-0 mt-1 w-60 bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1 max-h-80 overflow-y-auto">
                        {etiquetasFlow.length === 0 && (
                          <p className="px-3 py-3 text-xs text-gray-400 text-center">Nenhuma etiqueta criada ainda.</p>
                        )}
                        {etiquetasFlow.map(etq => {
                          const ativa = filtroEtiquetas.includes(etq.id);
                          return (
                            <button
                              key={etq.id}
                              onClick={() => setFiltroEtiquetas(prev => ativa ? prev.filter(id => id !== etq.id) : [...prev, etq.id])}
                              className="w-full flex items-center gap-2 px-2.5 py-1.5 hover:bg-gray-50 transition-colors text-left"
                            >
                              <span
                                className="flex-1 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none truncate"
                                style={{ backgroundColor: etq.cor, color: corTextoParaFundo(etq.cor) }}
                              >
                                {etq.nome}
                              </span>
                              {ativa && (
                                <svg className="h-4 w-4 text-indigo-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>
                          );
                        })}
                        <div className="border-t border-gray-100 mt-1 pt-1">
                          {filtroEtiquetas.length > 0 && (
                            <button
                              onClick={() => setFiltroEtiquetas([])}
                              className="w-full text-left px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                            >
                              Limpar filtro
                            </button>
                          )}
                          {(userPermissions?.canAdmin) && (
                            <button
                              onClick={() => { setMostrarMenuEtiquetas(false); setMostrarGerenciarEtiquetas(true); }}
                              className="w-full flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-indigo-600 hover:bg-indigo-50 transition-colors"
                            >
                              <PlusIcon className="h-3.5 w-3.5" /> Gerenciar etiquetas
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Botão Nova Etapa */}
                {(userPermissions?.canAdmin) && (
                  <button
                    onClick={addEtapa}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-xs font-medium flex-shrink-0"
                  >
                    <PlusIcon className="h-3.5 w-3.5" />
                    <span className="hidden lg:inline">Nova Etapa</span>
                  </button>
                )}
                
                {/* Botão Permissões */}
                {(userPermissions?.canAdmin) && (
                  <button
                    onClick={() => setMostrarConfigPermissoes(true)}
                    className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors text-xs font-medium flex-shrink-0 border border-gray-200"
                    title="Configurar permissões do fluxo"
                  >
                    <Cog6ToothIcon className="h-3.5 w-3.5" />
                    <span className="hidden xl:inline">Permissões</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Tabs de navegação */}
          <div className="px-3 md:px-5 flex items-center gap-1 overflow-x-auto border-t border-gray-100">
            {[
              ...(fluxoTemAtas(flow) ? [{ key: 'atas' as ViewMode, label: 'Atas', icon: ClipboardDocumentListIcon }] : []),
              { key: 'kanban' as ViewMode, label: 'Kanban', icon: ViewColumnsIcon },
              { key: 'lista' as ViewMode, label: 'Lista', icon: ListBulletIcon },
              ...(fluxoTemAgenda(flow) ? [{ key: 'agenda' as ViewMode, label: 'Agenda', icon: CalendarDaysIcon }] : []),
              { key: 'fluxo' as ViewMode, label: 'Fluxo', icon: ArrowPathIcon },
              { key: 'relatorios' as ViewMode, label: 'Relatórios', icon: ChartBarIcon },
              ...(fluxoTemQualidade(flow) ? [{ key: 'qualidade' as ViewMode, label: 'Qualidade', icon: CheckBadgeIcon }] : []),
              ...(fluxoTemVerbas(flow) ? [{ key: 'verbas' as ViewMode, label: 'Painel de Verbas', icon: BanknotesIcon }] : []),
              ...(fluxoTemChamadosTI(flow) ? [{ key: 'indicadoresTI' as ViewMode, label: 'Indicadores', icon: PresentationChartBarIcon }] : []),
              { key: 'documentos' as ViewMode, label: 'Documentos', icon: DocumentTextIcon },
              { key: 'portais' as ViewMode, label: 'Portais', icon: GlobeAltIcon },
              { key: 'hierarquia' as ViewMode, label: 'Hierarquia', icon: RectangleGroupIcon },
              ...(podeVerVila
                ? [{ key: 'vila' as ViewMode, label: 'Vila', icon: HomeModernIcon }]
                : []),
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setViewMode(key)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                  viewMode === key
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Área de Conteúdo - Visualizações */}
        <div 
          className="flex-1 overflow-auto min-h-0" 
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {renderContent()}
        </div>

        {/* Modais e Popovers */}
      {configuring && (
        <ConfiguracaoEtapa
          etapa={configuring!}
          flow={{ ...flow, etapas: todasEtapas }} // Usar TODAS as etapas sem filtro de permissões
          cards={cards}
          departamentos={departamentos}
          colaboradores={colaboradores}
          onSave={handleUpdateEtapa}
          onClose={() => { setConfiguring(null); onCloseEtapaConfig?.(); }}
          onDelete={handleDeleteEtapa}
          userPermissions={userPermissions}
        />
      )}

      {/* Modal de Configuração de Hierarquia */}
      {configuringHierarquia && (
        <ConfiguracaoHierarquia
          etapa={configuringHierarquia}
          todasEtapas={etapas}
          automacoes={automacoes}
          cards={cards}
          onSave={handleSaveHierarquiaConfig}
          onClose={() => setConfiguringHierarquia(null)}
        />
      )}

      {novoCartao && (
        <NovoCartao
          etapa={novoCartao!}
          onSave={handleAddCard}
          onClose={() => setNovoCartao(null)}
          empresaId={flow.empresaId}
          automacoes={automacoes}
          flowName={flow.name}
          flowId={flow.id}
          usuario={userPermissions?.currentUser}
          podeVerRestritos={Boolean(userPermissions?.canAdmin)}
        />
      )}

      {/* Modal de Adicionar Filho a Card Pai */}
      {adicionandoFilhoAoPai && (
        <ModalAdicionarFilho
          cardPai={adicionandoFilhoAoPai}
          cards={cards}
          etapas={etapas}
          onAdicionarFilho={(cardFilho) => handleAdicionarFilho(adicionandoFilhoAoPai, cardFilho)}
          onClose={() => setAdicionandoFilhoAoPai(null)}
        />
      )}

      {/* Modal de Criar Card Conectado */}
      {cardConectadoModal && (
        <ModalCriarCardConectado
          card={cardConectadoModal}
          flow={flow}
          session={userPermissions?.currentUser ? { usuario: userPermissions.currentUser, empresa: { id: flow.empresaId }, token: '' } : undefined}
          onClose={() => setCardConectadoModal(null)}
          onCardCriado={(novoCard, relacao, abrirParaEditar) => {
            // Atualizar o card local com a referência
            const cardOrigem = cards.find(c => c.id === cardConectadoModal.id);
            if (cardOrigem) {
              const updatedCards = cards.map(c => {
                if (c.id === cardConectadoModal.id) {
                  return {
                    ...c,
                    cardsConectadosIds: [...(c.cardsConectadosIds || []), novoCard.id]
                  };
                }
                return c;
              });
              setCards(updatedCards);
            }
            setCardConectadoModal(null);
            
            // Se "obrigar preenchimento" está ativo, navegar para o card criado no fluxo de destino
            if (abrirParaEditar && relacao?.flowDestinoId && onNavigateToFlow) {
              // Navegar ao fluxo de destino e abrir o card automaticamente
              setTimeout(() => {
                onNavigateToFlow(
                  { id: relacao.flowDestinoId, name: relacao.flowDestinoNome } as Flow,
                  novoCard.id
                );
              }, 300);
            }
          }}
        />
      )}

      {/* 📎 Modal "qual anexo precisa ser reenviado" (etapa de reprovação) */}
      {reenvioPendente && (() => {
        const etapaDestino = (flow.etapas || []).find(e => String(e.id) === reenvioPendente.destinationId);
        const etapaOrigem = (flow.etapas || []).find(e => String(e.id) === reenvioPendente.sourceId);
        if (!etapaDestino) return null;
        return (
          <ModalSolicitarReenvio
            card={reenvioPendente.card}
            flow={flow}
            etapaDestino={etapaDestino}
            etapaOrigem={etapaOrigem}
            onCancelar={() => setReenvioPendente(null)}
            onConfirmar={(payload) => {
              const pend = reenvioPendente;
              setReenvioPendente(null);
              executarMovimentacaoCard(
                pend.draggableId,
                pend.sourceId,
                pend.destinationId,
                pend.destIndexRelativo,
                payload,
                { viaAutomacao: pend.viaAutomacao }
              );
            }}
          />
        );
      })()}

      {/* Modal de Configuração de Permissões */}
      {mostrarConfigPermissoes && (
        <ModalConfigPermissoes
          flow={flow}
          colaboradores={(() => {
            // Mostrar apenas colaboradores que participam de alguma etapa deste fluxo
            const ids = new Set<string>();
            flow.etapas?.forEach(e => e.participantes?.forEach(id => ids.add(String(id))));
            const filtrados = colaboradores.filter(c => ids.has(String(c.id || (c as any)._id)));
            return filtrados.length > 0 ? filtrados : colaboradores;
          })()}
          onClose={() => setMostrarConfigPermissoes(false)}
          onSave={(permissoes, cotacao, prazoProcesso) => {
            // ⏱️ Prazo do processo também é do fluxo inteiro e tem rota própria
            if (prazoProcesso) {
              fetch(apiUrl(`/flows/${flow.id}/prazo-processo`), {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'X-User-Data': JSON.stringify({
                    id: userPermissions?.currentUser?.id || 'admin_user',
                    empresaId: flow.empresaId || 'empresa_principal',
                    role: 'admin'
                  })
                },
                body: JSON.stringify({ prazoProcesso })
              })
                .then(res => res.json())
                .then(data => {
                  if (data.success) {
                    flow.prazoProcesso = prazoProcesso.habilitado ? prazoProcesso : undefined;
                    setAgoraSla(new Date());
                  } else {
                    console.error('❌ Erro ao salvar prazo do processo:', data.error);
                  }
                })
                .catch(err => console.error('❌ Erro ao salvar prazo do processo:', err));
            }

            // 💰 A cotação é do fluxo inteiro e vai por outra rota, junto no mesmo Salvar
            if (cotacao) {
              fetch(apiUrl(`/flows/${flow.id}/cotacao`), {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'X-User-Data': JSON.stringify({
                    id: userPermissions?.currentUser?.id || 'admin_user',
                    empresaId: flow.empresaId || 'empresa_principal',
                    role: 'admin'
                  })
                },
                body: JSON.stringify({
                  configuracaoCotacao: cotacao.habilitado ? cotacao : null
                })
              })
                .then(res => res.json())
                .then(data => {
                  if (data.success) {
                    // desligar não apaga o modelo de campos — o servidor também o guarda
                    flow.configuracaoCotacao = cotacao.habilitado
                      ? cotacao
                      : { ...cotacao, habilitado: false };
                    console.log('✅ Cotação do fluxo salva');
                  } else {
                    console.error('❌ Erro ao salvar cotação:', data.error);
                  }
                })
                .catch(err => console.error('❌ Erro ao salvar cotação:', err));
            }

            // Salvar permissões no backend
            console.log('💾 Salvando permissões:', permissoes);
            fetch(apiUrl(`/flows/${flow.id}/permissoes`), {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'X-User-Data': JSON.stringify({
                  id: userPermissions?.currentUser?.id || 'admin_user',
                  empresaId: flow.empresaId || 'empresa_principal',
                  role: 'admin'
                })
              },
              body: JSON.stringify({ permissoes })
            })
              .then(res => res.json())
              .then(data => {
                if (data.success) {
                  console.log('✅ Permissões salvas com sucesso');
                  // Recarregar permissões para aplicar imediatamente
                  setPermissoesGranulares(permissoes);
                } else {
                  console.error('❌ Erro ao salvar permissões:', data.error);
                }
              })
              .catch(err => console.error('❌ Erro ao salvar permissões:', err));
          }}
        />
      )}

      {/* 🏷️ Modal de Gerenciamento de Etiquetas */}
      {mostrarGerenciarEtiquetas && (
        <GerenciarEtiquetasModal
          etiquetas={etiquetasFlow}
          onSalvar={(novas) => onUpdateFlow({ ...flow, etiquetas: novas })}
          onClose={() => setMostrarGerenciarEtiquetas(false)}
        />
      )}

      {cardSelecionado && (() => {
        const etapaDoCard = etapas.find(e => String(e.id) === String(cardSelecionado!.etapaId));
        // Determinar modo baseado nas permissões do usuário
        const podeEditar = checkCardEditPermission ? checkCardEditPermission(etapaDoCard, cardSelecionado) : false;
        // 27/10 André: quem pode editar já abre em edição (sem clicar no lápis); só grava no "Salvar Alterações"
        const modoInicial = podeEditar ? "editar" : "visualizar";
        
        console.log('🎯 Abrindo DetalhesCartao:', {
          cardId: cardSelecionado.id,
          titulo: cardSelecionado.titulo,
          etapa: etapaDoCard?.nome,
          podeEditar,
          modoInicial
        });
        
        return (
          <DetalhesCartao
            // 🔑 Força remontagem ao trocar de card (ex: abrir um filho a partir do pai).
            // Sem key, o React reaproveita a instância e estados inicializados via
            // useState(card.X) — titulo, responsável, etiquetas, modo — ficam com o
            // valor do card ANTERIOR e são gravados por cima ao salvar.
            key={String(cardSelecionado!.id)}
            card={cardSelecionado!}
            cotacaoHabilitada={!!flow.configuracaoCotacao?.habilitado}
            cotacaoRotulo={flow.configuracaoCotacao?.rotulo}
            // O servidor já gravou o padrão na etapa; aqui só refletimos na tela
            onCotacaoPadraoSalvo={(etapaId, padrao) => {
              const aplicar = (lista: Etapa[]) => lista.map(e =>
                String(e.id) === String(etapaId)
                  ? {
                      ...e,
                      cotacaoCampos: padrao.campos || undefined,
                      cotacaoFornecedores: padrao.fornecedores || undefined,
                    }
                  : e
              );
              setEtapas(aplicar);
              setTodasEtapas(aplicar);
              if (flow.etapas) flow.etapas = aplicar(flow.etapas);
            }}
            etapa={etapaDoCard!}
            etapasAnteriores={getEtapasAnteriores(cardSelecionado!.etapaId, cardSelecionado!)}
            modo={modoInicial}
            onSave={handleUpdateCard}
            onDelete={handleDeleteCard}
            onClose={() => setCardSelecionado(null)}
            userPermissions={userPermissions}
            checkCardEditPermission={checkCardEditPermission}
            automacoes={automacoes}
            flowId={flow.id}
            flowName={flow.name}
            // IMPORTANTE: Passar empresaId do flow para DetalhesCartao
            // O card pode não ter empresaId definido, então usamos o do flow como fallback
            // Necessário para buscar templates de documentos corretamente
            // Corrigido em: 22/01/2026
            empresaId={flow.empresaId}
            todosCards={cards}
            onDesvincularFilho={handleRemoverFilho}
            onSelecionarCard={setCardSelecionado}
            colaboradores={colaboradores}
            todasEtapas={todasEtapas.length > 0 ? todasEtapas : etapas}
            etiquetas={etiquetasFlow}
            // ➡️ "Mover para": mesmas regras do drag-and-drop (permissões + permiteMoverPara).
            // A lista já vem filtrada, e a movimentação passa pela mesma rotina.
            etapasDisponiveisParaMover={getEtapasDisponiveisParaMover(cardSelecionado!)}
            onMoverParaEtapa={(etapaDestinoId) =>
              executarMovimentacaoCard(
                String(cardSelecionado!.id),
                String(cardSelecionado!.etapaId),
                String(etapaDestinoId),
                // Entra no topo da coluna de destino — o drag também usa índice relativo
                0
              )
            }
            onCardFatiado={(novoCard, cardOrigemId, camposRestantes) => {
              // O backend já gravou a fatia e atualizou a origem. Aqui só refletimos
              // o resultado no board, sem novo PUT.
              setCards(prev => {
                const atualizados = prev.map(c =>
                  String(c.id) === String(cardOrigemId)
                    ? {
                        ...c,
                        campos: camposRestantes,
                        fatiasIds: [...(c.fatiasIds || []), novoCard.id]
                      }
                    : c
                );
                const jaExiste = atualizados.some(c => String(c.id) === String(novoCard.id));
                return jaExiste ? atualizados : [...atualizados, novoCard as Card];
              });
              // Mantém o card aberto em sincronia com o que sobrou
              setCardSelecionado(prev =>
                prev && String(prev.id) === String(cardOrigemId)
                  ? { ...prev, campos: camposRestantes, fatiasIds: [...(prev.fatiasIds || []), novoCard.id] }
                  : prev
              );
            }}
          />
        );
      })()}

      {colaboradorSelecionado && (
        <div style={{ 
          position: 'absolute', 
          left: colaboradorSelecionado!.position.x, 
          top: colaboradorSelecionado!.position.y 
        }}>
          <ColaboradorPopover
            colaborador={colaboradorSelecionado!.colaborador}
            departamentos={departamentos}
            flows={[flow]}
            onClose={() => setColaboradorSelecionado(null)}
            onViewProfile={() => {/* Implementar navegação para perfil */}}
            onNavigateToFlow={onNavigateToFlow}
            userPermissions={userPermissions}
          />
        </div>
      )}

      {/* Modal de Cards da Etapa (Visualização Fluxo) */}
      {etapaFluxoSelecionada && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[70vh] overflow-hidden flex flex-col">
            {/* Header do Modal */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 text-white">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-lg font-bold mb-1">{etapaFluxoSelecionada.nome}</h2>
                  <p className="text-blue-100 text-sm">
                    {cards.filter(c => String(c.etapaId) === String(etapaFluxoSelecionada.id)).length} cartões nesta etapa
                  </p>
                </div>
                <button
                  onClick={() => setEtapaFluxoSelecionada(null)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Lista de Cards */}
            <div className="flex-1 overflow-y-auto p-4">
              {cards.filter(c => String(c.etapaId) === String(etapaFluxoSelecionada.id)).length > 0 ? (
                <div className="space-y-2">
                  {cards
                    .filter(c => String(c.etapaId) === String(etapaFluxoSelecionada.id))
                    .map(card => {
                      // Verificar vencimento
                      const campoVencimento = etapaFluxoSelecionada.campos?.find(c => c.tipo === 'data_vencimento');
                      let statusVencimento = null;
                      if (campoVencimento && card.campos?.[campoVencimento.id]) {
                        try {
                          const dataVencimento = new Date(card.campos[campoVencimento.id]);
                          const agora = new Date();
                          const diferencaHoras = (dataVencimento.getTime() - agora.getTime()) / (1000 * 60 * 60);
                          
                          if (dataVencimento < agora) {
                            statusVencimento = { label: 'VENCIDO', color: 'bg-red-100 text-red-700' };
                          } else if (diferencaHoras <= 24) {
                            statusVencimento = { label: 'VENCE HOJE', color: 'bg-yellow-100 text-yellow-700' };
                          }
                        } catch (error) {
                          // Ignorar
                        }
                      }

                      return (
                        <div
                          key={card.id}
                          className="bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-blue-400 hover:shadow-lg transition-all cursor-pointer group"
                          onClick={() => {
                            setCardSelecionado(card);
                            setEtapaFluxoSelecionada(null);
                          }}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <h3 className="font-bold text-gray-900 text-lg flex-1 group-hover:text-blue-600 transition-colors">
                              {card.titulo || 'Sem título'}
                            </h3>
                            {statusVencimento && (
                              <span className={`px-2 py-1 text-xs font-bold rounded-full ${statusVencimento.color}`}>
                                {statusVencimento.label}
                              </span>
                            )}
                          </div>

                          {/* Preview de campos */}
                          <div className="space-y-2 mb-3">
                            {etapaFluxoSelecionada.campos?.slice(0, 3).map(campo => {
                              const valor = card.campos?.[campo.id];
                              if (!valor) return null;

                              return (
                                <div key={campo.id} className="flex items-start gap-2 text-sm">
                                  <span className="text-gray-600 font-medium min-w-[100px]">{campo.nome}:</span>
                                  <span className="text-gray-900 flex-1 truncate">
                                    {campo.tipo === 'data' || campo.tipo === 'data_vencimento' ? (
                                      new Date(valor).toLocaleDateString('pt-BR')
                                    ) : Array.isArray(valor) ? (
                                      `${valor.length} ${valor.length === 1 ? 'item' : 'itens'}`
                                    ) : (
                                      String(valor).substring(0, 50)
                                    )}
                                  </span>
                                </div>
                              );
                            })}
                          </div>

                          {/* Footer com data */}
                          <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                            <span>
                              {card.updatedAt ? (
                                <>Atualizado em {new Date(card.updatedAt).toLocaleDateString('pt-BR')}</>
                              ) : (
                                'Sem data'
                              )}
                            </span>
                            <span className="text-blue-600 font-medium group-hover:underline">
                              Ver detalhes →
                            </span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-3">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-base font-medium mb-2">Nenhum cartão nesta etapa</p>
                  {checkCardEditPermission?.(etapaFluxoSelecionada) && (etapaFluxoSelecionada.permiteCriarCards ?? true) && (
                    <button
                      onClick={() => {
                        setNovoCartao(etapaFluxoSelecionada);
                        setEtapaFluxoSelecionada(null);
                      }}
                      className="mt-3 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium text-sm"
                    >
                      + Criar primeiro cartão
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Clique em um cartão para ver todos os detalhes
              </div>
              <button
                onClick={() => setEtapaFluxoSelecionada(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Configuracao de Ordenacao */}
      {/* 🧾 Resultado da varredura de lançamento de NF no GIX */}
      {resultadoNf && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
             onClick={() => setResultadoNf(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Lançamento de notas no GIX</h3>
                {resultadoNf.success ? (() => {
                  const itens = resultadoNf.itens || [];
                  const prontas = itens.filter((i: any) => i.dryRun).length;
                  // Nota de serviço sai da conta de recusa: ela não falhou, ela é
                  // manual. Contada junto, inflava "recusadas" com trabalho de
                  // rotina e escondia quantos problemas reais havia de verdade.
                  const servico = itens.filter((i: any) => i.notaDeServico).length;
                  const recusadas = itens.filter(
                    (i: any) => !i.ok && !i.dryRun && !i.notaDeServico).length;
                  return (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {resultadoNf.total} card(s) avaliado(s)
                      {resultadoNf.lancadas > 0 && (
                        <> · <strong className="text-emerald-700">{resultadoNf.lancadas} lançada(s)</strong></>
                      )}
                      {/* Sem isso o contador mostrava "0 lançadas" mesmo quando tudo
                          passou na validação e só faltava ligar a chave de escrita. */}
                      {prontas > 0 && (
                        <> · <strong className="text-amber-700">{prontas} pronta(s) para lançar</strong></>
                      )}
                      {servico > 0 && (
                        <> · <strong className="text-blue-700">{servico} de serviço (manual)</strong></>
                      )}
                      {recusadas > 0 && <> · {recusadas} recusada(s)</>}
                    </p>
                  );
                })() : (
                  <p className="text-xs text-red-600 mt-0.5">{resultadoNf.error || 'falhou'}</p>
                )}
              </div>
              <button onClick={() => setResultadoNf(null)}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto px-5 py-3 space-y-2">
              {(resultadoNf.itens || []).length === 0 && resultadoNf.success && (
                <p className="text-sm text-gray-500 py-6 text-center">
                  Nenhum card nesta etapa com a chave de lançamento ligada.
                </p>
              )}
              {(resultadoNf.itens || []).map((item: any, i: number) => (
                <div key={i}
                     className={`rounded-lg border px-3 py-2 ${
                       item.ok ? 'border-emerald-200 bg-emerald-50'
                       : item.dryRun ? 'border-amber-200 bg-amber-50'
                       : item.notaDeServico ? 'border-blue-200 bg-blue-50'
                       : 'border-red-200 bg-red-50'}`}>
                  <div className="flex items-start gap-2">
                    <span className="text-sm mt-0.5">
                      {item.ok ? '✅' : item.dryRun ? '🧪' : item.notaDeServico ? '📄' : '🚫'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-gray-900 truncate">
                        {item.titulo || item.cardId}
                      </p>
                      <p className="text-[11px] text-gray-600 mt-0.5">
                        {item.ok
                          ? <>NF {item.numeroNf} {item.jaLancada ? 'já constava lançada' : 'lançada'} — OC {item.numeroOc}/empresa {item.empresa}</>
                          : item.motivo}
                      </p>
                      {item.numeroOc && !item.ok && (
                        <p className="text-[10px] text-gray-400 mt-0.5">OC {item.numeroOc}{item.empresa ? ` · empresa ${item.empresa}` : ''}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <p className="text-[11px] text-gray-500">
                Os cards que não lançaram vão para a etapa configurada, com o motivo escrito no campo e no histórico.
                O lançamento real só acontece com <code className="px-1 bg-gray-200 rounded">GIX_NF_ALLOW_INSERT=1</code> na API GIX —
                sem isso tudo aparece como 🧪 simulação e nada é escrito.
              </p>
            </div>
          </div>
        </div>
      )}

      {configurandoOrdenacao && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setConfigurandoOrdenacao(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header compacto */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/80">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-900">Ordenar Cards</h2>
                  <p className="text-[10px] text-gray-500">{configurandoOrdenacao.nome}</p>
                </div>
              </div>
              <button onClick={() => setConfigurandoOrdenacao(null)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-1 rounded-lg transition-all">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Opcoes compactas */}
            <div className="p-3 space-y-1.5">
              {[
                { value: 'mais_recente', label: 'Mais recente primeiro', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
                { value: 'mais_antigo', label: 'Mais antigo primeiro', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
                { value: 'vencimento_proximo', label: 'Vencimento mais proximo', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg> },
                { value: 'vencimento_distante', label: 'Vencimento mais distante', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> },
                { value: 'titulo_az', label: 'Titulo A-Z', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" /></svg> },
                { value: 'titulo_za', label: 'Titulo Z-A', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" /></svg> }
              ].map((opcao) => {
                const isSelected = configurandoOrdenacao.ordenacaoCards === opcao.value;
                return (
                  <button
                    key={opcao.value}
                    onClick={() => handleSalvarOrdenacaoEtapa(configurandoOrdenacao.id, opcao.value)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all flex items-center gap-3 ${
                      isSelected
                        ? 'border-indigo-400 bg-indigo-50 shadow-sm'
                        : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className={`flex-shrink-0 ${isSelected ? 'text-indigo-600' : 'text-gray-400'}`}>{opcao.icon}</span>
                    <span className={`flex-1 text-sm ${isSelected ? 'font-semibold text-indigo-900' : 'text-gray-700'}`}>{opcao.label}</span>
                    {isSelected && (
                      <svg className="w-4 h-4 text-indigo-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                );
              })}

              {/* 03/11/2026 André: filtro de período da etapa */}
              <div className="mt-2 rounded-lg border border-gray-200 p-3">
                <div className="text-[11px] font-semibold text-gray-700 mb-2">Filtrar por período (entrada na etapa)</div>
                <div className="flex items-center gap-2">
                  <input type="date" className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-xs"
                    value={filtroDataEtapa[String(configurandoOrdenacao.id)]?.de || ''}
                    onChange={e => salvarFiltroData(String(configurandoOrdenacao.id), { ...filtroDataEtapa[String(configurandoOrdenacao.id)], de: e.target.value || undefined })} />
                  <span className="text-[11px] text-gray-500">até</span>
                  <input type="date" className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-xs"
                    value={filtroDataEtapa[String(configurandoOrdenacao.id)]?.ate || ''}
                    onChange={e => salvarFiltroData(String(configurandoOrdenacao.id), { ...filtroDataEtapa[String(configurandoOrdenacao.id)], ate: e.target.value || undefined })} />
                </div>
                {(filtroDataEtapa[String(configurandoOrdenacao.id)]?.de || filtroDataEtapa[String(configurandoOrdenacao.id)]?.ate) && (
                  <button onClick={() => salvarFiltroData(String(configurandoOrdenacao.id), null)}
                    className="mt-2 text-[11px] text-indigo-600 hover:underline">Limpar período</button>
                )}
                <p className="mt-1 text-[10px] text-gray-400">Vale só para você, neste navegador.</p>
              </div>

              {!configurandoOrdenacao.campos?.some((c: any) => c.tipo === 'data_vencimento') && (
                <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                  Esta etapa nao possui campo de vencimento. Ordenacao por vencimento nao tera efeito.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/*  Toast de notificação para campos limpos */}
      {toastInfo?.visible && (
        <div className="fixed bottom-4 right-4 z-50 max-w-md animate-slide-in-right">
          <div className="bg-white rounded-xl shadow-2xl border-2 border-red-300 overflow-hidden">
            {/* Header do Toast */}
            <div className="bg-gradient-to-r from-red-500 to-orange-500 px-4 py-3 flex items-center justify-between">
              <h4 className="text-white font-bold flex items-center gap-2">
                ⚠️ {toastInfo.titulo}
              </h4>
              <button 
                onClick={() => setToastInfo(null)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Conteúdo */}
            <div className="p-4">
              <p className="text-sm text-gray-700 mb-3">{toastInfo.mensagem}</p>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <ul className="space-y-2">
                  {toastInfo.camposLimpos.map((campo, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <span className="text-red-500 font-bold">•</span>
                      <div>
                        <span className="font-semibold text-red-700">{campo.nomeCampo}:</span>
                        <span className="text-red-600 ml-1 italic">"{campo.valorAnterior}"</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              
              <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                💡 Preencha novamente quando necessário
              </p>
            </div>
            
            {/* Barra de progresso */}
            <div className="h-1 bg-gray-200">
              <div
                className="h-full bg-gradient-to-r from-red-500 to-orange-500 animate-shrink-width"
                style={{ animationDuration: '10s' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* 💬 Toast de nova mensagem do portal (solicitante externo) */}
      {portalToast && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm animate-slide-in-right">
          <div className="bg-white rounded-xl shadow-2xl border-2 border-blue-300 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 flex items-center justify-between">
              <h4 className="text-white font-bold flex items-center gap-2 text-sm">
                💬 Nova mensagem
              </h4>
              <button
                onClick={() => setPortalToast(null)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">{portalToast.nome}</span> enviou uma mensagem.
              </p>
              {portalToast.protocolo && (
                <p className="text-xs text-gray-400 mt-1">Protocolo: {portalToast.protocolo}</p>
              )}
              <p className="text-xs text-gray-500 mt-2">Abra o card para responder.</p>
            </div>
          </div>
        </div>
      )}

      {/* 🔄 Loading Global - Bloqueia toda a tela enquanto salva */}
      {salvandoGlobal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 flex flex-col items-center gap-4 max-w-md mx-4">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-lg font-medium text-gray-700 dark:text-gray-200 text-center">
              {salvandoGlobal}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Por favor, aguarde...
            </p>
          </div>
        </div>
      )}

      {/* 🤖 Assistente de IA */}
      <AIAssistant
        flow={flow}
        cards={cards}
        etapas={etapas}
        colaboradores={colaboradores}
        empresaId={flow.empresaId}
      />

      </div>
    </div>
  );
};

export default KanbanBoard;


